import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

async function fetchTablesState(eventId, token) {
  const res = await fetch(`/api/events/${eventId}/tables/state`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function startTables(eventId, token) {
  const res = await fetch(`/api/events/${eventId}/tables/start`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function stopTables(eventId, token, releaseActiveSlots = true) {
  const res = await fetch(`/api/events/${eventId}/tables/stop`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ release_active_slots: releaseActiveSlots }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ===== FILTER INPUT — defined outside component to preserve focus on re-render =====
const FilterInput = ({ type = "text", value, onChange, placeholder, min, max, width = "160px", themeVars, label }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px", width }}>
    {label && (
      <span style={{
        fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px",
        whiteSpace: "nowrap",
      }}>
        {label}
      </span>
    )}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      style={{
        padding: "10px 16px", borderRadius: "12px",
        background: themeVars.card, border: `1px solid ${themeVars.border}`,
        color: themeVars.fg, fontSize: "0.95rem", fontWeight: 500,
        outline: "none", width: "100%", transition: "all 0.2s ease",
      }}
      onFocus={(e) => e.target.style.borderColor = themeVars.primary}
      onBlur={(e) => e.target.style.borderColor = themeVars.border}
    />
  </div>
);

export default function EventAdminDashboard({
  lang = "en",
  theme = "dark",
  onToggleTheme,
  onToggleLang
}) {
  const { slug } = useParams();
  const navigate = useNavigate();

  // ===== STATE MANAGEMENT =====
  // numeric event ID resolved from slug (used in all API calls)
  const [eventId, setEventId] = useState(null);
  // true once the slug→event+permission check is done
  const [accessChecked, setAccessChecked] = useState(false);
  // true if current user is the event creator (not just co-admin)
  const [isCreator, setIsCreator] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [eventTitle, setEventTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [eventCapacity, setEventCapacity] = useState(null);
  const [estimatedCost, setEstimatedCost] = useState(null);

  // ===== ACTIVE TAB =====
  const [activeTab, setActiveTab] = useState("participants");

  // ===== DETAIL PANELS =====
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [participantRequests, setParticipantRequests] = useState([]);
  const [participantRequestsLoading, setParticipantRequestsLoading] = useState(false);
  const [isRequestDetailsPanelOpen, setIsRequestDetailsPanelOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRequestMatches, setSelectedRequestMatches] = useState([]);
  const [isMatchDetailsPanelOpen, setIsMatchDetailsPanelOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isMatchDetailsModalOpen, setIsMatchDetailsModalOpen] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [matchDetails, setMatchDetails] = useState(null);

  // ===== INVITED USERS STATE =====
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(true);
  const [referralsError, setReferralsError] = useState(null);
  const [expandedReferral, setExpandedReferral] = useState(null);
  const [referralsSearch, setReferralsSearch] = useState('');

  // ===== CO-ADMINS STATE =====
  const [coAdmins, setCoAdmins] = useState([]);
  const [coAdminsLoading, setCoAdminsLoading] = useState(false);
  const [addAdminInput, setAddAdminInput] = useState('');
  const [addAdminLoading, setAddAdminLoading] = useState(false);

  // ===== TABLE TIMERS STATE =====
  const [tableSeatTimer, setTableSeatTimer] = useState(null);
  const [tableRotationTimer, setTableRotationTimer] = useState(null);

  // ===== TABLE SETTINGS EDIT STATE =====
  const [tableSettings, setTableSettings] = useState({
    number_of_tables: null,
    seats_per_table: null,
  });
  const [tableSettingsEdit, setTableSettingsEdit] = useState(false);
  const [tableSettingsDraft, setTableSettingsDraft] = useState({ number_of_tables: "", seats_per_table: "" });
  const [tableSettingsSaving, setTableSettingsSaving] = useState(false);
  const [tableSettingsLoaded, setTableSettingsLoaded] = useState(false);

  // ===== LIVE TABLE STATE (polling 1s when tables tab active) =====
  const [liveTableData, setLiveTableData] = useState(null);
  const liveTableIntervalRef = useRef(null);
  const [tablesState, setTablesState] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [releaseActiveSlots, setReleaseActiveSlots] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // ===== REAL-TIME POLLING REFS =====
  const participantsIntervalRef = useRef(null);
  const requestsIntervalRef = useRef(null);
  const matchesIntervalRef = useRef(null);
  // Track which row IDs were recently updated for flash animation
  const [updatedParticipantIds, setUpdatedParticipantIds] = useState(new Set());
  const [updatedRequestIds, setUpdatedRequestIds]         = useState(new Set());
  const [updatedMatchIds, setUpdatedMatchIds]             = useState(new Set());

  const refreshTablesState = useCallback(async () => {
    if (!eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const data = await fetchTablesState(eventId, token);
      setLiveTableData(data);
      setTablesState(data);
      setErrorMsg(null);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to refresh tables state");
    }
  }, [eventId]);

  useEffect(() => {
    if (activeTab !== 'tables' || !eventId) {
      clearInterval(liveTableIntervalRef.current);
      return;
    }
    refreshTablesState();
    // Тикаем 1с только пока вкладка видима. На скрытой вкладке
    // не дёргаем сеть — возобновляемся при возвращении.
    const tick = () => {
      if (!document.hidden) refreshTablesState();
    };
    liveTableIntervalRef.current = setInterval(tick, 1000);
    const onVisibility = () => {
      if (!document.hidden) refreshTablesState();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(liveTableIntervalRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [activeTab, eventId, refreshTablesState]);

  // ===== FILTERS & SORTING =====
  const [filters, setFilters] = useState({
    status: 'all',
    profileMin: 0,
    nameSearch: '',
    idSearch: '',
    emailSearch: '',
    phoneSearch: '',
    dateFrom: '',
    dateTo: ''
  });
  const [sortConfig, setSortConfig] = useState({
    participants: { key: null, direction: 'asc' },
    searchRequests: { key: null, direction: 'asc' },
    matches: { key: null, direction: 'asc' }
  });

  // ===== DATA STATES =====
  const [searchRequests, setSearchRequests] = useState([]);
  const [searchRequestsLoading, setSearchRequestsLoading] = useState(true);
  const [searchRequestsError, setSearchRequestsError] = useState(null);
  const [searchRequestsFilters, setSearchRequestsFilters] = useState({
    status: 'all',
    category: 'all',
    userSearch: '',
    idSearch: '',
    emailSearch: '',
    matchesMin: 0,
    dateFrom: '',
    dateTo: ''
  });
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [matchesError, setMatchesError] = useState(null);
  const [matchesFilters, setMatchesFilters] = useState({
    status: 'all',
    category: 'all',
    idSearch: '',
    user1Search: '',
    user2Search: '',
    scoreMin: 0,
    scoreMax: 100,
    dateFrom: '',
    dateTo: ''
  });

  // ===== COMMENTS STATE =====
  const [adminComments, setAdminComments] = useState([]);
  const [adminCommentsLoading, setAdminCommentsLoading] = useState(false);
  const [adminCommentsError, setAdminCommentsError] = useState(null);

  // ===== SETTINGS MENU STATE =====
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  // ===== MOBILE FILTER TOGGLES =====
  const [mobileParticipantFiltersOpen, setMobileParticipantFiltersOpen] = useState(false);
  const [mobileRequestFiltersOpen, setMobileRequestFiltersOpen] = useState(false);
  const [mobileMatchFiltersOpen, setMobileMatchFiltersOpen] = useState(false);
  const [mobileAdminsPanelOpen, setMobileAdminsPanelOpen] = useState(false);

  // ===== TOAST =====
  const [toast, setToast] = useState(null);
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2200);
  };

  // ===== RESPONSIVE =====
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 640 && window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 640);
      setIsTablet(w > 640 && w <= 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ===== ESCAPE KEY HANDLER =====
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (isMatchDetailsPanelOpen) closeMatchDetailsPanel();
        else if (isRequestDetailsPanelOpen) closeRequestDetailsPanel();
        else if (isDrawerOpen) closeDrawer();
        else if (isMatchDetailsModalOpen) {
          setIsMatchDetailsModalOpen(false);
          setSelectedMatchId(null);
          setMatchDetails(null);
        }
        else if (isSettingsOpen) setIsSettingsOpen(false);
        else if (isLangOpen) setIsLangOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isDrawerOpen, isRequestDetailsPanelOpen, isMatchDetailsPanelOpen, isMatchDetailsModalOpen, isSettingsOpen, isLangOpen]);

  // ===== AUTH & PERMISSION CHECK =====
  // 1. Resolve slug → numeric event ID
  // 2. Verify current user is creator or co-admin
  // 3. Only then allow the rest of the data-loading effects to run
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/auth");
      return;
    }
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUserId(tokenPayload.user_id || tokenPayload.id || null);
    } catch (err) {
      console.error("Error decoding token:", err);
    }

    const resolveAndCheck = async () => {
      try {
        // Try slug first; fall back to numeric ID for backwards-compat
        let res = await fetch(`/api/published_events/by_slug/${slug}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok && !isNaN(slug)) {
          res = await fetch(`/api/published_events/${slug}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        if (!res.ok) {
          setError(lang === "fr" ? "Événement non trouvé" : "Event not found");
          setLoading(false);
          setAccessChecked(true);
          return;
        }
        const event = await res.json();

        // ── Permission gate ─────────────────────────────────────────────
        const canAdmin = event.is_mine || event.is_co_admin;
        if (!canAdmin) {
          // Redirect non-admins to the public event page silently
          navigate(`/events/${slug}`, { replace: true });
          return;
        }
        // ── Permission granted ──────────────────────────────────────────
        setEventId(event.id);
        setIsCreator(!!event.is_mine);
        setAccessChecked(true);
      } catch (err) {
        console.error("Error resolving event:", err);
        setError(lang === "fr" ? "Une erreur est survenue" : "An error occurred");
        setLoading(false);
        setAccessChecked(true);
      }
    };

    resolveAndCheck();
  }, [slug, lang, navigate]);

  // ===== PARTICIPANTS — real-time polling every 20 s =====
  useEffect(() => {
    if (!accessChecked || !eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchParticipants = async (silent = false) => {
      try {
        const res = await fetch(`/api/events/${eventId}/participants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (!silent) {
            const errData = await res.json().catch(() => ({}));
            if (res.status === 403) setError(lang === "fr" ? "Accès refusé" : "Access denied");
            else if (res.status === 404) setError(lang === "fr" ? "Événement non trouvé" : "Event not found");
            else setError(errData.detail || (lang === "fr" ? "Erreur de chargement" : "Failed to load"));
          }
          return;
        }
        const data = await res.json();
        const incoming = data.participants || [];

        if (silent) {
          // Detect new/changed IDs for flash animation
          setParticipants(prev => {
            const prevMap = new Map(prev.map(p => [p.user_id, p]));
            const changedIds = new Set();
            incoming.forEach(p => {
              const old = prevMap.get(p.user_id);
              if (!old || old.status !== p.status) changedIds.add(p.user_id);
            });
            if (changedIds.size > 0) {
              setUpdatedParticipantIds(changedIds);
              setTimeout(() => setUpdatedParticipantIds(new Set()), 1200);
            }
            return incoming;
          });
        } else {
          setParticipants(incoming);
          setEventTitle(data.event_title || "");
          setEventCapacity(data.capacity || null);
          setEstimatedCost(data.estimated_cost || null);
        }
      } catch (err) {
        if (!silent) {
          console.error("Error loading participants:", err);
          setError(lang === "fr" ? "Une erreur est survenue lors du chargement" : "An error occurred while loading participants");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    };

    fetchParticipants(false);
    const tick = () => {
      if (!document.hidden) fetchParticipants(true);
    };
    participantsIntervalRef.current = setInterval(tick, 20000);
    return () => clearInterval(participantsIntervalRef.current);
  }, [accessChecked, eventId, lang]);

  // ===== SEARCH REQUESTS — real-time polling every 15 s =====
  useEffect(() => {
    if (!accessChecked || !eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchSearchRequests = async (silent = false) => {
      try {
        if (!silent) setSearchRequestsLoading(true);
        const res = await fetch(`/api/events/${eventId}/search_requests`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load search requests");
        const data = await res.json();
        const incoming = data.search_requests || [];

        if (silent) {
          setSearchRequests(prev => {
            const prevMap = new Map(prev.map(r => [r.id, r]));
            const changedIds = new Set();
            incoming.forEach(r => {
              const old = prevMap.get(r.id);
              if (!old || old.status !== r.status || old.total_matches !== r.total_matches) changedIds.add(r.id);
            });
            if (changedIds.size > 0) {
              setUpdatedRequestIds(changedIds);
              setTimeout(() => setUpdatedRequestIds(new Set()), 1200);
            }
            return incoming;
          });
        } else {
          setSearchRequests(incoming);
        }
      } catch (err) {
        if (!silent) {
          console.error("Error loading search requests:", err);
          setSearchRequestsError(lang === "fr" ? "Erreur de chargement des requêtes" : "Failed to load search requests");
        }
      } finally {
        if (!silent) setSearchRequestsLoading(false);
      }
    };

    fetchSearchRequests(false);
    // Стартуем интервал со сдвигом 5с, чтобы не бить запросами вместе
    // с participants и matches.
    const tick = () => {
      if (!document.hidden) fetchSearchRequests(true);
    };
    let staggerId = setTimeout(() => {
      requestsIntervalRef.current = setInterval(tick, 15000);
    }, 5000);
    return () => {
      clearTimeout(staggerId);
      clearInterval(requestsIntervalRef.current);
    };
  }, [accessChecked, eventId, lang]);

  // ===== MATCHES — real-time polling every 15 s =====
  useEffect(() => {
    if (!accessChecked || !eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchMatches = async (silent = false) => {
      try {
        if (!silent) setMatchesLoading(true);
        const res = await fetch(`/api/events/${eventId}/matches`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load matches");
        const data = await res.json();
        const incoming = data.matches || [];

        if (silent) {
          setMatches(prev => {
            const prevMap = new Map(prev.map(m => [m.id, m]));
            const changedIds = new Set();
            incoming.forEach(m => {
              const old = prevMap.get(m.id);
              if (!old || old.status !== m.status) changedIds.add(m.id);
            });
            if (changedIds.size > 0) {
              setUpdatedMatchIds(changedIds);
              setTimeout(() => setUpdatedMatchIds(new Set()), 1200);
            }
            return incoming;
          });
        } else {
          setMatches(incoming);
        }
      } catch (err) {
        if (!silent) {
          console.error("Error loading matches:", err);
          setMatchesError(lang === "fr" ? "Erreur de chargement des matches" : "Failed to load matches");
        }
      } finally {
        if (!silent) setMatchesLoading(false);
      }
    };

    fetchMatches(false);
    // Сдвиг 10с — равномерно распределяем три интервала
    // (participants ~0с, requests ~5с, matches ~10с).
    const tick = () => {
      if (!document.hidden) fetchMatches(true);
    };
    let staggerId = setTimeout(() => {
      matchesIntervalRef.current = setInterval(tick, 15000);
    }, 10000);
    return () => {
      clearTimeout(staggerId);
      clearInterval(matchesIntervalRef.current);
    };
  }, [accessChecked, eventId, lang]);

  // ===== TABLE SETTINGS (loaded on demand when tab opens) =====
  useEffect(() => {
    if (activeTab !== 'tables' || !eventId || tableSettingsLoaded) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchTableSettings = async () => {
      try {
        const res = await fetch(`/api/events/${eventId}/participants`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const nt = data.number_of_tables ?? null;
        const sp = data.seats_per_table ?? null;
        const ts = data.table_timer_seat ?? null;
        const tr = data.table_timer_rotation ?? null;
        setTableSettings({ number_of_tables: nt, seats_per_table: sp });
        setTableSettingsDraft({ number_of_tables: nt ?? "", seats_per_table: sp ?? "" });
        setTableSeatTimer(ts);
        setTableRotationTimer(tr);
        setTableSettingsLoaded(true);
      } catch (err) {
        console.error("Error loading table settings:", err);
      }
    };

    fetchTableSettings();
  }, [activeTab, eventId, tableSettingsLoaded]);

  useEffect(() => {
    if (!accessChecked || !eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchReferrals = async () => {
      try {
        setReferralsLoading(true);
        const res = await fetch(`/api/events/${eventId}/referrals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load referrals");
        const data = await res.json();
        setReferrals(data.referrals || []);
      } catch (err) {
        console.error("Error loading referrals:", err);
        setReferralsError(lang === "fr" ? "Erreur de chargement des invitations" : "Failed to load referrals");
      } finally {
        setReferralsLoading(false);
      }
    };

    fetchReferrals();
  }, [accessChecked, eventId, lang]);

  // ===== LOAD CO-ADMINS =====
  useEffect(() => {
    if (!accessChecked || !eventId || !isCreator) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const fetchCoAdmins = async () => {
      try {
        setCoAdminsLoading(true);
        const res = await fetch(`/api/events/${eventId}/admins`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setCoAdmins(data.admins || []);
      } catch (err) {
        console.error("Error loading co-admins:", err);
      } finally {
        setCoAdminsLoading(false);
      }
    };
    fetchCoAdmins();
  }, [accessChecked, eventId, isCreator]);

  useEffect(() => {
    if (!accessChecked || !eventId) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const fetchComments = async () => {
      try {
        setAdminCommentsLoading(true);
        const res = await fetch(`/api/events/${eventId}/comments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to load comments");
        const data = await res.json();
        setAdminComments(data || []);
      } catch (err) {
        setAdminCommentsError(lang === "fr" ? "Erreur de chargement des commentaires" : "Failed to load comments");
      } finally {
        setAdminCommentsLoading(false);
      }
    };
    fetchComments();
  }, [accessChecked, eventId, lang]);

  // ===== HELPER FUNCTIONS =====
  const getProfileFill = (participant) => {
    if (!participant.request && !participant.about) return 0;
    if ((participant.request && !participant.about) || (!participant.request && participant.about)) return 50;
    return 100;
  };

  const handleSort = (table, key) => {
    setSortConfig(prev => ({
      ...prev,
      [table]: {
        key,
        direction: prev[table].key === key && prev[table].direction === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const canViewSearchRequests = currentUserId === 169;

  // ===== НОВАЯ ФУНКЦИЯ РАСЧЁТА СТАТУСА ЗАПРОСА (ОБНОВЛЁННАЯ) =====
  const getRequestStatus = useCallback((searchRequest, allMatches) => {
    // 1. Проверяем активный ли запрос
    const isActive = searchRequest.is_active !== false;
    
    // 2. Получаем матчи для этого запроса
    const requestMatches = allMatches.filter(m => 
      m.search_request_user1_id === searchRequest.id || 
      m.search_request_user2_id === searchRequest.id
    );
    
    // 3. Сортируем матчи по дате создания (последний первый)
    const sortedMatches = [...requestMatches].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    // 4. Получаем последний матч
    const lastMatch = sortedMatches.length > 0 ? sortedMatches[0] : null;
    
    // 5. Считаем статусы матчей
    const acceptedCount = requestMatches.filter(m => m.status === 'active').length;
    const pendingCount = requestMatches.filter(m => m.status === 'pending').length;
    const rejectedCount = requestMatches.filter(m => m.status === 'rejected').length;
    
    // 6. Формируем детальный статус
    if (!isActive) {
      return {
        label: lang === "fr" ? "Inactif" : "Inactive",
        type: 'inactive',
        color: 'gray',
        details: {
          isActive: false,
          hasMatches: requestMatches.length > 0,
          matchCount: requestMatches.length,
          acceptedCount,
          pendingCount,
          rejectedCount,
          lastMatchStatus: lastMatch?.status || null
        }
      };
    }
    
    // Если есть матчи - проверяем статус последнего
    if (lastMatch) {
      // Если последний матч в pending - ждём ответа пользователя
      if (lastMatch.status === 'pending') {
        return {
          label: lang === "fr" ? "En attente de réponse utilisateur" : "Waiting for User Response",
          type: 'waiting_for_match',
          color: 'warning',
          details: {
            isActive: true,
            hasMatches: true,
            matchCount: requestMatches.length,
            acceptedCount,
            pendingCount,
            rejectedCount,
            lastMatchStatus: lastMatch.status
          }
        };
      }
      
      // Если последний матч accepted/declined и запрос active - ищем дальше
      if ((lastMatch.status === 'active' || lastMatch.status === 'rejected') && searchRequest.status === 'active') {
        return {
          label: lang === "fr" ? "Recherche" : "Searching",
          type: 'searching',
          color: 'warning',
          details: {
            isActive: true,
            hasMatches: true,
            matchCount: requestMatches.length,
            acceptedCount,
            pendingCount,
            rejectedCount,
            lastMatchStatus: lastMatch.status
          }
        };
      }
    }
    
    // Если нет матчей вообще
    if (requestMatches.length === 0) {
      return {
        label: lang === "fr" ? "Recherche (aucun match)" : "Searching (No Matches)",
        type: 'searching',
        color: 'warning',
        details: {
          isActive: true,
          hasMatches: false,
          matchCount: 0,
          acceptedCount: 0,
          pendingCount: 0,
          rejectedCount: 0,
          lastMatchStatus: null
        }
      };
    }
    
    // Если есть несколько матчей
    if (requestMatches.length > 1) {
      return {
        label: lang === "fr" ? `Plusieurs matchs (${requestMatches.length})` : `Multiple Matches (${requestMatches.length})`,
        type: 'multiple',
        color: 'info',
        details: {
          isActive: true,
          hasMatches: true,
          matchCount: requestMatches.length,
          acceptedCount,
          pendingCount,
          rejectedCount,
          lastMatchStatus: lastMatch?.status || null
        }
      };
    }
    
    // Один матч - показываем его статус
    if (lastMatch.status === 'active') {
      return {
        label: lang === "fr" ? "Match accepté" : "Match Accepted",
        type: 'accepted',
        color: 'success',
        details: {
          isActive: true,
          hasMatches: true,
          matchCount: 1,
          acceptedCount: 1,
          pendingCount: 0,
          rejectedCount: 0,
          lastMatchStatus: lastMatch.status
        }
      };
    } else if (lastMatch.status === 'rejected') {
      return {
        label: lang === "fr" ? "Match rejeté" : "Match Rejected",
        type: 'rejected',
        color: 'danger',
        details: {
          isActive: true,
          hasMatches: true,
          matchCount: 1,
          acceptedCount: 0,
          pendingCount: 0,
          rejectedCount: 1,
          lastMatchStatus: lastMatch.status
        }
      };
    }
    
    return {
      label: searchRequest.status || (lang === "fr" ? "Inconnu" : "Unknown"),
      type: 'unknown',
      color: 'gray',
      details: {
        isActive,
        hasMatches: requestMatches.length > 0,
        matchCount: requestMatches.length,
        acceptedCount,
        pendingCount,
        rejectedCount,
        lastMatchStatus: lastMatch?.status || null
      }
    };
  }, [lang]);

  // ===== FILTERED DATA =====
  const filteredParticipants = useMemo(() => {
    let result = participants.filter(p => {
      if (filters.status !== 'all' && p.status !== filters.status) return false;
      const profileFill = getProfileFill(p);
      if (profileFill < filters.profileMin) return false;
      if (filters.nameSearch && !p.name.toLowerCase().includes(filters.nameSearch.toLowerCase())) return false;
      if (filters.idSearch && !String(p.user_id).includes(filters.idSearch.trim())) return false;
      if (filters.emailSearch && !(p.email || '').toLowerCase().includes(filters.emailSearch.toLowerCase())) return false;
      if (filters.phoneSearch && !(p.phone_number || '').includes(filters.phoneSearch)) return false;
      if (filters.dateFrom && new Date(p.registered_at) < new Date(filters.dateFrom)) return false;
      if (filters.dateTo && new Date(p.registered_at) > new Date(filters.dateTo)) return false;
      return true;
    });
    const { key, direction } = sortConfig.participants;
    if (key) {
      // Предвычисление для сортировки по статусу — иначе O(N²·M)
      // на каждое сравнение (find по searchRequests + filter по matches).
      let statusValueByUserId = null;
      let nameLowerByUserId = null;
      if (key === 'status') {
        const statusOrder = { inactive: 0, searching: 1, waiting_for_match: 2, multiple: 3, rejected: 4, accepted: 5, completed: 6, closed: 7, unknown: 8, no_request: 9 };
        const srByUserId = new Map(searchRequests.map(sr => [sr.user_id, sr]));
        statusValueByUserId = new Map();
        nameLowerByUserId = new Map();
        result.forEach(p => {
          const sr = srByUserId.get(p.user_id);
          statusValueByUserId.set(
            p.user_id,
            sr ? (statusOrder[getRequestStatus(sr, matches).type] ?? 8) : 9
          );
          nameLowerByUserId.set(p.user_id, (p.name || '').toLowerCase());
        });
      }
      result = [...result].sort((a, b) => {
        let aVal, bVal;
        switch(key) {
          case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
          case 'phone': aVal = (a.phone_number || '').toLowerCase(); bVal = (b.phone_number || '').toLowerCase(); break;
          case 'profile': aVal = getProfileFill(a); bVal = getProfileFill(b); break;
          case 'status': {
            aVal = statusValueByUserId.get(a.user_id);
            bVal = statusValueByUserId.get(b.user_id);
            if (aVal === bVal) {
              const an = nameLowerByUserId.get(a.user_id);
              const bn = nameLowerByUserId.get(b.user_id);
              return an < bn ? -1 : an > bn ? 1 : 0;
            }
            break;
          }
          case 'registered_at': aVal = new Date(a.registered_at); bVal = new Date(b.registered_at); break;
          case 'matches_count': aVal = a.matches_count || 0; bVal = b.matches_count || 0; break;
          default: aVal = a[key]; bVal = b[key];
        }
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [participants, filters, sortConfig.participants, searchRequests, matches, getRequestStatus]);

  const filteredSearchRequests = useMemo(() => {
    // Кэшируем getRequestStatus по sr.id — внутри он filter-ит matches,
    // и при фильтрации/сортировке вызывается N+ раз для одного и того же sr.
    const statusInfoById = new Map();
    const getStatusInfo = (sr) => {
      let info = statusInfoById.get(sr.id);
      if (!info) {
        info = getRequestStatus(sr, matches);
        statusInfoById.set(sr.id, info);
      }
      return info;
    };

    let result = searchRequests.filter(sr => {
      const statusInfo = getStatusInfo(sr);

      if (searchRequestsFilters.status !== 'all' && statusInfo.type !== searchRequestsFilters.status) return false;
      if (searchRequestsFilters.category !== 'all' && sr.category !== searchRequestsFilters.category) return false;
      if (searchRequestsFilters.userSearch && !sr.user_name.toLowerCase().includes(searchRequestsFilters.userSearch.toLowerCase())) return false;
      if (searchRequestsFilters.idSearch && !sr.id.toString().includes(searchRequestsFilters.idSearch.trim())) return false;
      if (searchRequestsFilters.emailSearch && !(sr.user_email || '').toLowerCase().includes(searchRequestsFilters.emailSearch.toLowerCase())) return false;
      if (sr.total_matches < searchRequestsFilters.matchesMin) return false;
      if (searchRequestsFilters.dateFrom && new Date(sr.created_at) < new Date(searchRequestsFilters.dateFrom)) return false;
      if (searchRequestsFilters.dateTo && new Date(sr.created_at) > new Date(searchRequestsFilters.dateTo)) return false;
      return true;
    });
    const { key, direction } = sortConfig.searchRequests;
    if (key) {
      // Для сортировки по статусу — берём из уже вычисленного кэша.
      let statusValueById = null;
      if (key === 'status') {
        const statusOrder = { inactive: 0, searching: 1, waiting_for_match: 2, multiple: 3, rejected: 4, accepted: 5, completed: 6, closed: 7, unknown: 8 };
        statusValueById = new Map();
        result.forEach(sr => {
          statusValueById.set(sr.id, statusOrder[getStatusInfo(sr).type] ?? 99);
        });
      }
      result.sort((a, b) => {
        let aVal, bVal;
        switch(key) {
          case 'id': aVal = a.id; bVal = b.id; break;
          case 'user_name': aVal = a.user_name.toLowerCase(); bVal = b.user_name.toLowerCase(); break;
          case 'category': aVal = a.category.toLowerCase(); bVal = b.category.toLowerCase(); break;
          case 'status': {
            aVal = statusValueById.get(a.id);
            bVal = statusValueById.get(b.id);
            break;
          }
          case 'created_at': aVal = new Date(a.created_at); bVal = new Date(b.created_at); break;
          case 'total_matches': aVal = a.total_matches; bVal = b.total_matches; break;
          default: aVal = a[key]; bVal = b[key];
        }
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [searchRequests, matches, searchRequestsFilters, sortConfig.searchRequests, getRequestStatus]);

  const filteredMatches = useMemo(() => {
    let result = matches.filter(m => {
      if (matchesFilters.status !== 'all' && m.status !== matchesFilters.status) return false;
      if (matchesFilters.category !== 'all' && m.category !== matchesFilters.category) return false;
      if (matchesFilters.idSearch && !String(m.id).includes(matchesFilters.idSearch.trim())) return false;
      if (matchesFilters.user1Search && !m.user1_name.toLowerCase().includes(matchesFilters.user1Search.toLowerCase())) return false;
      if (matchesFilters.user2Search && !m.user2_name.toLowerCase().includes(matchesFilters.user2Search.toLowerCase())) return false;
      if (m.compatibility_score < matchesFilters.scoreMin || m.compatibility_score > matchesFilters.scoreMax) return false;
      if (matchesFilters.dateFrom && new Date(m.created_at) < new Date(matchesFilters.dateFrom)) return false;
      if (matchesFilters.dateTo && new Date(m.created_at) > new Date(matchesFilters.dateTo)) return false;
      return true;
    });
    const { key, direction } = sortConfig.matches;
    if (key) {
      result.sort((a, b) => {
        let aVal, bVal;
        switch(key) {
          case 'id': aVal = a.id; bVal = b.id; break;
          case 'user1_name': aVal = a.user1_name.toLowerCase(); bVal = b.user1_name.toLowerCase(); break;
          case 'user2_name': aVal = a.user2_name.toLowerCase(); bVal = b.user2_name.toLowerCase(); break;
          case 'category': aVal = a.category.toLowerCase(); bVal = b.category.toLowerCase(); break;
          case 'status': aVal = a.status; bVal = b.status; break;
          case 'created_at': aVal = new Date(a.created_at); bVal = new Date(b.created_at); break;
          case 'compatibility_score': aVal = a.compatibility_score; bVal = b.compatibility_score; break;
          default: aVal = a[key]; bVal = b[key];
        }
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [matches, matchesFilters, sortConfig.matches]);

  const stats = useMemo(() => {
    const total = participants.length;
    const withProfile = participants.filter(p => getProfileFill(p) === 100).length;
    const halfProfile = participants.filter(p => getProfileFill(p) === 50).length;
    const pending = participants.filter(p => p.status === 'pending').length;
    const confirmed = participants.filter(p => p.status === 'confirmed').length;
    return {
      total,
      withProfile,
      halfProfile,
      pending,
      confirmed,
      avgProfile: Math.round(participants.reduce((sum, p) => sum + getProfileFill(p), 0) / (participants.length || 1))
    };
  }, [participants]);

  // ===== PANEL FUNCTIONS =====
  const openDrawer = async (participant) => {
    setSelectedParticipant(participant);
    setIsDrawerOpen(true);
    setIsRequestDetailsPanelOpen(false);
    setIsMatchDetailsPanelOpen(false);
    setSelectedRequest(null);
    setSelectedMatch(null);
    document.body.style.overflow = "hidden";
    await loadParticipantSearchRequests(participant);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setIsRequestDetailsPanelOpen(false);
    setIsMatchDetailsPanelOpen(false);
    setSelectedParticipant(null);
    setSelectedRequest(null);
    setSelectedMatch(null);
    setParticipantRequests([]);
    document.body.style.overflow = "auto";
  };

  const loadParticipantSearchRequests = async (participant) => {
    setParticipantRequestsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/api/events/${eventId}/participants/${participant.user_id}/search_requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load participant search requests");
      const data = await res.json();
      setParticipantRequests(data.search_requests || []);
    } catch (err) {
      console.error("Error loading participant search requests:", err);
      setParticipantRequests([]);
    } finally {
      setParticipantRequestsLoading(false);
    }
  };

  const openRequestDetailsPanel = (request) => {
    setSelectedRequest(request);
    const matchesForRequest = matches.filter(match =>
      match.search_request_user1_id === request.id ||
      match.search_request_user2_id === request.id
    );
    setSelectedRequestMatches(matchesForRequest);
    setIsRequestDetailsPanelOpen(true);
  };

  const closeRequestDetailsPanel = () => {
    setIsRequestDetailsPanelOpen(false);
    setSelectedRequest(null);
    setSelectedRequestMatches([]);
  };

  const openMatchDetailsPanel = (match) => {
    setSelectedMatch(match);
    setIsMatchDetailsPanelOpen(true);
  };

  const closeMatchDetailsPanel = () => {
    setIsMatchDetailsPanelOpen(false);
    setSelectedMatch(null);
  };

  const openMatchDetailsModal = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    setMatchDetails(match || null);
    setSelectedMatchId(matchId);
    setIsMatchDetailsModalOpen(true);
  };

  // ===== CSV EXPORT =====
  const exportParticipantsToCSV = () => {
    const headers = ['ID', 'Name', 'Phone', 'Status', 'Registered At', 'Profile Complete', 'Request', 'About'];
    const rows = filteredParticipants.map(p => [
      p.user_id,
      `"${p.name.replace(/"/g, '""')}"`,
      `"${(p.phone_number || p.email || '').replace(/"/g, '""')}"`,
      p.status,
      p.registered_at,
      `${getProfileFill(p)}%`,
      `"${(p.request || '').replace(/"/g, '""')}"`,
      `"${(p.about || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-${eventId}-participants-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportSearchRequestsToCSV = () => {
    const headers = ['ID', 'User', 'Category', 'Status', 'Created At', 'Total Matches', 'Pending Matches', 'Request Text'];
    const rows = filteredSearchRequests.map(sr => {
      const statusInfo = getRequestStatus(sr, matches);
      return [
        sr.id,
        `"${sr.user_name.replace(/"/g, '""')}"`,
        sr.category,
        statusInfo.label,
        sr.created_at,
        sr.total_matches,
        sr.pending_matches,
        `"${(sr.request_text || '').replace(/"/g, '""')}"`
      ];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-${eventId}-search-requests-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportMatchesToCSV = () => {
    const headers = ['ID', 'User 1', 'User 2', 'Category', 'Status', 'User1 Status', 'User2 Status', 'Created At', 'Compatibility Score', 'Match Reason'];
    const rows = filteredMatches.map(m => [
      m.id,
      `"${m.user1_name.replace(/"/g, '""')}"`,
      `"${m.user2_name.replace(/"/g, '""')}"`,
      m.category,
      m.status,
      m.user1_status || 'N/A',
      m.user2_status || 'N/A',
      m.created_at,
      m.compatibility_score,
      `"${(m.match_reason || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-${eventId}-matches-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ===== CO-ADMIN MANAGEMENT =====
  const handleAddAdmin = async () => {
    const input = addAdminInput.toString().trim();
    if (!input) return;
    const token = localStorage.getItem("token");
    setAddAdminLoading(true);
    try {
      const res = await fetch(`/api/events/${eventId}/admins`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: input }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(`❌ ${data.detail || (lang === "fr" ? "Erreur" : "Error")}`);
        return;
      }
      setCoAdmins(prev => [...prev, data.admin]);
      setAddAdminInput('');
      showToast(`✅ ${lang === "fr" ? "Admin added" : "Admin added"}`);
    } catch (err) {
      showToast(`❌ ${lang === "fr" ? "Network error" : "Network error"}`);
    } finally {
      setAddAdminLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/events/${eventId}/admins/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setCoAdmins(prev => prev.filter(a => a.user_id !== userId));
        showToast(`✅ ${lang === "fr" ? "Admin removed" : "Admin removed"}`);
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(`❌ ${data.detail || "Error"}`);
      }
    } catch (err) {
      showToast(`❌ ${lang === "fr" ? "Network error" : "Network error"}`);
    }
  };

  // ===== KICK PARTICIPANT =====
  const handleKickUser = async (userId, userName) => {
    const confirmMsg = lang === "fr"
      ? `Expulser ${userName || `User #${userId}`} de l'événement ?`
      : `Kick ${userName || `User #${userId}`} from the event?`;
    if (!window.confirm(confirmMsg)) return;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`/api/events/${eventId}/participants/${userId}/kick`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showToast(`🚫 ${lang === "fr" ? "Participant removed" : "Participant removed"}`);
        setParticipants(prev => prev.filter(p => p.user_id !== userId));
        setReferrals(prev => prev.map(r => ({
          ...r,
          scans: r.scans.map(s =>
            s.registered_user_id === userId
              ? { ...s, resulted_in_registration: false, registered_user_id: null, kicked: true }
              : s
          )
        })));
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(`❌ ${data.detail || "Error"}`);
      }
    } catch (err) {
      showToast(`❌ ${lang === "fr" ? "Network error" : "Network error"}`);
    }
  };

  // ===== THEME COLORS =====
  // Dark: original deep-space purple-blue (#0a0a14 base)
  // Light: warm cream (#FFF3E6 base) + deep cherry primary (#381931)
  const themeVars = theme === "dark" ? {
    bg:           "#0a0a14",
    fg:           "#e6e9f0",
    card:         "#1a1f2e",
    cardHover:    "#252b3d",
    border:       "rgba(139,92,246,0.18)",
    subtle:       "rgba(26,31,46,0.85)",
    primary:      "#8b5cf6",
    primaryLight: "rgba(139,92,246,0.1)",
    accent:       "#7c3aed",
    smallText:    "#a0aec0",
    success:      "#10b981",
    successLight: "rgba(16,185,129,0.1)",
    warning:      "#f59e0b",
    warningLight: "rgba(245,158,11,0.1)",
    danger:       "#ef4444",
    dangerLight:  "rgba(239,68,68,0.1)",
    info:         "#3b82f6",
    gradient:     "linear-gradient(135deg, #8b5cf6, #7c3aed)",
  } : {
    bg:           "#FFF3E6",
    fg:           "#1a0c14",
    card:         "#ffffff",
    cardHover:    "#FFF8F2",
    border:       "rgba(56,25,49,0.12)",
    subtle:       "rgba(255,255,255,0.92)",
    primary:      "#381931",
    primaryLight: "rgba(56,25,49,0.09)",
    accent:       "#6b2e58",
    smallText:    "#8a6070",
    success:      "#2a7a50",
    successLight: "rgba(42,122,80,0.10)",
    warning:      "#b8620a",
    warningLight: "rgba(184,98,10,0.09)",
    danger:       "#c0392b",
    dangerLight:  "rgba(192,57,43,0.09)",
    info:         "#5c2d8a",
    gradient:     "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  // ===== ОБНОВЛЁННЫЙ STATUSBADGE С ДЕТАЛИЗАЦИЕЙ =====
  const StatusBadge = ({ status, type = 'participant', details = null }) => {
    const styles = {
      participant: {
        registered: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Inscrit" : "Registered" },
        pending: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "En attente" : "Pending" },
        confirmed: { bg: themeVars.primaryLight, color: themeVars.primary, text: lang === "fr" ? "Confirmé" : "Confirmed" },
        cancelled: { bg: themeVars.dangerLight, color: themeVars.danger, text: lang === "fr" ? "Annulé" : "Cancelled" }
      },
      request: {
        inactive: { bg: `${themeVars.smallText}15`, color: themeVars.smallText, text: lang === "fr" ? "Inactif" : "Inactive" },
        searching: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "Recherche" : "Searching" },
        multiple: { bg: themeVars.info + '15', color: themeVars.info, text: lang === "fr" ? "Plusieurs matchs" : "Multiple Matches" },
        accepted: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Accepté" : "Accepted" },
        pending: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "En attente" : "Pending" },
        rejected: { bg: themeVars.dangerLight, color: themeVars.danger, text: lang === "fr" ? "Rejeté" : "Rejected" },
        waiting_for_match: { bg: themeVars.primaryLight, color: themeVars.primary, text: lang === "fr" ? "En attente de réponse utilisateur" : "Waiting for User Response" },
        completed: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Terminé" : "Completed" },
        closed: { bg: themeVars.smallText + '15', color: themeVars.smallText, text: lang === "fr" ? "Fermé" : "Closed" },
        unknown: { bg: themeVars.smallText + '15', color: themeVars.smallText, text: lang === "fr" ? "Inconnu" : "Unknown" }
      },
      match: {
        pending: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "En attente" : "Pending" },
        active: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Accepté" : "Accepted" },
        rejected: { bg: themeVars.dangerLight, color: themeVars.danger, text: lang === "fr" ? "Rejeté" : "Rejected" }
      }
    };
    
    const style = styles[type]?.[status] || styles.participant.registered;
    
    return (
      <div style={{ display: "inline-flex", flexDirection: "column", gap: "4px" }}>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
          background: style.bg, color: style.color, border: `1px solid ${style.color}30`,
          whiteSpace: "nowrap", minWidth: "fit-content",
        }}>
          {style.text}
        </span>
        
        {/* Детали статусов матчей */}
        {details && (details.pendingCount > 0 || details.acceptedCount > 0 || details.rejectedCount > 0) && (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", fontSize: "0.75rem" }}>
            {details.acceptedCount > 0 && (
              <span style={{ color: themeVars.success, fontWeight: 500 }}>
                ✅ {details.acceptedCount}
              </span>
            )}
            {details.pendingCount > 0 && (
              <span style={{ color: themeVars.warning, fontWeight: 500 }}>
                ⏳ {details.pendingCount}
              </span>
            )}
            {details.rejectedCount > 0 && (
              <span style={{ color: themeVars.danger, fontWeight: 500 }}>
                ❌ {details.rejectedCount}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const CategoryBadge = ({ category }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
      background: themeVars.primaryLight, color: themeVars.primary,
      border: `1px solid ${themeVars.primary}30`, textTransform: "capitalize",
      whiteSpace: "nowrap", minWidth: "fit-content",
    }}>
      {category}
    </span>
  );

  const getUserStatusBadge = (userStatus, userName) => {
    const statusStyles = {
      accepted: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Accepté" : "Accepted", icon: "✅" },
      rejected: { bg: themeVars.dangerLight, color: themeVars.danger, text: lang === "fr" ? "Rejeté" : "Rejected", icon: "❌" },
      pending: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "En attente" : "Pending", icon: "⏳" }
    };
    const style = statusStyles[userStatus] || statusStyles.pending;
    const shortName = userName ? (userName.length > 12 ? userName.substring(0, 12) + '...' : userName) : 'User';
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: "6px", padding: "5px 12px", borderRadius: "16px", fontSize: "0.85rem", fontWeight: 600,
        background: style.bg, color: style.color, border: `1px solid ${style.color}30`,
        whiteSpace: "nowrap", minWidth: "fit-content",
      }}>
        {style.icon} {shortName}: {style.text}
      </span>
    );
  };

  const getMatchStatusBadge = (status, user1Status, user2Status, user1Name, user2Name) => {
    const styles = {
      pending: { bg: themeVars.warningLight, color: themeVars.warning, text: lang === "fr" ? "En attente" : "Pending" },
      active: { bg: themeVars.successLight, color: themeVars.success, text: lang === "fr" ? "Accepté" : "Accepted" },
      rejected: { bg: themeVars.dangerLight, color: themeVars.danger, text: lang === "fr" ? "Rejeté" : "Rejected" }
    };
    const style = styles[status] || styles.pending;
    if (status === 'pending') {
      const user1Badge = getUserStatusBadge(user1Status, user1Name);
      const user2Badge = getUserStatusBadge(user2Status, user2Name);
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: "fit-content" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
            background: style.bg, color: style.color, border: `1px solid ${style.color}30`,
            whiteSpace: "nowrap", minWidth: "fit-content", marginBottom: "2px"
          }}>
            {style.text}
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {user1Badge}
            {user2Badge}
          </div>
        </div>
      );
    }
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
        background: style.bg, color: style.color, border: `1px solid ${style.color}30`,
        whiteSpace: "nowrap", minWidth: "fit-content",
      }}>
        {style.text}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    if (!role) return null;
    const roleStyles = {
      admin: { bg: "#ef444415", color: "#ef4444", icon: "👑" },
      event_admin: { bg: "#f59e0b15", color: "#f59e0b", icon: "🎟️" },
      event_user: { bg: "#10b98115", color: "#10b981", icon: "👥" },
      user: { bg: "#6366f115", color: "#6366f1", icon: "👤" }
    };
    const style = roleStyles[role] || roleStyles.user;
    const displayRole = lang === "fr"
      ? ({ admin: "Admin", event_admin: "Admin Événement", event_user: "Utilisateur Événement", user: "Utilisateur" }[role] || role)
      : ({ admin: "Admin", event_admin: "Event Admin", event_user: "Event User", user: "User" }[role] || role);
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: "4px", padding: "5px 12px", borderRadius: "16px", fontSize: "0.85rem", fontWeight: 600,
        background: style.bg, color: style.color, border: `1px solid ${style.color}30`,
        whiteSpace: "nowrap", minWidth: "fit-content",
      }}>
        {style.icon} {displayRole}
      </span>
    );
  };

  // ===== ICON BUTTON =====
  const IconButton = ({ onClick, icon, label, color = themeVars.primary, hoverBg }) => (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        padding: "10px 18px", borderRadius: "14px",
        background: "transparent", border: `1px solid ${color}30`,
        color: color, fontSize: "0.95rem", fontWeight: 500,
        cursor: "pointer", transition: "all 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.background = hoverBg || `${color}15`;
        e.target.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.target.style.background = "transparent";
        e.target.style.transform = "translateY(0)";
      }}
    >
      {icon}
      {label}
    </button>
  );

  // ===== STATS CARD =====
  const StatsCard = ({ label, value, color, icon }) => (
    <div
      className="ead-stats-card"
      style={{
        padding: isMobile ? "12px 8px" : "20px", borderRadius: isMobile ? "12px" : "16px",
        background: themeVars.card, border: `1px solid ${themeVars.border}`,
        textAlign: "center",
        transition: "all 0.3s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `0 8px 24px ${color}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = themeVars.border;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {isMobile && <div style={{ fontSize: "1.1rem", marginBottom: "2px" }}>{icon}</div>}
      <div className="ead-stats-card-value" style={{ fontSize: isMobile ? "1.25rem" : "1.8rem", fontWeight: 700, color, marginBottom: "2px" }}>
        {value}
      </div>
      <div style={{ fontSize: isMobile ? "0.65rem" : "0.95rem", color: themeVars.smallText, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1.2 }}>
        {label}
      </div>
    </div>
  );

  // ===== TAB BUTTON =====
  const TabButton = ({ id, label, icon, count }) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          display: "flex", alignItems: "center", gap: isMobile ? "5px" : "8px",
          padding: isMobile ? "9px 13px" : "12px 20px", borderRadius: "12px",
          background: isActive ? themeVars.gradient : "transparent",
          color: isActive ? "#fff" : themeVars.smallText,
          border: "none", fontSize: isMobile ? "0.85rem" : "0.95rem", fontWeight: 600,
          cursor: "pointer", transition: "all 0.3s ease",
          position: "relative", overflow: "hidden",
          whiteSpace: "nowrap", flexShrink: 0,
        }}
      >
        {icon} {label}
        {count !== undefined && (
          <span style={{
            padding: "2px 8px", borderRadius: "10px",
            background: isActive ? "rgba(255,255,255,0.2)" : themeVars.primaryLight,
            color: isActive ? "#fff" : themeVars.primary,
            fontSize: "0.75rem", fontWeight: 700,
          }}>
            {count}
          </span>
        )}
      </button>
    );
  };

  // ===== SORTABLE HEADER =====
  const SortableHeader = ({ table, columnKey, children }) => {
    const currentSort = sortConfig[table];
    const isSorted = currentSort.key === columnKey;
    return (
      <th
        onClick={() => handleSort(table, columnKey)}
        style={{
          textAlign: "left", padding: "18px 16px", fontSize: "0.8rem", fontWeight: 700,
          color: themeVars.fg, borderBottom: `2px solid ${themeVars.border}`,
          background: themeVars.subtle, cursor: "pointer", userSelect: "none",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
        title={lang === "fr" ? "Cliquer pour trier" : "Click to sort"}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {children}
          <span style={{
            fontSize: '0.75rem',
            opacity: isSorted ? 1 : 0.4,
            color: isSorted ? themeVars.primary : themeVars.smallText
          }}>
            {isSorted ? (currentSort.direction === 'asc' ? '↑' : '↓') : '↕'}
          </span>
        </div>
      </th>
    );
  };

  // ===== TABLE STYLES =====
  const tableCellStyle = (isNameCell = false) => ({
    padding: "18px 16px", fontSize: "0.95rem",
    color: isNameCell ? themeVars.fg : themeVars.smallText,
    fontWeight: isNameCell ? 500 : 400, verticalAlign: "middle",
    borderBottom: `1px solid ${themeVars.border}`,
  });

  // ===== RENDER CONTENT BASED ON TAB =====
  const renderContent = () => {
    switch (activeTab) {
      case 'participants':
        return renderParticipantsSection();
      case 'requests':
        return canViewSearchRequests ? renderRequestsSection() : renderParticipantsSection();
      case 'matches':
        return renderMatchesSection();
      case 'tables':
        return renderTablesSection();
      case 'invitedUsers':
        return renderInvitedUsersSection();
      case 'comments':
        return renderCommentsSection();
      case 'admins':
        return renderAdminsSection();
      default:
        return renderParticipantsSection();
    }
  };

  // ===== BUILD TABLE LIVE HTML (popup window) =====
  const buildTableLiveHTML = (evId, token, apiBase, lng) => `<!DOCTYPE html>
<html lang="${lng}">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>🪑 Tables Live · Event #${evId}</title>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  :root{
    --bg:#09090f;--card:#12121e;--card2:#1a1a2e;--border:rgba(99,102,241,0.18);
    --fg:#e2e8f0;--sub:#64748b;--green:#22c55e;--orange:#f59e0b;
    --red:#ef4444;--indigo:#6366f1;--yellow:#f59e0b;
  }
  html,body{height:100%}
  body{font-family:'Inter',system-ui,-apple-system,sans-serif;background:var(--bg);color:var(--fg);min-height:100vh;overflow-x:hidden;-webkit-font-smoothing:antialiased}
  @keyframes pulse{0%,100%{opacity:1;box-shadow:0 0 6px var(--green)}50%{opacity:.5;box-shadow:0 0 14px var(--green)}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideIn{from{opacity:0;transform:translateX(-10px)}to{opacity:1;transform:translateX(0)}}
  @keyframes timerFlash{0%,100%{color:var(--red)}50%{color:#ff8080}}
  @keyframes rainbow{0%{background-position:0% 50%}100%{background-position:200% 50%}}
  @keyframes rowEnter{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
  @keyframes scaleIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}

  .rainbow-bar{height:3px;background:linear-gradient(90deg,#6366f1,#a78bfa,#ec4899,#f59e0b,#22c55e,#06b6d4,#6366f1);background-size:200% 100%;animation:rainbow 2.5s linear infinite}

  .header{
    display:flex;align-items:center;justify-content:space-between;
    padding:16px 24px;border-bottom:1px solid var(--border);
    backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);
    position:sticky;top:0;z-index:10;
    background:rgba(9,9,15,.88);gap:12px;flex-wrap:wrap;
  }
  .header-left{display:flex;align-items:center;gap:10px}
  .dot{width:9px;height:9px;border-radius:50%;background:var(--green);animation:pulse 2s ease infinite;flex-shrink:0}
  .header-title{font-size:.95rem;font-weight:800;line-height:1.2}
  .header-sub{font-size:.65rem;color:var(--sub);margin-top:2px}

  .stats-bar{display:flex;gap:0;border:1px solid var(--border);border-radius:12px;overflow:hidden;background:var(--card)}
  .stat{padding:10px 18px;text-align:center;border-right:1px solid var(--border)}
  .stat:last-child{border-right:none}
  .stat-val{font-size:1.4rem;font-weight:900;line-height:1;font-variant-numeric:tabular-nums}
  .stat-lbl{font-size:.58rem;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:var(--sub);margin-top:3px}

  .stime{
    font-size:2rem;
    color:var(--fg);
    font-variant-numeric:tabular-nums;
    white-space:nowrap;
    font-weight:900;
    letter-spacing:.04em;
    line-height:1;
    padding:8px 16px;
    border-radius:12px;
    border:1px solid rgba(99,102,241,.35);
    background:rgba(99,102,241,.12);
    box-shadow:0 8px 20px rgba(0,0,0,.28);
    min-width:190px;
    text-align:center;
  }

  .content{padding:18px 24px;display:flex;flex-direction:column;gap:16px}

  .complete-banner{
    background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.25);
    border-radius:14px;padding:14px 18px;animation:fadeIn .4s ease;
  }
  .complete-banner b{color:var(--green)}

  .grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:12px}

  .table-card{
    background:var(--card);border:1px solid var(--border);
    border-radius:16px;padding:14px 16px;
    transition:border-color .3s,box-shadow .3s;
  }
  .table-card.occupied{border-color:rgba(99,102,241,.35);box-shadow:0 0 0 1px rgba(99,102,241,.12)}
  .table-card.free{opacity:.42}

  .table-label{
    font-size:.62rem;font-weight:700;text-transform:uppercase;
    letter-spacing:.1em;color:var(--sub);margin-bottom:10px;
    display:flex;align-items:center;gap:6px
  }
  .free-badge{color:var(--green);font-weight:700}
  .waiting{text-align:center;padding:18px 0;font-size:.75rem;color:var(--sub)}

  .slot{
    background:rgba(255,255,255,.04);border:1px solid var(--border);
    border-radius:11px;padding:11px 13px;margin-bottom:7px;
    position:relative;overflow:hidden;
    transition:border-color .3s;
  }
  .slot:last-child{margin-bottom:0}
  .slot.urgent{border-color:rgba(245,158,11,.45)}
  .slot.critical{border-color:rgba(239,68,68,.6)}

  .slot-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:7px}
  .slot-meta{font-size:.64rem;color:var(--sub);line-height:1.5}
  .timer{
    font-size:2rem;font-weight:900;font-variant-numeric:tabular-nums;line-height:1;
    transition:color .5s;
    padding:4px 10px;border-radius:10px;
    background:rgba(0,0,0,.28);border:1px solid rgba(255,255,255,.1);
    box-shadow:0 0 0 1px rgba(99,102,241,.15), 0 6px 16px rgba(0,0,0,.25);
  }
  .timer.ok{color:var(--green)}
  .timer.warn{color:var(--orange)}
  .timer.crit{animation:timerFlash 1s ease infinite}

  .progress{height:3px;border-radius:2px;background:rgba(255,255,255,.07);overflow:hidden;margin-bottom:9px}
  .progress-bar{height:100%;border-radius:2px;transition:width .9s linear,background .5s}

  .users{display:flex;gap:6px}
  .user-pill{
    flex:1;min-width:0;background:rgba(255,255,255,.05);border-radius:7px;
    padding:5px 8px;display:flex;align-items:center;gap:6px;
    cursor:pointer;transition:background .15s;
  }
  .user-pill:hover{background:rgba(99,102,241,.18)}
  .avatar{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.8rem;font-weight:800;flex-shrink:0}
  .uname{font-size:.9rem;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0}

  .actions{display:flex;align-items:center;gap:5px;margin-top:7px}
  .actions-label{font-size:.6rem;color:var(--sub);text-transform:uppercase;letter-spacing:.05em;flex-shrink:0}
  .btn-time{
    padding:2px 8px;border-radius:6px;font-size:.68rem;font-weight:700;
    border:1px solid var(--border);background:transparent;color:var(--sub);
    cursor:pointer;transition:all .15s;
  }
  .btn-time:hover{border-color:var(--indigo);color:var(--indigo)}
  .btn-release{
    margin-left:auto;background:none;border:none;cursor:pointer;
    color:var(--sub);font-size:.8rem;padding:2px 5px;border-radius:5px;
    transition:color .15s;
  }
  .btn-release:hover{color:var(--red)}

  .section-card{background:var(--card);border:1px solid var(--border);border-radius:14px;overflow:hidden}
  .section-head{
    display:flex;align-items:center;gap:8px;padding:10px 16px;
    border-bottom:1px solid var(--border);background:rgba(255,255,255,.02)
  }
  .section-title{font-weight:700;font-size:.85rem}
  .badge{padding:2px 10px;border-radius:20px;font-size:.68rem;font-weight:700;margin-left:auto}

  .queue-row,.done-row{
    display:flex;align-items:center;gap:10px;padding:9px 16px;
    border-bottom:1px solid rgba(255,255,255,.04);
    transition:background .15s;
  }
  .queue-row:last-child,.done-row:last-child{border-bottom:none}
  .queue-row:hover,.done-row:hover{background:rgba(255,255,255,.03)}

  .pos{width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:800;flex-shrink:0}
  .mid{font-size:.68rem;color:var(--sub);min-width:58px;flex-shrink:0}
  .names{flex:1;min-width:0;display:flex;align-items:center;gap:6px;overflow:hidden}
  .nname{font-size:.78rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:43%;cursor:pointer;transition:color .15s}
  .nname:hover{color:var(--indigo)}
  .next-badge{padding:2px 7px;border-radius:5px;background:rgba(245,158,11,.12);color:var(--orange);font-size:.58rem;font-weight:800;text-transform:uppercase;flex-shrink:0}
  .done-check{color:var(--green);flex-shrink:0;font-size:.85rem}
  .btn-restart{
    padding:3px 9px;border-radius:7px;font-size:.68rem;font-weight:700;
    border:1px solid var(--border);background:transparent;color:var(--sub);
    cursor:pointer;transition:all .15s;flex-shrink:0;
  }
  .btn-restart:hover{border-color:var(--indigo);color:var(--indigo);background:rgba(99,102,241,.08)}

  .empty{text-align:center;padding:48px 20px;color:var(--sub)}
  .empty-icon{font-size:2.5rem;margin-bottom:12px}
  .empty-title{font-size:1rem;font-weight:700;color:var(--fg);margin-bottom:6px}

  @media(max-width:600px){
    .header{padding:12px 16px}
    .content{padding:12px 16px}
    .stats-bar{gap:0}
    .stat{padding:8px 12px}
    .stat-val{font-size:1.1rem}
    .stime{font-size:1.45rem;min-width:150px}
    .avatar{width:24px;height:24px;font-size:.72rem}
    .uname{font-size:.82rem}
  }
</style>
</head>
<body>
<div class="rainbow-bar"></div>
<div class="header">
  <div class="header-left">
    <div class="dot"></div>
    <div>
      <div class="header-title">${lng === "fr" ? "Tables — Vue en direct" : "Tables — Live View"}</div>
      <div class="header-sub">${lng === "fr" ? "Mise à jour automatique" : "Auto-updating"} · Event #${evId}</div>
    </div>
  </div>
  <div class="stime" id="stime"></div>
  <div class="stats-bar" id="stats"></div>
</div>

<div class="content">
  <div id="complete-banner"></div>
  <div class="grid" id="grid"></div>
  <div id="queue-section"></div>
  <div id="done-section"></div>
  <div id="empty-state" style="display:none">
    <div class="empty">
      <div class="empty-icon">🪑</div>
      <div class="empty-title">${lng === "fr" ? "Aucune table active" : "No active tables"}</div>
      <div style="font-size:.82rem">${lng === "fr" ? "Les tables apparaîtront ici dès que l'événement démarre" : "Tables will appear here once the event starts"}</div>
    </div>
  </div>
</div>

<script>
const TOKEN = ${JSON.stringify(token)};
const API   = ${JSON.stringify(apiBase)};
const EVID  = ${evId};
const LANG  = ${JSON.stringify(lng)};
let lastData = null;
let lastStructureKey = '';

function fmt(s){ return Math.floor(s/60)+':'+(s%60<10?'0':'')+s%60 }
function copyName(name){ try{navigator.clipboard.writeText(name)}catch(e){} }
function getStructureKey(d){
  return JSON.stringify({
    tables: d.number_of_tables || 0,
    slots: (d.slots || []).map(s => [s.slot_id, s.table_number, s.seat_pair, s.match_id, s.user1_name, s.user2_name]),
    queue: (d.queue || []).map(q => [q.match_id, q.position]),
    done: (d.seated || []).map(x => x.match_id),
    complete: !!d.round_complete
  });
}
function updateSlotTimers(d){
  const totalSec = Math.max(1, (d.table_timer_seat || 10) * 60);
  for(const slot of (d.slots || [])){
    const s = Number(slot.seconds_left || 0);
    const timerEl = document.getElementById('timer-'+slot.slot_id);
    const progressEl = document.getElementById('progress-'+slot.slot_id);
    const slotEl = document.getElementById('slot-'+slot.slot_id);
    if(timerEl){
      const timerClass = s < 30 ? 'crit' : s < 60 ? 'warn' : 'ok';
      timerEl.className = 'timer ' + timerClass;
      timerEl.textContent = fmt(s);
    }
    if(progressEl){
      const pct = Math.min(100, Math.round(100 * (1 - (s / totalSec))));
      progressEl.style.width = (100 - pct) + '%';
      progressEl.style.background = s < 30 ? 'var(--red)' : s < 60 ? 'var(--orange)' : 'var(--green)';
    }
    if(slotEl){
      slotEl.classList.toggle('critical', s < 30);
      slotEl.classList.toggle('urgent', s >= 30 && s < 60);
    }
  }
}

async function extend(slotId,mins){
  await fetch(API+'/api/events/'+EVID+'/tables/slots/'+slotId+'/extend?minutes='+mins,
    {method:'POST',headers:{Authorization:'Bearer '+TOKEN}});
}
async function release(slotId){
  await fetch(API+'/api/events/'+EVID+'/tables/slots/'+slotId+'/release',
    {method:'DELETE',headers:{Authorization:'Bearer '+TOKEN}});
}
async function restart(matchId){
  await fetch(API+'/api/events/'+EVID+'/tables/matches/'+matchId+'/restart',
    {method:'POST',headers:{Authorization:'Bearer '+TOKEN}});
}

function render(d){
  lastData = d;

  // Stats
  document.getElementById('stats').innerHTML=
    [['#6366f1', d.occupied, LANG==='fr'?'Aux tables':'At tables'],
     ['#f59e0b', d.queue_length, LANG==='fr'?'File':'Queue'],
     ['#22c55e', d.total_seated||0, LANG==='fr'?'Terminés':'Done'],
     ['#64748b', d.unseated_count||0, LANG==='fr'?'En attente':'Waiting']]
    .map(([c,v,l])=>\`<div class="stat"><div class="stat-val" style="color:\${c}">\${v}</div><div class="stat-lbl">\${l}</div></div>\`)
    .join('');

  const t=new Date(d.server_time||Date.now());
  document.getElementById('stime').textContent=t.toLocaleTimeString();

  // Empty state
  const hasAny = (d.number_of_tables||0)>0;
  document.getElementById('empty-state').style.display = hasAny?'none':'block';
  if(!hasAny){ document.getElementById('grid').innerHTML=''; return; }

  // Complete banner
  document.getElementById('complete-banner').innerHTML = d.round_complete
    ? \`<div class="complete-banner"><b>✅ \${LANG==='fr'?'Tous les matchs ont passé à table !':'All matches completed their table session!'}</b>
       <div style="font-size:.78rem;color:var(--sub);margin-top:4px">\${LANG==='fr'?'Utilisez Restart sur les matchs individuels pour les remettre en file.':'Use Restart on individual matches to re-queue them.'}</div></div>\`
    : '';

  const structureKey = getStructureKey(d);
  if(structureKey !== lastStructureKey){
    lastStructureKey = structureKey;
    const byTable={};
    for(let t=1;t<=(d.number_of_tables||0);t++) byTable[t]=[];
    for(const slot of (d.slots||[])){
      if(!byTable[slot.table_number]) byTable[slot.table_number]=[];
      byTable[slot.table_number].push(slot);
    }

    document.getElementById('grid').innerHTML=Object.entries(byTable).map(([tNum,slots])=>{
      const occupied=slots.length>0;
      return \`<div class="table-card \${occupied?'occupied':'free'}">
        <div class="table-label">
          🪑 \${LANG==='fr'?'Table':'Table'} \${tNum}
          \${!occupied?\`<span class="free-badge">· \${LANG==='fr'?'Libre':'Free'}</span>\`:''}
        </div>
        \${!occupied
          ? \`<div class="waiting">\${LANG==='fr'?'En attente d\\'un match':'Waiting for a match'}</div>\`
          : slots.map(slot=>\`
              <div class="slot" id="slot-\${slot.slot_id}">
                <div class="slot-top">
                  <div class="slot-meta">
                    <div>T\${slot.table_number} · P\${slot.seat_pair}</div>
                    <div style="opacity:.6">Match #\${slot.match_id}</div>
                  </div>
                  <div class="timer ok" id="timer-\${slot.slot_id}">--:--</div>
                </div>
                <div class="progress"><div class="progress-bar" id="progress-\${slot.slot_id}"></div></div>
                <div class="users">
                  \${[{n:slot.user1_name,c:'rgba(99,102,241,.22)',tc:'#6366f1'},{n:slot.user2_name,c:'rgba(245,158,11,.18)',tc:'#f59e0b'}].map(u=>\`
                    <div class="user-pill" onclick="copyName(\${JSON.stringify(u.n||'')})">
                      <div class="avatar" style="background:\${u.c};color:\${u.tc}">\${(u.n||'?')[0].toUpperCase()}</div>
                      <span class="uname" title="\${u.n||'—'}">\${u.n||'—'}</span>
                    </div>\`).join('')}
                </div>
                <div class="actions">
                  <span class="actions-label">+min</span>
                  \${[1,2,5].map(m=>\`<button class="btn-time" onclick="extend(\${slot.slot_id},\${m})">+\${m}</button>\`).join('')}
                  <button class="btn-release" onclick="release(\${slot.slot_id})" title="Release">✕</button>
                </div>
              </div>\`
            ).join('')
        }
      </div>\`;
    }).join('');

    const q=d.queue||[];
    document.getElementById('queue-section').innerHTML=\`
      <div class="section-card">
        <div class="section-head">
          <span>⏳</span>
          <span class="section-title">\${LANG==='fr'?'File d\\'attente':'Queue'}</span>
          <div class="badge" style="background:rgba(245,158,11,.12);color:var(--orange);border:1px solid rgba(245,158,11,.3)">\${q.length} \${LANG==='fr'?'en attente':'waiting'}</div>
        </div>
        \${q.length===0
          ? \`<div class="queue-row"><div class="mid" style="width:100%;text-align:center">\${LANG==='fr'?'La file est vide':'Queue is empty'}</div></div>\`
          : q.map((m,i)=>\`<div class="queue-row">
              <div class="pos" style="background:\${i===0?'rgba(245,158,11,.18)':'rgba(99,102,241,.1)'};color:\${i===0?'var(--orange)':'var(--sub)'}">\${m.position}</div>
              <div class="mid">Match #\${m.match_id}</div>
              <div class="names">
                <span class="nname" onclick="copyName(\${JSON.stringify(m.user1_name||'')})" title="\${m.user1_name}">\${m.user1_name||'—'}</span>
                <span style="color:var(--sub);font-size:.68rem;flex-shrink:0">×</span>
                <span class="nname" onclick="copyName(\${JSON.stringify(m.user2_name||'')})" title="\${m.user2_name}">\${m.user2_name||'—'}</span>
              </div>
              \${i===0?\`<div class="next-badge">NEXT</div>\`:''}
            </div>\`).join('')}
      </div>\`;

    const done=d.seated||[];
    document.getElementById('done-section').innerHTML=\`
      <div class="section-card">
        <div class="section-head">
          <span>✅</span>
          <span class="section-title">\${LANG==='fr'?'Terminés':'Completed sessions'}</span>
          <div class="badge" style="background:rgba(34,197,94,.1);color:var(--green);border:1px solid rgba(34,197,94,.25)">\${done.length}</div>
        </div>
        \${done.length===0
          ? \`<div class="done-row"><div class="mid" style="width:100%;text-align:center">\${LANG==='fr'?'Aucune session terminee':'No completed sessions yet'}</div></div>\`
          : done.map((m,i)=>\`<div class="done-row">
              <span class="done-check">✓</span>
              <div class="mid">Match #\${m.match_id}</div>
              <div class="names">
                <span class="nname" onclick="copyName(\${JSON.stringify(m.user1_name||'')})" title="\${m.user1_name}" style="opacity:.7">\${m.user1_name||'—'}</span>
                <span style="color:var(--sub);font-size:.68rem;flex-shrink:0;opacity:.5">×</span>
                <span class="nname" onclick="copyName(\${JSON.stringify(m.user2_name||'')})" title="\${m.user2_name}" style="opacity:.7">\${m.user2_name||'—'}</span>
              </div>
              <button class="btn-restart" onclick="restart(\${m.match_id})">↩ \${LANG==='fr'?'Relancer':'Restart'}</button>
            </div>\`).join('')}
      </div>\`;
  }
  updateSlotTimers(d);
}

async function poll(){
  try{
    const r=await fetch(API+'/api/events/'+EVID+'/tables/state',
      {headers:{Authorization:'Bearer '+TOKEN}});
    if(r.ok) render(await r.json());
  }catch(e){}
  setTimeout(poll,1000);
}

poll();
</script>
</body>
</html>`;

  // ===== TABLES SECTION (tab) =====
  const renderTablesSection = () => {
    const C = themeVars;
    const token = localStorage.getItem("token");
    // Null-safe UI values — sliders default to 10/3 only as a visual placeholder
    const seatVal = tableSeatTimer ?? 10;
    const rotVal  = tableRotationTimer ?? 3;
    const totalCycle = seatVal + rotVal;
    const timersSet = tableSeatTimer !== null && tableRotationTimer !== null;
    const schedulerStatus = tablesState?.tables_scheduler_status || "stopped";
    const deadlinePassed = Boolean(tablesState?.deadline_passed);
    const isRunning = schedulerStatus === "running";
    const isStopped = schedulerStatus === "stopped" || schedulerStatus === "stopped_deadline";
    const startDisabled = isStarting || isRunning || deadlinePassed;
    const stopDisabled = isStopping || isStopped;
    const statusTone = schedulerStatus === "running"
      ? { bg: "rgba(34,197,94,0.14)", border: "rgba(34,197,94,0.35)", fg: "#22c55e", label: "Running" }
      : schedulerStatus === "stopped_deadline"
        ? { bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.35)", fg: "#f59e0b", label: "Stopped (deadline reached)" }
        : { bg: "rgba(148,163,184,0.16)", border: "rgba(148,163,184,0.35)", fg: "#94a3b8", label: "Stopped" };
    const formatDateTime = (iso) => {
      if (!iso) return "—";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return d.toLocaleString();
    };

    // ── Save all table settings (tables, seats, timers) ──
    const handleSaveTableSettings = async () => {
      setTableSettingsSaving(true);
      try {
        const body = {
          number_of_tables: tableSettingsDraft.number_of_tables !== ""
            ? parseInt(tableSettingsDraft.number_of_tables) : null,
          seats_per_table: tableSettingsDraft.seats_per_table !== ""
            ? parseInt(tableSettingsDraft.seats_per_table) : null,
          table_timer_seat: tableSeatTimer,
          table_timer_rotation: tableRotationTimer,
        };
        const res = await fetch(`/api/events/${eventId}/tables`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          showToast(err.detail || (lang === "fr" ? "Erreur lors de la sauvegarde" : "Failed to save"));
          return;
        }
        const data = await res.json();
        setTableSettings({
          number_of_tables: data.number_of_tables,
          seats_per_table: data.seats_per_table,
        });
        setTableSeatTimer(data.table_timer_seat ?? tableSeatTimer);
        setTableRotationTimer(data.table_timer_rotation ?? tableRotationTimer);
        setTableSettingsEdit(false);
        showToast(lang === "fr" ? "Paramètres sauvegardés ✓" : "Settings saved ✓");
      } catch (err) {
        showToast(lang === "fr" ? "Erreur réseau" : "Network error");
      } finally {
        setTableSettingsSaving(false);
      }
    };

    const handleStartTables = async () => {
      if (!eventId || !token) return;
      setErrorMsg(null);
      setIsStarting(true);
      try {
        await startTables(eventId, token);
        await refreshTablesState();
        showToast(lang === "fr" ? "Tables démarrées ✓" : "Tables started ✓");
      } catch (err) {
        const msg = err?.message || (lang === "fr" ? "Erreur au démarrage des tables" : "Failed to start tables");
        setErrorMsg(msg);
        showToast(msg);
      } finally {
        setIsStarting(false);
      }
    };

    const handleStopTables = async () => {
      if (!eventId || !token) return;
      setErrorMsg(null);
      setIsStopping(true);
      try {
        await stopTables(eventId, token, releaseActiveSlots);
        await refreshTablesState();
        showToast(lang === "fr" ? "Tables arrêtées ✓" : "Tables stopped ✓");
      } catch (err) {
        const msg = err?.message || (lang === "fr" ? "Erreur à l'arrêt des tables" : "Failed to stop tables");
        setErrorMsg(msg);
        showToast(msg);
      } finally {
        setIsStopping(false);
      }
    };

    const SliderTimer = ({ label, desc, icon, value, setValue, min, max, presets, accentColor, accentBg, accentBorder }) => (
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: "16px", padding: "22px 24px",
        boxShadow: theme === "dark" ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "18px", gap: "12px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <span style={{ fontSize: "1.3rem" }}>{icon}</span>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: C.fg }}>{label}</span>
            </div>
            <p style={{ margin: 0, fontSize: "0.8rem", color: C.smallText, lineHeight: 1.5 }}>{desc}</p>
          </div>
          <div style={{
            minWidth: "68px", textAlign: "center", flexShrink: 0,
            background: accentBg, border: `1px solid ${accentBorder}`,
            borderRadius: "12px", padding: "10px 14px",
          }}>
            <div style={{ fontSize: "1.8rem", fontWeight: 800, color: accentColor, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {value}
            </div>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, color: C.smallText, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "3px" }}>
              min
            </div>
          </div>
        </div>
        <div style={{ padding: "4px 0 2px" }}>
          <input
            type="range" min={min} max={max} step={1} value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            style={{
              width: "100%", height: "7px", appearance: "none", WebkitAppearance: "none",
              background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${((value - min) / (max - min)) * 100}%, ${C.border} ${((value - min) / (max - min)) * 100}%, ${C.border} 100%)`,
              borderRadius: "4px", outline: "none", cursor: "pointer",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "7px", fontSize: "0.7rem", color: C.smallText }}>
            <span>{min} min</span>
            <span style={{ color: accentColor, fontWeight: 700 }}>{value} min</span>
            <span>{max} min</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "7px", flexWrap: "wrap", marginTop: "14px" }}>
          {presets.map(v => (
            <button key={v} type="button"
              onClick={() => setValue(v)}
              style={{
                padding: "5px 12px", borderRadius: "8px", fontSize: "0.78rem", fontWeight: 600,
                border: `1px solid ${value === v ? accentColor : C.border}`,
                background: value === v ? accentBg : "transparent",
                color: value === v ? accentColor : C.smallText,
                cursor: "pointer", transition: "all 0.18s ease",
              }}>
              {v} min
            </button>
          ))}
        </div>
      </div>
    );

    return (
      <>
      <div style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: "16px",
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        marginBottom: "18px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontWeight: 700, color: C.fg }}>{lang === "fr" ? "Service des tables" : "Tables service"}</span>
            <span style={{
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.73rem",
              fontWeight: 700,
              background: statusTone.bg,
              border: `1px solid ${statusTone.border}`,
              color: statusTone.fg,
            }}>
              {statusTone.label}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleStartTables}
              disabled={startDisabled}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "0.84rem",
                fontWeight: 700,
                border: "none",
                background: "#22c55e",
                color: "#fff",
                cursor: startDisabled ? "not-allowed" : "pointer",
                opacity: startDisabled ? 0.55 : 1,
              }}
            >
              {isStarting ? (lang === "fr" ? "Démarrage..." : "Starting...") : "Start tables"}
            </button>
            <button
              type="button"
              onClick={handleStopTables}
              disabled={stopDisabled}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "0.84rem",
                fontWeight: 700,
                border: "none",
                background: "#f59e0b",
                color: "#fff",
                cursor: stopDisabled ? "not-allowed" : "pointer",
                opacity: stopDisabled ? 0.55 : 1,
              }}
            >
              {isStopping ? (lang === "fr" ? "Arrêt..." : "Stopping...") : "Stop tables"}
            </button>
          </div>
        </div>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.smallText, fontSize: "0.84rem", fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={releaseActiveSlots}
            onChange={(e) => setReleaseActiveSlots(e.target.checked)}
            style={{ accentColor: "#f59e0b" }}
          />
          Release active slots
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          <div style={{ fontSize: "0.8rem", color: C.smallText }}>
            <strong style={{ color: C.fg }}>Started:</strong> {formatDateTime(tablesState?.tables_scheduler_started_at)}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.smallText }}>
            <strong style={{ color: C.fg }}>Stopped:</strong> {formatDateTime(tablesState?.tables_scheduler_stopped_at)}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.smallText }}>
            <strong style={{ color: C.fg }}>Reason:</strong> {tablesState?.tables_scheduler_stop_reason || "—"}
          </div>
          <div style={{ fontSize: "0.8rem", color: C.smallText, display: "flex", alignItems: "center", gap: 8 }}>
            <strong style={{ color: C.fg }}>Deadline:</strong> {formatDateTime(tablesState?.matching_deadline)}
            {deadlinePassed && (
              <span style={{ color: "#ef4444", fontSize: "0.72rem", fontWeight: 700 }}>
                {lang === "fr" ? "Passed" : "Passed"}
              </span>
            )}
          </div>
        </div>

        {errorMsg && (
          <div style={{
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid rgba(239,68,68,0.35)",
            background: "rgba(239,68,68,0.1)",
            color: "#ef4444",
            fontSize: "0.8rem",
            fontWeight: 600,
          }}>
            {errorMsg}
          </div>
        )}
      </div>

      <div style={{
        background: themeVars.subtle, borderRadius: "24px",
        border: `1px solid ${themeVars.border}`, overflow: "hidden",
        boxShadow: theme === "dark" ? "0 4px 24px rgba(0,0,0,0.28)" : "0 4px 24px rgba(0,0,0,0.07)",
      }}>
        {/* Header */}
        <div style={{ padding: "28px 32px 20px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                width: "44px", height: "44px", borderRadius: "12px",
                background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.28)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem",
              }}>🪑</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: "1.2rem", color: C.fg }}>
                  {lang === "fr" ? "Tables — Minuteries" : "Tables — Timers"}
                </div>
                <div style={{ fontSize: "0.82rem", color: C.smallText, marginTop: "2px" }}>
                  {lang === "fr"
                    ? "Configurez la durée des échanges et le temps de rotation entre tables."
                    : "Configure the time spent at each table and the rotation between tables."}
                </div>
              </div>
            </div>
            {/* Save / Edit buttons */}
            {tableSettingsEdit ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setTableSettingsDraft({
                      number_of_tables: tableSettings.number_of_tables ?? "",
                      seats_per_table: tableSettings.seats_per_table ?? "",
                    });
                    setTableSettingsEdit(false);
                  }}
                  style={{
                    padding: "9px 18px", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600,
                    border: `1px solid ${C.border}`, background: "transparent",
                    color: C.smallText, cursor: "pointer",
                  }}>
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={handleSaveTableSettings}
                  disabled={tableSettingsSaving}
                  style={{
                    padding: "9px 20px", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 700,
                    border: "none", background: themeVars.gradient || "#6366f1",
                    color: "#fff", cursor: tableSettingsSaving ? "not-allowed" : "pointer",
                    opacity: tableSettingsSaving ? 0.7 : 1, transition: "opacity 0.18s",
                    display: "flex", alignItems: "center", gap: "6px",
                  }}>
                  {tableSettingsSaving
                    ? (lang === "fr" ? "Sauvegarde..." : "Saving...")
                    : (lang === "fr" ? "💾 Sauvegarder" : "💾 Save")}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setTableSettingsEdit(true)}
                style={{
                  padding: "9px 20px", borderRadius: "10px", fontSize: "0.88rem", fontWeight: 600,
                  border: `1px solid ${C.border}`, background: C.card,
                  color: C.fg, cursor: "pointer", transition: "all 0.18s",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                ✏️ {lang === "fr" ? "Modifier" : "Edit"}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", gap: "24px" }}>

          {/* ── Tables & Seats config card ── */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: "16px", padding: "22px 24px",
            boxShadow: theme === "dark" ? "0 2px 12px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.06)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
              <span style={{ fontSize: "1.2rem" }}>🗂️</span>
              <span style={{ fontWeight: 700, fontSize: "1rem", color: C.fg }}>
                {lang === "fr" ? "Configuration des tables" : "Table Configuration"}
              </span>
            </div>

            {tableSettingsEdit ? (
              /* Edit mode */
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 160px" }}>
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: C.smallText, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {lang === "fr" ? "Nombre de tables" : "Number of tables"}
                  </label>
                  <input
                    type="number" min="1"
                    value={tableSettingsDraft.number_of_tables}
                    onChange={(e) => setTableSettingsDraft(prev => ({ ...prev, number_of_tables: e.target.value }))}
                    placeholder={lang === "fr" ? "ex. 10" : "e.g. 10"}
                    style={{
                      width: "100%", padding: "10px 14px", borderRadius: "10px",
                      background: themeVars.subtle, border: `1px solid ${C.border}`,
                      color: C.fg, fontSize: "1rem", fontWeight: 600, outline: "none",
                      transition: "border-color 0.18s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#6366f1"}
                    onBlur={(e) => e.target.style.borderColor = C.border}
                  />
                </div>
                <div style={{ flex: "1 1 160px" }}>
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: C.smallText, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {lang === "fr" ? "Places par table" : "Seats per table"}
                    <span style={{ color: C.smallText, fontWeight: 400, marginLeft: "4px" }}>(min 2)</span>
                  </label>
                  <input
                    type="number" min="2"
                    value={tableSettingsDraft.seats_per_table}
                    onChange={(e) => setTableSettingsDraft(prev => ({ ...prev, seats_per_table: e.target.value }))}
                    placeholder={lang === "fr" ? "ex. 4" : "e.g. 4"}
                    style={{
                      width: "100%", padding: "10px 14px", borderRadius: "10px",
                      background: themeVars.subtle, border: `1px solid ${C.border}`,
                      color: C.fg, fontSize: "1rem", fontWeight: 600, outline: "none",
                      transition: "border-color 0.18s",
                    }}
                    onFocus={(e) => e.target.style.borderColor = "#6366f1"}
                    onBlur={(e) => e.target.style.borderColor = C.border}
                  />
                </div>
              </div>
            ) : (
              /* Read mode */
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(99,102,241,0.2)", background: "rgba(99,102,241,0.04)" }}>
                <div style={{ padding: "14px 18px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6366f1", opacity: 0.7, marginBottom: "6px" }}>
                    {lang === "fr" ? "Tables" : "Tables"}
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#6366f1", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {tableSettings.number_of_tables ?? "—"}
                  </div>
                </div>
                <div style={{ background: "rgba(99,102,241,0.12)" }} />
                <div style={{ padding: "14px 18px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#f59e0b", opacity: 0.7, marginBottom: "6px" }}>
                    {lang === "fr" ? "Places/table" : "Seats/table"}
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#f59e0b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {tableSettings.seats_per_table ?? "—"}
                  </div>
                </div>
                <div style={{ background: "rgba(251,191,36,0.12)" }} />
                <div style={{ padding: "14px 18px", textAlign: "center" }}>
                  <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#34d399", opacity: 0.7, marginBottom: "6px" }}>
                    {lang === "fr" ? "Total places" : "Total seats"}
                  </div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#34d399", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {tableSettings.number_of_tables && tableSettings.seats_per_table
                      ? (tableSettings.number_of_tables * tableSettings.seats_per_table).toLocaleString()
                      : "—"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!tableSettingsEdit ? (
            <div style={{
              border: `1px solid ${C.border}`,
              borderRadius: "14px",
              background: C.card,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.smallText, fontSize: "0.82rem" }}>
                <span style={{ fontWeight: 700, color: "#6366f1" }}>⏱ {lang === "fr" ? "À table" : "At table"}: {tableSeatTimer ?? "—"} min</span>
                <span style={{ opacity: 0.5 }}>|</span>
                <span style={{ fontWeight: 700, color: "#f59e0b" }}>🔄 {lang === "fr" ? "Rotation" : "Rotation"}: {tableRotationTimer ?? "—"} min</span>
                <span style={{ opacity: 0.5 }}>|</span>
                <span style={{ fontWeight: 700, color: "#34d399" }}>{lang === "fr" ? "Cycle" : "Cycle"}: {timersSet ? totalCycle : "—"} min</span>
              </div>
              <span style={{ fontSize: "0.76rem", color: C.smallText }}>
                {lang === "fr" ? "Appuyez sur Edit pour modifier les minuteries." : "Click Edit to adjust timers."}
              </span>
            </div>
          ) : (
            <>
              {/* Timer 1 — seat */}
              <SliderTimer
                label={lang === "fr" ? "Temps à table" : "Time at table"}
                desc={lang === "fr" ? "Durée de l'échange entre deux participants avant le signal." : "How long each pair spends talking at a table before the bell rings."}
                icon="⏱️"
                value={seatVal}
                setValue={setTableSeatTimer}
                min={1} max={30}
                presets={[3, 5, 7, 10, 15, 20]}
                accentColor="#6366f1"
                accentBg="rgba(99,102,241,0.1)"
                accentBorder="rgba(99,102,241,0.28)"
              />

              {/* Timer 2 — rotation */}
              <SliderTimer
                label={lang === "fr" ? "Rotation de tables" : "Table rotation"}
                desc={lang === "fr" ? "Temps de transition entre chaque changement de table." : "Time given between each table switch."}
                icon="🔄"
                value={rotVal}
                setValue={setTableRotationTimer}
                min={1} max={15}
                presets={[1, 2, 3, 5, 7, 10]}
                accentColor="#f59e0b"
                accentBg="rgba(251,191,36,0.1)"
                accentBorder="rgba(251,191,36,0.3)"
              />

              {/* Info note */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", fontSize: "0.78rem", color: C.smallText, lineHeight: 1.6, padding: "14px 16px", borderRadius: "10px", background: C.card, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: "1rem", flexShrink: 0, marginTop: "1px" }}>💡</span>
                <span>
                  {timersSet
                    ? (lang === "fr"
                      ? `Cycle complet : ${seatVal} min à table + ${rotVal} min de transition = ${totalCycle} min par tour.`
                      : `Full cycle: ${seatVal} min at table + ${rotVal} min rotation = ${totalCycle} min per round.`)
                    : (lang === "fr"
                      ? "Les minuteries ne sont pas encore configurées. Réglez les curseurs et sauvegardez."
                      : "Timers are not set yet. Adjust the sliders and save.")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════
          LIVE TABLE VIEW
      ══════════════════════════════════════ */}
      {(() => {
        const ld = liveTableData || {};
        const isDark = theme === "dark";
        const C = themeVars;
        const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
        const fmtTime = (iso) => {
          if (!iso) return "—";
          const d = new Date(iso);
          return isNaN(d.getTime()) ? "—" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        };
        // Обратный отсчёт для очереди: показываем "через сколько" в виде m:ss / Xh Ym.
        // seconds_until_active обновляется каждую секунду через poll refreshTablesState,
        // поэтому таймер "тикает" в реальном времени.
        const fmtCountdown = (sec) => {
          const s = Math.max(0, Math.floor(Number(sec) || 0));
          if (s <= 0) return lang === "fr" ? "bientôt" : "soon";
          if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
          const h = Math.floor(s / 3600);
          const m = Math.floor((s % 3600) / 60);
          return `${h}h ${String(m).padStart(2, "0")}m`;
        };

        const handleExtend = async (slotId, mins) => {
          const tk = localStorage.getItem("token");
          await fetch(`/api/events/${eventId}/tables/slots/${slotId}/extend?minutes=${mins}`, {
            method: "POST", headers: { Authorization: `Bearer ${tk}` },
          });
          await refreshTablesState();
        };
        const handleRelease = async (slotId) => {
          const tk = localStorage.getItem("token");
          await fetch(`/api/events/${eventId}/tables/slots/${slotId}/release`, {
            method: "DELETE", headers: { Authorization: `Bearer ${tk}` },
          });
          await refreshTablesState();
        };
        const handleRestart = async (matchId) => {
          // Restart → конец очереди
          const tk = localStorage.getItem("token");
          await fetch(`/api/events/${eventId}/tables/matches/${matchId}/restart`, {
            method: "POST", headers: { Authorization: `Bearer ${tk}` },
          });
          await refreshTablesState();
          showToast(lang === "fr" ? "↩ Match remis en fin de file" : "↩ Match moved to end of queue");
        };

        // ── Build per-table map: active + searching slots ──
        const byTable = {};
        for (let t = 1; t <= (ld.number_of_tables || 0); t++) byTable[t] = [];

        // active slots в сетку
        for (const slot of (ld.slots || [])) {
          if (!byTable[slot.table_number]) byTable[slot.table_number] = [];
          byTable[slot.table_number].push({ ...slot, _phase: "active" });
        }
        // searching slots — тоже в сетку стола
        for (const slot of (ld.searching || [])) {
          if (!byTable[slot.table_number]) byTable[slot.table_number] = [];
          byTable[slot.table_number].push({ ...slot, _phase: "searching" });
        }
        // queued slots — зарезервированы за конкретным столом, но не показываем в сетке
        // (они идут в отдельную секцию очереди ниже)

        // Helper: пилюля статуса фазы
        const PhasePill = ({ phase }) => {
          const cfg = {
            active:    { bg: "rgba(99,102,241,0.14)",  border: "rgba(99,102,241,0.35)",  fg: "#6366f1",  label: lang === "fr" ? "En table" : "At table" },
            searching: { bg: "rgba(245,158,11,0.14)",  border: "rgba(245,158,11,0.35)",  fg: "#f59e0b",  label: lang === "fr" ? "Recherche stol…" : "Finding table…" },
            queued:    { bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.3)",   fg: "#94a3b8",  label: lang === "fr" ? "File" : "Queued" },
          }[phase] || {};
          return (
            <span style={{
              padding: "1px 7px", borderRadius: 20, fontSize: "0.6rem", fontWeight: 800,
              textTransform: "uppercase", letterSpacing: ".06em",
              background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.fg,
              flexShrink: 0,
            }}>{cfg.label}</span>
          );
        };

        // Helper: карточка матча в сетке (active + searching)
        const SlotCard = ({ slot }) => {
          const isSearching = slot._phase === "searching";
          const urgent = !isSearching && slot.seconds_left < 60;
          const timerColor = isSearching
            ? (slot.seconds_left < 20 ? "#ef4444" : "#f59e0b")
            : (slot.seconds_left < 30 ? "#ef4444" : urgent ? "#f59e0b" : "#22c55e");
          const borderColor = isSearching
            ? "rgba(245,158,11,0.4)"
            : urgent ? "rgba(251,191,36,0.4)" : C.border;

          return (
            <div style={{
              background: isSearching
                ? (isDark ? "rgba(245,158,11,0.06)" : "rgba(245,158,11,0.04)")
                : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
              border: `1px solid ${borderColor}`,
              borderRadius: 10, padding: "10px 12px", marginBottom: 6,
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {/* Header row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                    <span style={{ fontSize: "0.68rem", color: C.smallText }}>
                      {lang === "fr" ? `Paire ${slot.seat_pair}` : `Pair ${slot.seat_pair}`}
                    </span>
                    <PhasePill phase={slot._phase} />
                  </div>
                  <div style={{ fontSize: "0.65rem", color: C.smallText, opacity: 0.65, marginTop: 1 }}>
                    Match #{slot.match_id}
                  </div>
                </div>
                <div style={{ fontSize: "1.2rem", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: timerColor, flexShrink: 0 }}>
                  {fmtSec(slot.seconds_left)}
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ height: 3, borderRadius: 2, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 2,
                  width: `${100 - (slot.pct_elapsed || 0)}%`,
                  background: timerColor,
                  transition: "width 1s linear",
                }} />
              </div>

              {/* Caption */}
              {isSearching && (
                <div style={{ fontSize: "0.65rem", color: "#f59e0b", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ display: "inline-block", animation: "spin 1.2s linear infinite" }}>⟳</span>
                  {lang === "fr" ? "Dirigez-les vers la table…" : "Directing participants to table…"}
                </div>
              )}

              {/* Users */}
              <div style={{ display: "flex", gap: 5 }}>
                {[{ name: slot.user1_name, i: 0 }, { name: slot.user2_name, i: 1 }].map(u => (
                  <div key={u.i}
                    onClick={() => navigator.clipboard?.writeText(u.name || "")}
                    title={u.name || "—"}
                    style={{ flex: 1, minWidth: 0, background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: 7, padding: "4px 8px", display: "flex", alignItems: "center", gap: 5, cursor: "pointer", overflow: "hidden" }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, background: u.i === 0 ? "rgba(99,102,241,0.2)" : "rgba(251,191,36,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: u.i === 0 ? "#6366f1" : "#f59e0b" }}>
                      {(u.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontSize: "0.7rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{u.name || "—"}</span>
                  </div>
                ))}
              </div>

              {/* Actions — только для active */}
              {!isSearching && (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: "0.62rem", color: C.smallText, textTransform: "uppercase", letterSpacing: ".05em" }}>+time</span>
                  {[1, 2, 5].map(m => (
                    <button key={m} onClick={() => handleExtend(slot.slot_id, m)}
                      style={{ padding: "2px 7px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700, border: `1px solid ${C.border}`, background: "transparent", color: C.smallText, cursor: "pointer" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#6366f1"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.smallText; }}
                    >+{m}m</button>
                  ))}
                  <button onClick={() => handleRelease(slot.slot_id)}
                    style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: C.smallText, fontSize: "0.8rem", padding: "2px 5px", borderRadius: 5 }}
                    onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                    onMouseLeave={e => e.currentTarget.style.color = C.smallText}
                    title="Release">✕</button>
                </div>
              )}
            </div>
          );
        };

        return (
          <div style={{
            background: themeVars.subtle, borderRadius: "24px",
            border: `1px solid ${C.border}`, overflow: "hidden",
            boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.28)" : "0 4px 24px rgba(0,0,0,0.07)",
          }}>
            {/* Header */}
            <div style={{ padding: "20px 28px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} />
                <span style={{ fontWeight: 800, fontSize: "1rem", color: C.fg }}>
                  {lang === "fr" ? "Tables — Vue en direct" : "Tables — Live View"}
                </span>
                <span style={{ fontSize: "0.78rem", color: C.smallText }}>
                  {lang === "fr" ? "mis à jour chaque seconde" : "updates every second"}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  onClick={() => {
                    const tk = localStorage.getItem("token");
                    const apiBase = window.location.origin;
                    const html = buildTableLiveHTML(eventId, tk, apiBase, lang);
                    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const win = window.open(url, "_blank", "width=1360,height=900,resizable=yes,scrollbars=yes");
                    if (win) win.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
                    else URL.revokeObjectURL(url);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 16px", borderRadius: "10px", fontSize: "0.85rem", fontWeight: 700,
                    border: "none", background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff", cursor: "pointer", boxShadow: "0 2px 12px rgba(99,102,241,0.35)", transition: "opacity 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                >
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  {lang === "fr" ? "Ouvrir en direct" : "Open Live View"}
                </button>
                <button
                  onClick={() => {
                    const tk = localStorage.getItem("token");
                    fetch(`/api/events/${eventId}/tables/queue`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
                      .then(() => refreshTablesState());
                  }}
                  style={{
                    padding: "6px 14px", borderRadius: "8px", fontSize: "0.82rem", fontWeight: 600,
                    border: `1px solid ${C.border}`, background: "transparent", color: C.smallText, cursor: "pointer",
                  }}>
                  {lang === "fr" ? "Vider les slots" : "Clear All Slots"}
                </button>
              </div>
            </div>

            <div style={{ padding: "20px 28px", display: "flex", flexDirection: "column", gap: 20 }}>

              {/* ── Stats row (5 counters) ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr 1px 1fr 1px 1fr", borderRadius: "12px", overflow: "hidden", border: `1px solid ${C.border}`, background: isDark ? C.card : "#f8f9ff" }}>
                {[
                  { label: lang === "fr" ? "À table" : "At table",        value: ld.occupied,           color: "#6366f1" },
                  { label: lang === "fr" ? "Cherchent" : "Finding table",  value: ld.searching_count,    color: "#f59e0b" },
                  { label: lang === "fr" ? "En file" : "In queue",         value: ld.queued_count,       color: "#94a3b8" },
                  { label: lang === "fr" ? "Terminés ✓" : "Done ✓",       value: ld.total_seated,       color: "#22c55e" },
                  { label: lang === "fr" ? "Sans place" : "Unseated",      value: ld.unseated_count,     color: C.smallText },
                ].map((s, i) => (
                  <React.Fragment key={i}>
                    <div style={{ padding: "12px 6px", textAlign: "center" }}>
                      <div style={{ fontSize: "1.5rem", fontWeight: 800, color: s.color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{s.value ?? 0}</div>
                      <div style={{ fontSize: "0.58rem", fontWeight: 700, color: C.smallText, textTransform: "uppercase", letterSpacing: ".07em", marginTop: 3 }}>{s.label}</div>
                    </div>
                    {i < 4 && <div style={{ background: C.border }} />}
                  </React.Fragment>
                ))}
              </div>

              {/* ── Round complete banner ── */}
              {ld.round_complete && (
                <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 12, padding: "14px 18px" }}>
                  <div style={{ fontWeight: 800, color: "#22c55e", marginBottom: 3 }}>✅ {lang === "fr" ? "Tous les matchs ont été servis !" : "All matches completed!"}</div>
                  <div style={{ fontSize: "0.8rem", color: C.smallText }}>
                    {lang === "fr"
                      ? `${ld.total_seated} matchs terminés. Utilisez ↩ Relancer pour les remettre en fin de file.`
                      : `${ld.total_seated} matches done. Use ↩ Restart to send them to end of queue.`}
                  </div>
                </div>
              )}

              {/* ── Tables grid (active + searching slots) ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
                {Object.entries(byTable).map(([tNum, slots]) => {
                  // Проверяем, есть ли queued-слоты для этого стола (зарезервированы, но ещё не там)
                  const queuedForThisTable = (ld.queue || []).filter(q => q.table_number === Number(tNum));
                  const isEmpty = slots.length === 0;
                  return (
                    <div key={tNum} style={{
                      background: isEmpty ? (isDark ? C.subtle : "#f4f5ff") : C.card,
                      border: `1px solid ${queuedForThisTable.length > 0 ? "rgba(148,163,184,0.45)" : C.border}`,
                      borderRadius: 12, padding: "10px 12px",
                      opacity: isEmpty && queuedForThisTable.length === 0 ? 0.5 : 1,
                    }}>
                      {/* Table header */}
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, color: C.smallText, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                        🪑 {lang === "fr" ? `Table ${tNum}` : `Table ${tNum}`}
                        {isEmpty && queuedForThisTable.length === 0 && (
                          <span style={{ color: "#22c55e", fontWeight: 600 }}>· {lang === "fr" ? "Libre" : "Free"}</span>
                        )}
                        {isEmpty && queuedForThisTable.length > 0 && (
                          <span style={{ color: "#94a3b8", fontWeight: 600 }}>· {lang === "fr" ? "Réservée" : "Reserved"}</span>
                        )}
                      </div>

                      {/* Active / searching cards */}
                      {isEmpty
                        ? <div style={{ fontSize: "0.75rem", color: C.smallText, textAlign: "center", padding: "6px 0 4px" }}>
                            {lang === "fr" ? "En attente d'un match" : "Waiting for match"}
                          </div>
                        : slots.map(slot => <SlotCard key={slot.slot_id} slot={slot} />)
                      }

                      {/* Queued-preview: кто следующий за этим столом */}
                      {queuedForThisTable.length > 0 && (
                        <div style={{ marginTop: 6, padding: "6px 8px", borderRadius: 8, background: isDark ? "rgba(148,163,184,0.07)" : "rgba(148,163,184,0.08)", border: `1px dashed ${C.border}` }}>
                          <div style={{ fontSize: "0.6rem", fontWeight: 700, color: C.smallText, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>
                            {lang === "fr" ? `⏳ ${queuedForThisTable.length} en file` : `⏳ ${queuedForThisTable.length} queued`}
                          </div>
                          {queuedForThisTable.slice(0, 2).map((q, qi) => (
                            <div key={q.match_id} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: qi < queuedForThisTable.slice(0,2).length - 1 ? 3 : 0 }}>
                              <div style={{ width: 16, height: 16, borderRadius: "50%", background: "rgba(148,163,184,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.58rem", fontWeight: 800, color: C.smallText, flexShrink: 0 }}>{q.position}</div>
                              <span style={{ fontSize: "0.68rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{q.user1_name} × {q.user2_name}</span>
                              {q.seconds_until_active != null ? (
                                <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "#f59e0b", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{fmtCountdown(q.seconds_until_active)}</span>
                              ) : q.estimated_active_at ? (
                                <span style={{ fontSize: "0.6rem", color: C.smallText, flexShrink: 0 }}>~{fmtTime(q.estimated_active_at)}</span>
                              ) : null}
                            </div>
                          ))}
                          {queuedForThisTable.length > 2 && (
                            <div style={{ fontSize: "0.6rem", color: C.smallText, marginTop: 2, paddingLeft: 21 }}>
                              +{queuedForThisTable.length - 2} {lang === "fr" ? "autres" : "more"}…
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ── Queue section ── */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, background: isDark ? C.subtle : "#f8f9ff" }}>
                  <span>⏳</span>
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", color: C.fg }}>{lang === "fr" ? "File d'attente" : "Queue"}</span>
                  <div style={{ marginLeft: "auto", padding: "2px 9px", borderRadius: 20, background: "rgba(148,163,184,0.12)", color: "#94a3b8", border: "1px solid rgba(148,163,184,0.3)", fontSize: "0.72rem", fontWeight: 700 }}>
                    {(ld.queue || []).length} {lang === "fr" ? "en attente" : "waiting"}
                  </div>
                </div>
                {(!ld.queue || ld.queue.length === 0) ? (
                  <div style={{ padding: "14px 16px", fontSize: "0.8rem", color: C.smallText, textAlign: "center" }}>
                    {lang === "fr" ? "La file est vide" : "Queue is empty"}
                  </div>
                ) : (
                  <>
                    {/* Column headers */}
                    <div style={{ display: "grid", gridTemplateColumns: "28px 60px 1fr 90px 70px", gap: 8, padding: "6px 16px 4px", borderBottom: `1px solid ${C.border}` }}>
                      {["#", "Match", lang === "fr" ? "Participants" : "Participants", lang === "fr" ? "Table réservée" : "Reserved table", lang === "fr" ? "Dans" : "In"].map((h, i) => (
                        <div key={i} style={{ fontSize: "0.58rem", fontWeight: 700, color: C.smallText, textTransform: "uppercase", letterSpacing: ".06em", textAlign: i >= 3 ? "right" : "left" }}>{h}</div>
                      ))}
                    </div>
                    {ld.queue.map((m, idx) => (
                      <div key={m.match_id} style={{
                        display: "grid", gridTemplateColumns: "28px 60px 1fr 90px 70px", gap: 8,
                        padding: "9px 16px",
                        borderBottom: idx < ld.queue.length - 1 ? `1px solid ${C.border}` : "none",
                        background: idx % 2 === 0 ? "transparent" : (isDark ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.015)"),
                        alignItems: "center",
                      }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: idx === 0 ? "rgba(245,158,11,0.15)" : "rgba(148,163,184,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.65rem", fontWeight: 800, color: idx === 0 ? "#f59e0b" : C.smallText }}>{m.position}</div>
                        <div style={{ fontSize: "0.7rem", color: C.smallText }}>#{m.match_id}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, overflow: "hidden" }}>
                          <span onClick={() => navigator.clipboard?.writeText(m.user1_name || "")} title={m.user1_name} style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", maxWidth: "45%" }}>{m.user1_name}</span>
                          <span style={{ color: C.smallText, fontSize: "0.68rem", flexShrink: 0 }}>×</span>
                          <span onClick={() => navigator.clipboard?.writeText(m.user2_name || "")} title={m.user2_name} style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", maxWidth: "45%" }}>{m.user2_name}</span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#6366f1" }}>🪑 T{m.table_number}·P{m.seat_pair}</span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {m.seconds_until_active != null ? (
                            <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#f59e0b", fontVariantNumeric: "tabular-nums" }}>{fmtCountdown(m.seconds_until_active)}</span>
                          ) : m.estimated_active_at ? (
                            <span style={{ fontSize: "0.72rem", fontWeight: 600, color: C.fg }}>~{fmtTime(m.estimated_active_at)}</span>
                          ) : (
                            <span style={{ fontSize: "0.7rem", color: C.smallText }}>—</span>
                          )}
                          {idx === 0 && (
                            <div style={{ marginTop: 2, padding: "1px 6px", borderRadius: 4, background: "rgba(245,158,11,0.12)", color: "#f59e0b", fontSize: "0.58rem", fontWeight: 800, textTransform: "uppercase", display: "inline-block" }}>Next</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* ── Searching section ── */}
              <div style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: "9px 16px", borderBottom: "1px solid rgba(245,158,11,0.2)", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "1rem", animation: "spin 1.2s linear infinite", display: "inline-block" }}>⟳</span>
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#f59e0b" }}>
                    {lang === "fr" ? "En route vers leur table" : "Finding their table"}
                  </span>
                  <div style={{ marginLeft: "auto", padding: "2px 9px", borderRadius: 20, background: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)", fontSize: "0.72rem", fontWeight: 700 }}>
                    {(ld.searching || []).length}
                  </div>
                </div>
                {(!ld.searching || ld.searching.length === 0) ? (
                  <div style={{ padding: "14px 16px", fontSize: "0.8rem", color: "#f59e0b", textAlign: "center", opacity: 0.6 }}>
                    {lang === "fr" ? "Aucun en déplacement" : "None currently moving"}
                  </div>
                ) : ld.searching.map((s, idx) => (
                  <div key={s.slot_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", borderBottom: idx < ld.searching.length - 1 ? "1px solid rgba(245,158,11,0.12)" : "none" }}>
                    <div style={{ fontWeight: 800, fontSize: "1rem", color: "#f59e0b", minWidth: 40, fontVariantNumeric: "tabular-nums" }}>{fmtSec(s.seconds_left)}</div>
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6366f1" }}>🪑 T{s.table_number}·P{s.seat_pair}</div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
                      <span style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>{s.user1_name}</span>
                      <span style={{ color: C.smallText, fontSize: "0.7rem", flexShrink: 0 }}>×</span>
                      <span style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>{s.user2_name}</span>
                    </div>
                    <div style={{ height: 3, width: 48, borderRadius: 2, background: "rgba(245,158,11,0.15)", overflow: "hidden", flexShrink: 0 }}>
                      <div style={{ height: "100%", borderRadius: 2, width: `${100 - (s.pct_elapsed || 0)}%`, background: "#f59e0b", transition: "width 1s linear" }} />
                    </div>
                    <button onClick={() => handleRelease(s.slot_id)}
                      title={lang === "fr" ? "Marquer comme terminé" : "Mark as completed"}
                      style={{ background: "none", border: "none", cursor: "pointer", color: C.smallText, fontSize: "0.8rem", padding: "2px 5px", borderRadius: 5, flexShrink: 0 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                      onMouseLeave={e => e.currentTarget.style.color = C.smallText}
                    >✕</button>
                  </div>
                ))}
              </div>

              {/* ── Seated / Done ── */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, background: isDark ? C.subtle : "#f8f9ff" }}>
                  <span>✅</span>
                  <span style={{ fontWeight: 700, fontSize: "0.88rem", color: C.fg }}>
                    {lang === "fr" ? "Matchs passés à table" : "Matches that completed their session"}
                  </span>
                  <div style={{ marginLeft: "auto", padding: "2px 9px", borderRadius: 20, background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)", fontSize: "0.72rem", fontWeight: 700 }}>
                    {(ld.seated || []).length} {lang === "fr" ? "terminés" : "done"}
                  </div>
                </div>
                {(!ld.seated || ld.seated.length === 0) ? (
                  <div style={{ padding: "14px 16px", fontSize: "0.8rem", color: C.smallText, textAlign: "center" }}>
                    {lang === "fr" ? "Aucun match terminé pour l'instant" : "No completed matches yet"}
                  </div>
                ) : ld.seated.map((m, idx) => (
                  <div key={m.match_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 16px", borderBottom: idx < ld.seated.length - 1 ? `1px solid ${C.border}` : "none", opacity: 0.8 }}>
                    <span style={{ color: "#22c55e", fontSize: "0.8rem", flexShrink: 0 }}>✓</span>
                    <div style={{ fontSize: "0.72rem", color: C.smallText, minWidth: 52 }}>Match #{m.match_id}</div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
                      <span onClick={() => navigator.clipboard?.writeText(m.user1_name || "")} title={m.user1_name} style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", maxWidth: "45%" }}>{m.user1_name}</span>
                      <span style={{ color: C.smallText, fontSize: "0.7rem", flexShrink: 0 }}>×</span>
                      <span onClick={() => navigator.clipboard?.writeText(m.user2_name || "")} title={m.user2_name} style={{ fontSize: "0.8rem", color: C.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", maxWidth: "45%" }}>{m.user2_name}</span>
                    </div>
                    <button onClick={() => handleRestart(m.match_id)}
                      title={lang === "fr" ? "Remettre en fin de file" : "Send to end of queue"}
                      style={{ padding: "3px 9px", borderRadius: 7, fontSize: "0.72rem", fontWeight: 700, border: `1px solid ${C.border}`, background: "transparent", color: C.smallText, cursor: "pointer", flexShrink: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#6366f1"; e.currentTarget.style.background = "rgba(99,102,241,0.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.smallText; e.currentTarget.style.background = "transparent"; }}
                    >↩ {lang === "fr" ? "Relancer (fin)" : "Restart (end)"}</button>
                  </div>
                ))}
              </div>

            </div>

            {/* CSS spin keyframe для иконок "ищем стол" */}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        );
      })()}
      </>
    );
  };


  // ===== CO-ADMINS SECTION (tab) =====
  const renderAdminsSection = () => (
    <div style={{
      background: themeVars.subtle, borderRadius: "24px",
      border: `1px solid ${themeVars.border}`, overflow: "hidden",
      boxShadow: theme === "dark" ? "0 4px 24px rgba(0,0,0,0.28)" : "0 4px 24px rgba(0,0,0,0.07)",
    }}>
      {/* Header */}
      <div style={{
        padding: isMobile ? "16px 16px 12px" : "24px 28px 20px",
        borderBottom: `1px solid ${themeVars.border}`,
        background: themeVars.card,
      }}>
        <h2 style={{ margin: "0 0 4px", fontSize: isMobile ? "1.1rem" : "1.25rem", fontWeight: 800, color: themeVars.fg, display: "flex", alignItems: "center", gap: "8px" }}>
          🛡️ {lang === "fr" ? "Co-Administrateurs" : "Co-Admins"}
        </h2>
        <p style={{ margin: 0, fontSize: "0.85rem", color: themeVars.smallText }}>
          {lang === "fr" ? "Gérer les administrateurs de cet événement" : "Manage event administrators"}
        </p>
      </div>

      <div style={{ padding: isMobile ? "16px" : "28px", display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* Admins list */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 700, color: themeVars.smallText, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {lang === "fr" ? "Admins actuels" : "Current admins"} {coAdmins.length > 0 && `(${coAdmins.length})`}
          </label>
          {coAdminsLoading ? (
            <div style={{ color: themeVars.smallText, fontSize: "0.9rem", padding: "16px 0", textAlign: "center" }}>
              ⏳ {lang === "fr" ? "Chargement..." : "Loading..."}
            </div>
          ) : coAdmins.length === 0 ? (
            <div style={{
              padding: "24px", borderRadius: "14px", textAlign: "center",
              background: `${themeVars.primary}08`, border: `1px dashed ${themeVars.border}`,
              color: themeVars.smallText, fontSize: "0.9rem",
            }}>
              {lang === "fr" ? "Aucun co-admin pour l'instant" : "No co-admins yet"}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap: "10px" }}>
              {coAdmins.map(admin => (
                <div key={admin.user_id} style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "14px 16px", borderRadius: "14px",
                  background: themeVars.card,
                  border: `1px solid ${themeVars.border}`,
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = themeVars.primary + '50'}
                onMouseLeave={e => e.currentTarget.style.borderColor = themeVars.border}
                >
                  <div style={{
                    width: "42px", height: "42px", borderRadius: "50%", flexShrink: 0,
                    background: `linear-gradient(135deg, ${themeVars.accent}30, ${themeVars.primary}20)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: "1rem", color: themeVars.accent,
                    border: `1px solid ${themeVars.accent}30`,
                  }}>
                    {(admin.name || admin.username || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem", color: themeVars.fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {admin.name || admin.username || `User #${admin.user_id}`}
                    </div>
                    {admin.username && (
                      <div style={{ fontSize: "0.78rem", color: themeVars.smallText }}>@{admin.username}</div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveAdmin(admin.user_id)}
                    title={lang === "fr" ? "Retirer admin" : "Remove admin"}
                    style={{
                      padding: "7px 14px", borderRadius: "10px", flexShrink: 0,
                      background: themeVars.dangerLight, color: themeVars.danger,
                      border: `1px solid ${themeVars.danger}30`,
                      cursor: "pointer", fontSize: "0.82rem", fontWeight: 700, transition: "all 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = `${themeVars.danger}28`}
                    onMouseLeave={e => e.currentTarget.style.background = themeVars.dangerLight}
                  >
                    {lang === "fr" ? "Retirer" : "Remove"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: "1px", background: themeVars.border }} />

        {/* Add admin */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxWidth: "480px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 700, color: themeVars.smallText, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {lang === "fr" ? "Ajouter un co-admin" : "Add co-admin"}
          </label>
          {participants.length === 0 ? (
            <div style={{ fontSize: "0.88rem", color: themeVars.smallText, fontStyle: "italic", padding: "8px 0" }}>
              {lang === "fr" ? "Aucun participant disponible" : "No participants available"}
            </div>
          ) : (
            <>
              <select
                value={addAdminInput}
                onChange={e => setAddAdminInput(e.target.value)}
                style={{
                  padding: "12px 14px", borderRadius: "12px",
                  background: themeVars.card, border: `1px solid ${themeVars.border}`,
                  color: addAdminInput ? themeVars.fg : themeVars.smallText,
                  fontSize: "0.95rem", outline: "none", cursor: "pointer",
                  transition: "border-color 0.2s", width: "100%",
                }}
                onFocus={e => e.target.style.borderColor = themeVars.primary}
                onBlur={e => e.target.style.borderColor = themeVars.border}
              >
                <option value="">{lang === "fr" ? "Choisir un participant..." : "Select a participant..."}</option>
                {participants
                  .filter(p => !p.is_owner && !coAdmins.some(a => a.user_id === p.user_id))
                  .map(p => (
                    <option key={p.user_id} value={p.user_id}>
                      {p.name}{p.username ? ` (@${p.username})` : ''} #{p.user_id}
                    </option>
                  ))
                }
              </select>
              <button
                onClick={handleAddAdmin}
                disabled={addAdminLoading || !addAdminInput}
                style={{
                  padding: "12px 20px", borderRadius: "12px",
                  background: addAdminLoading || !addAdminInput
                    ? `${themeVars.primary}40`
                    : `linear-gradient(135deg, ${themeVars.primary}, ${themeVars.accent})`,
                  color: "#fff", border: "none", fontSize: "0.95rem", fontWeight: 700,
                  cursor: addAdminLoading || !addAdminInput ? "not-allowed" : "pointer",
                  transition: "opacity 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                }}
                onMouseEnter={e => { if (!addAdminLoading && addAdminInput) e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                {addAdminLoading ? "⏳" : "+"} {lang === "fr" ? "Ajouter comme admin" : "Add as co-admin"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ===== PARTICIPANTS SECTION =====
  const renderParticipantsSection = () => (
    <div style={{
      background: themeVars.subtle, borderRadius: "24px",
      border: `1px solid ${themeVars.border}`, overflow: "hidden",
      boxShadow: theme === "dark"
        ? "0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(176,96,144,0.12)"
        : "0 10px 30px rgba(0,0,0,0.08), 0 0 0 1px rgba(56,25,49,0.08)",
    }}>
      {/* Section Header */}
      <div className="ead-section-header" style={{
        padding: isMobile ? "14px 16px" : "28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: isMobile ? "10px" : "16px",
      }}>
        <div>
          <h2 style={{ fontSize: isMobile ? "1.15rem" : "1.8rem", fontWeight: 700, margin: "0 0 4px", color: themeVars.fg, display: "flex", alignItems: "center", gap: "10px" }}>
            {lang === "fr" ? "Liste des participants" : "Participants List"}
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "5px",
              fontSize: "0.7rem", fontWeight: 600, color: themeVars.success,
              background: `${themeVars.success}12`, border: `1px solid ${themeVars.success}30`,
              borderRadius: "20px", padding: "3px 9px",
            }}>
              <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: themeVars.success, animation: "pulse 2s infinite", display: "inline-block" }} />
              {lang === "fr" ? "En direct" : "Live"}
            </span>
          </h2>
          {!isMobile && (
            <p style={{ fontSize: "1.05rem", color: themeVars.smallText, margin: 0 }}>
              {lang === "fr"
                ? "Cliquez sur Détails pour voir les informations du participant"
                : "Click on Details to view participant information"}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: isMobile ? "8px" : "14px", flexWrap: "wrap" }}>
          <IconButton
            onClick={exportParticipantsToCSV}
            icon="📥"
            label={isMobile ? "" : (lang === "fr" ? "Exporter CSV" : "Export CSV")}
            color={themeVars.success}
          />
          <IconButton
            onClick={() => {
              const token = localStorage.getItem("token");
              if (!token || !eventId) return;
              setLoading(true);
              fetch(`/api/events/${eventId}/participants`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.json())
                .then(data => {
                  setParticipants(data.participants || []);
                  setEventTitle(data.event_title || "");
                  setEventCapacity(data.capacity || null);
                  setEstimatedCost(data.estimated_cost || null);
                })
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
            }}
            icon="🔄"
            label={isMobile ? "" : (lang === "fr" ? "Actualiser" : "Refresh")}
            color={themeVars.primary}
          />
        </div>
      </div>

      {/* Filters — mobile toggle header */}
      {isMobile && (
        <div style={{
          padding: "12px 16px", borderBottom: mobileParticipantFiltersOpen ? "none" : `1px solid ${themeVars.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: themeVars.card,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              onClick={() => setMobileParticipantFiltersOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "7px 14px", borderRadius: "10px", border: `1px solid ${themeVars.border}`,
                background: mobileParticipantFiltersOpen ? `${themeVars.primary}15` : themeVars.subtle,
                color: mobileParticipantFiltersOpen ? themeVars.primary : themeVars.fg,
                fontSize: "0.88rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              🎛️ {lang === "fr" ? "Filtres" : "Filters"}
              <span style={{ fontSize: "0.7rem", transition: "transform 0.2s", transform: mobileParticipantFiltersOpen ? "rotate(180deg)" : "none" }}>▼</span>
            </button>
            {(filters.status !== 'all' || filters.profileMin > 0 || filters.nameSearch || filters.idSearch || filters.emailSearch || filters.phoneSearch || filters.dateFrom || filters.dateTo) && (
              <span style={{
                padding: "3px 8px", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 700,
                background: themeVars.primary, color: "#fff",
              }}>●</span>
            )}
          </div>
          <div style={{ fontSize: "0.85rem", color: themeVars.smallText, fontWeight: 600 }}>
            {filteredParticipants.length}/{participants.length}
          </div>
        </div>
      )}

      {/* Filters */}
      {(!isMobile || mobileParticipantFiltersOpen) && (
      <div className="ead-filter-bar" style={{
        padding: isMobile ? "12px 16px" : "20px 28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", flexWrap: "wrap", gap: isMobile ? "10px" : "14px", alignItems: "flex-end",
        background: isMobile ? themeVars.card : "transparent",
      }}>
        {/* Status */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Statut" : "Status"}
          </span>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "160px",
            }}
          >
            <option value="all">{lang === "fr" ? "Tous" : "All"}</option>
            <option value="registered">{lang === "fr" ? "Inscrit" : "Registered"}</option>
            <option value="pending">{lang === "fr" ? "En attente" : "Pending"}</option>
            <option value="confirmed">{lang === "fr" ? "Confirmé" : "Confirmed"}</option>
            <option value="cancelled">{lang === "fr" ? "Annulé" : "Cancelled"}</option>
          </select>
        </div>
        {/* Profile completeness */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Profil" : "Profile"}
          </span>
          <select
            value={filters.profileMin}
            onChange={(e) => setFilters({ ...filters, profileMin: parseInt(e.target.value) })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "160px",
            }}
          >
            <option value={0}>{lang === "fr" ? "Tous les profils" : "All Profiles"}</option>
            <option value={50}>{lang === "fr" ? "50%+ complété" : "50%+ Complete"}</option>
            <option value={100}>{lang === "fr" ? "100% complété" : "100% Complete"}</option>
          </select>
        </div>
        <FilterInput
          value={filters.nameSearch}
          onChange={(v) => setFilters({ ...filters, nameSearch: v })}
          label={lang === "fr" ? "Nom" : "Name"}
          placeholder={lang === "fr" ? "🔍 Prénom Nom..." : "🔍 John Doe..."}
          width={isMobile ? "100%" : "180px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={filters.idSearch}
          onChange={(v) => setFilters({ ...filters, idSearch: v })}
          label={lang === "fr" ? "ID utilisateur" : "User ID"}
          placeholder="🔍 123..."
          width={isMobile ? "100%" : "120px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={filters.emailSearch}
          onChange={(v) => setFilters({ ...filters, emailSearch: v })}
          label="Email"
          placeholder={lang === "fr" ? "🔍 user@mail.com..." : "🔍 user@mail.com..."}
          width={isMobile ? "100%" : "200px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={filters.phoneSearch}
          onChange={(v) => setFilters({ ...filters, phoneSearch: v })}
          label={lang === "fr" ? "Téléphone" : "Phone"}
          placeholder="🔍 +1234..."
          width={isMobile ? "100%" : "160px"}
          themeVars={themeVars}
        />
        {/* Date range group */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Inscrit du" : "Registered from"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <FilterInput
              type="date"
              value={filters.dateFrom}
              onChange={(v) => setFilters({ ...filters, dateFrom: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
            <span style={{ color: themeVars.smallText, fontSize: "0.85rem", fontWeight: 500 }}>→</span>
            <FilterInput
              type="date"
              value={filters.dateTo}
              onChange={(v) => setFilters({ ...filters, dateTo: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
          </div>
        </div>
        <button
          onClick={() => setFilters({ status: 'all', profileMin: 0, nameSearch: '', idSearch: '', emailSearch: '', phoneSearch: '', dateFrom: '', dateTo: '' })}
          style={{
            padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
            background: `${themeVars.warning}0c`, color: themeVars.warning,
            border: `1px solid ${themeVars.warning}30`, fontSize: isMobile ? "0.85rem" : "0.9rem",
            fontWeight: 500, cursor: "pointer", marginLeft: isMobile ? "0" : "auto",
            transition: "all 0.2s ease", alignSelf: "flex-end",
          }}
          onMouseEnter={(e) => e.target.style.background = `${themeVars.warning}15`}
          onMouseLeave={(e) => e.target.style.background = `${themeVars.warning}0c`}
        >
          {lang === "fr" ? "✕ Réinitialiser" : "✕ Reset"}
        </button>
        {!isMobile && (
          <div style={{ marginLeft: "12px", fontSize: "0.9rem", color: themeVars.smallText, alignSelf: "flex-end", paddingBottom: "2px" }}>
            {filteredParticipants.length} {lang === "fr" ? "sur" : "of"} {participants.length}
          </div>
        )}
      </div>
      )}

      {/* Mobile Card List */}
      {isMobile ? (
        <div style={{ padding: "12px" }}>
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              {lang === "fr" ? "Chargement..." : "Loading..."}
            </div>
          ) : error ? (
            <div style={{ padding: "20px", textAlign: "center", color: themeVars.danger }}>{error}</div>
          ) : filteredParticipants.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>📭</div>
              <p style={{ fontWeight: 500 }}>{lang === "fr" ? "Aucun participant" : "No participants yet"}</p>
            </div>
          ) : (
            filteredParticipants.map((p) => {
              const participantRequest = searchRequests.find(sr =>
                (sr.user_id === p.user_id) ||
                (sr.id === p.user_id) ||
                (sr.user_email && sr.user_email.toLowerCase() === (p.email || '').toLowerCase())
              );
              const participantMatches = matches.filter(m => m.user1_id === p.user_id || m.user2_id === p.user_id);
              const profileFill = getProfileFill(p);
              return (
                <div
                  key={p.user_id}
                  style={{
                    background: themeVars.card, borderRadius: "16px",
                    border: `1px solid ${themeVars.border}`,
                    marginBottom: "10px", overflow: "hidden",
                    boxShadow: theme === "dark" ? "0 2px 12px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.06)",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  }}
                  onTouchStart={(e) => { e.currentTarget.style.transform = "scale(0.99)"; }}
                  onTouchEnd={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                >
                  {/* Card Header */}
                  <div style={{ padding: "14px 14px 10px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{
                      width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0,
                      background: themeVars.primaryLight, display: "flex", alignItems: "center",
                      justifyContent: "center", fontWeight: 700, fontSize: "1.2rem", color: themeVars.primary,
                      border: `2px solid ${themeVars.primary}25`,
                    }}>
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt={p.name} style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                          onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = `<span style="font-size:1.2rem;font-weight:700">${p.name.charAt(0).toUpperCase()}</span>`; }} />
                      ) : (
                        <span>{p.name.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "1rem", color: themeVars.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.name}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: themeVars.smallText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.email || (lang === "fr" ? "Aucun email" : "No email")}
                      </div>
                    </div>
                    <StatusBadge status={p.status} type="participant" />
                  </div>

                  {/* Card Body */}
                  <div style={{ padding: "0 14px 12px", display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                    {/* Profile fill */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{ width: "50px", height: "5px", borderRadius: "3px", background: themeVars.border, overflow: "hidden" }}>
                        <div style={{
                          width: `${profileFill}%`, height: "100%", borderRadius: "3px",
                          background: profileFill === 100 ? themeVars.success : profileFill === 50 ? themeVars.warning : themeVars.primary,
                        }} />
                      </div>
                      <span style={{ fontSize: "0.78rem", fontWeight: 600, color: profileFill === 100 ? themeVars.success : profileFill === 50 ? themeVars.warning : themeVars.smallText }}>
                        {profileFill}%
                      </span>
                    </div>

                    {/* Matches count */}
                    {participantMatches.length > 0 && (
                      <span style={{
                        padding: "3px 10px", borderRadius: "16px", fontSize: "0.78rem", fontWeight: 600,
                        background: themeVars.successLight, color: themeVars.success, border: `1px solid ${themeVars.success}30`,
                      }}>
                        🤝 {participantMatches.length}
                      </span>
                    )}

                    {/* Request status */}
                    {participantRequest && (() => {
                      const statusInfo = getRequestStatus(participantRequest, matches);
                      return <StatusBadge status={statusInfo.type} type="request" details={statusInfo.details} />;
                    })()}

                    {/* Phone */}
                    {p.phone_number && (
                      <span style={{ fontSize: "0.78rem", color: themeVars.smallText }}>📱 {p.phone_number}</span>
                    )}
                  </div>

                  {/* Card Footer — actions */}
                  <div style={{
                    padding: "10px 14px", borderTop: `1px solid ${themeVars.border}`,
                    display: "flex", gap: "8px",
                  }}>
                    <button
                      onClick={() => openDrawer(p)}
                      style={{
                        flex: 1, padding: "9px 0", borderRadius: "10px",
                        background: `${themeVars.accent}15`, color: themeVars.accent,
                        border: `1px solid ${themeVars.accent}30`, fontSize: "0.88rem",
                        fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      👁️ {lang === "fr" ? "Détails" : "Details"}
                    </button>
                    {currentUserId && p.user_id !== currentUserId && (
                      <button
                        onClick={() => handleKickUser(p.user_id, p.name)}
                        style={{
                          padding: "9px 14px", borderRadius: "10px",
                          background: themeVars.dangerLight, color: themeVars.danger,
                          border: `1px solid ${themeVars.danger}30`, fontSize: "0.88rem",
                          fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        🚫
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (

      /* Desktop Table */
      <>
      <div className="ead-table-wrap">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
          <thead>
            <tr>
              <SortableHeader table="participants" columnKey="name">{lang === "fr" ? "Participant" : "Participant"}</SortableHeader>
              <SortableHeader table="participants" columnKey="phone">{lang === "fr" ? "Téléphone" : "Phone"}</SortableHeader>
              <SortableHeader table="participants" columnKey="status">{lang === "fr" ? "Statut requête" : "Request Status"}</SortableHeader>
              <SortableHeader table="participants" columnKey="profile">{lang === "fr" ? "Profil" : "Profile"}</SortableHeader>
              <SortableHeader table="participants" columnKey="matches_count">{lang === "fr" ? "Matches" : "Matches"}</SortableHeader>
              <th style={tableCellStyle()}>{lang === "fr" ? "Actions" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                {lang === "fr" ? "Chargement des participants..." : "Loading participants..."}
              </td></tr>
            ) : error ? (
              <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: themeVars.danger }}>{error}</td></tr>
            ) : filteredParticipants.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                <div style={{ fontSize: "3rem", marginBottom: "12px" }}>📭</div>
                <p style={{ fontSize: "1.2rem", fontWeight: 500, margin: "0 0 8px" }}>
                  {lang === "fr" ? "Aucun participant" : "No participants yet"}
                </p>
                <p style={{ color: themeVars.smallText, opacity: 0.8 }}>
                  {lang === "fr"
                    ? "Les participants apparaîtront ici une fois inscrits"
                    : "Participants will appear here once they register"}
                </p>
              </td></tr>
            ) : (
              filteredParticipants.map((p) => {
                const participantRequest = searchRequests.find(
                  sr => (sr.user_id === p.user_id) ||
                    (sr.id === p.user_id) ||
                    (sr.user_email && sr.user_email.toLowerCase() === (p.email || '').toLowerCase())
                );
                const participantMatches = matches.filter(
                  m => m.user1_id === p.user_id || m.user2_id === p.user_id
                );
                const matchCategories = [...new Set(participantMatches.map(m => m.category))];

                return (
                  <tr
                    key={p.user_id}
                    className={updatedParticipantIds.has(p.user_id) ? (theme === "dark" ? "row-updated-dark" : "row-updated-light") : ""}
                    style={{
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = themeVars.cardHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={tableCellStyle(true)}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                          width: "40px", height: "40px", borderRadius: "50%",
                          background: themeVars.primaryLight, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontWeight: 600, color: themeVars.primary, flexShrink: 0,
                        }}>
                          {p.avatar_url ? (
                            <img
                              src={p.avatar_url}
                              alt={p.name}
                              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.parentElement.innerHTML = `<span style="font-size: 1.2rem; font-weight: 600;">${p.name.charAt(0).toUpperCase()}</span>`;
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: "1.2rem", fontWeight: 600 }}>{p.name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: themeVars.fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? "120px" : "180px" }}>
                            {p.name}
                          </div>
                          <div
                            title={p.email || (lang === "fr" ? "Aucun email" : "No email")}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(p.email || '').catch(() => {});
                              showToast(lang === "fr" ? `📋 Email copié !` : `📋 Email copied!`);
                            }}
                            style={{
                              fontSize: "0.8rem", color: themeVars.smallText,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              maxWidth: isMobile ? "120px" : "180px",
                              cursor: "pointer", borderRadius: "6px",
                              border: "1px solid transparent",
                              padding: "2px 6px", marginLeft: "-6px",
                              transition: "border-color 0.15s, background 0.15s, max-width 0.2s",
                              userSelect: "none",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = themeVars.primary;
                              e.currentTarget.style.background = `${themeVars.primary}15`;
                              e.currentTarget.style.maxWidth = "400px";
                              e.currentTarget.style.overflow = "visible";
                              e.currentTarget.style.whiteSpace = "nowrap";
                              e.currentTarget.style.zIndex = "10";
                              e.currentTarget.style.position = "relative";
                              e.currentTarget.style.boxShadow = `0 2px 12px ${themeVars.primary}20`;
                              e.currentTarget.style.userSelect = "text";
                              e.currentTarget.style.cursor = "text";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = "transparent";
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.maxWidth = isMobile ? "120px" : "180px";
                              e.currentTarget.style.overflow = "hidden";
                              e.currentTarget.style.boxShadow = "none";
                              e.currentTarget.style.userSelect = "none";
                              e.currentTarget.style.cursor = "pointer";
                            }}
                          >
                            {p.email || (lang === "fr" ? "Aucun email" : "No email")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={tableCellStyle()}>{p.phone_number || (lang === "fr" ? "Non spécifié" : "Not specified")}</td>
                    <td style={tableCellStyle()}>
                      {participantRequest ? (
                        (() => {
                          const statusInfo = getRequestStatus(participantRequest, matches);
                          return (
                            <StatusBadge
                              status={statusInfo.type}
                              type="request"
                              details={statusInfo.details}
                            />
                          );
                        })()
                      ) : (
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
                          background: `${themeVars.smallText}10`, color: themeVars.smallText,
                          border: `1px solid ${themeVars.smallText}20`, whiteSpace: "nowrap",
                        }}>
                          {lang === "fr" ? "Aucune requête" : "No Request"}
                        </span>
                      )}
                    </td>
                    <td style={tableCellStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                          width: "60px", height: "6px", borderRadius: "3px",
                          background: `${themeVars.border}`, overflow: "hidden",
                        }}>
                          <div style={{
                            width: `${getProfileFill(p)}%`, height: "100%",
                            background: getProfileFill(p) === 100 ? themeVars.success :
                              getProfileFill(p) === 50 ? themeVars.warning : themeVars.primary,
                            borderRadius: "3px",
                          }} />
                        </div>
                        <span style={{
                          fontSize: "0.85rem", fontWeight: 500,
                          color: getProfileFill(p) === 100 ? themeVars.success :
                            getProfileFill(p) === 50 ? themeVars.warning : themeVars.smallText
                        }}>
                          {getProfileFill(p)}%
                        </span>
                      </div>
                    </td>
                    <td style={tableCellStyle()}>
                      {participantMatches.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "center" }}>
                          <span style={{
                            padding: "6px 14px", borderRadius: "20px",
                            background: themeVars.successLight, color: themeVars.success,
                            fontSize: "0.85rem", fontWeight: 600,
                            border: `1px solid ${themeVars.success}30`,
                          }}>
                            🤝 {participantMatches.length}
                          </span>
                          {matchCategories.length > 0 && (
                            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
                              {matchCategories.slice(0, 3).map((category, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    padding: "3px 8px", borderRadius: "10px",
                                    background: `${themeVars.accent}10`, color: themeVars.accent,
                                    fontSize: "0.75rem", fontWeight: 500,
                                    border: `1px solid ${themeVars.accent}20`,
                                  }}
                                >
                                  {category}
                                </span>
                              ))}
                              {matchCategories.length > 3 && (
                                <span style={{
                                  padding: "3px 8px", borderRadius: "10px",
                                  background: `${themeVars.smallText}10`, color: themeVars.smallText,
                                  fontSize: "0.75rem", fontWeight: 500,
                                }}>
                                  +{matchCategories.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: "0.85rem", color: themeVars.smallText, textAlign: "center" }}>
                          {lang === "fr" ? "Aucun match" : "No matches"}
                        </div>
                      )}
                    </td>
                    <td style={tableCellStyle()}>
                      <button
                        onClick={() => openDrawer(p)}
                        style={{
                          padding: "6px 12px", borderRadius: "8px",
                          background: `${themeVars.accent}15`, color: themeVars.accent,
                          border: `1px solid ${themeVars.accent}30`, fontSize: "0.85rem",
                          fontWeight: 500, cursor: "pointer", transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = `${themeVars.accent}25`;
                          e.target.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = `${themeVars.accent}15`;
                          e.target.style.transform = "scale(1)";
                        }}
                      >
                        👁️ {lang === "fr" ? "Détails" : "Details"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div style={{
        padding: "24px", borderTop: `1px solid ${themeVars.border}`,
        fontSize: "1.05rem", color: themeVars.smallText, textAlign: "center",
      }}>
        {lang === "fr"
          ? "💡 Cliquez sur le bouton Détails pour voir le profil complet du participant"
          : "💡 Click on the Details button to view the participant's full profile"}
      </div>
      </>
      )}
    </div>
  );

  // ===== REQUESTS SECTION =====
  const renderRequestsSection = () => (
    <div style={{
      background: themeVars.subtle, borderRadius: "24px",
      border: `1px solid ${themeVars.border}`, overflow: "hidden",
      boxShadow: theme === "dark"
        ? "0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(176,96,144,0.12)"
        : "0 10px 30px rgba(0,0,0,0.08), 0 0 0 1px rgba(56,25,49,0.08)",
    }}>
      {/* Section Header */}
      <div className="ead-section-header" style={{
        padding: isMobile ? "14px 16px" : "28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: isMobile ? "10px" : "16px",
      }}>
        <div>
          <h2 style={{ fontSize: isMobile ? "1.15rem" : "1.8rem", fontWeight: 700, margin: "0 0 4px", color: themeVars.fg }}>
            {lang === "fr" ? "Requêtes de recherche" : "Search Requests"}
          </h2>
          {!isMobile && (
            <p style={{ fontSize: "1.05rem", color: themeVars.smallText, margin: 0 }}>
              {lang === "fr"
                ? "Toutes les requêtes de recherche dans cet événement"
                : "All search requests in this event"}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: isMobile ? "8px" : "14px", flexWrap: "wrap" }}>
          <IconButton
            onClick={exportSearchRequestsToCSV}
            icon="📥"
            label={isMobile ? "" : (lang === "fr" ? "Exporter CSV" : "Export CSV")}
            color={themeVars.success}
          />
          <IconButton
            onClick={() => {
              const token = localStorage.getItem("token");
              if (!token || !eventId) return;
              fetch(`/api/events/${eventId}/search_requests`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.json())
                .then(data => setSearchRequests(data.search_requests || []))
                .catch(err => console.error("Error reloading search requests:", err));
            }}
            icon="🔄"
            label={isMobile ? "" : (lang === "fr" ? "Actualiser" : "Refresh")}
            color={themeVars.primary}
          />
        </div>
      </div>

      {/* Filters — mobile toggle header */}
      {isMobile && (
        <div style={{
          padding: "12px 16px", borderBottom: mobileRequestFiltersOpen ? "none" : `1px solid ${themeVars.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: themeVars.card,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              onClick={() => setMobileRequestFiltersOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "7px 14px", borderRadius: "10px", border: `1px solid ${themeVars.border}`,
                background: mobileRequestFiltersOpen ? `${themeVars.primary}15` : themeVars.subtle,
                color: mobileRequestFiltersOpen ? themeVars.primary : themeVars.fg,
                fontSize: "0.88rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              🎛️ {lang === "fr" ? "Filtres" : "Filters"}
              <span style={{ fontSize: "0.7rem", transition: "transform 0.2s", transform: mobileRequestFiltersOpen ? "rotate(180deg)" : "none" }}>▼</span>
            </button>
          </div>
          <div style={{ fontSize: "0.85rem", color: themeVars.smallText, fontWeight: 600 }}>
            {filteredSearchRequests.length}/{searchRequests.length}
          </div>
        </div>
      )}

      {/* Filters */}
      {(!isMobile || mobileRequestFiltersOpen) && (
      <div className="ead-filter-bar" style={{
        padding: isMobile ? "12px 16px" : "20px 28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", flexWrap: "wrap", gap: isMobile ? "10px" : "14px", alignItems: "flex-end",
        background: isMobile ? themeVars.card : "transparent",
      }}>
        {/* Status */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Statut" : "Status"}
          </span>
          <select
            value={searchRequestsFilters.status}
            onChange={(e) => setSearchRequestsFilters({ ...searchRequestsFilters, status: e.target.value })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "200px",
            }}
          >
            <option value="all">{lang === "fr" ? "Tous" : "All"}</option>
            <option value="inactive">{lang === "fr" ? "Inactif" : "Inactive"}</option>
            <option value="searching">{lang === "fr" ? "En recherche" : "Searching"}</option>
            <option value="waiting_for_match">{lang === "fr" ? "Attend réponse user" : "Waiting for User"}</option>
            <option value="accepted">{lang === "fr" ? "Match accepté" : "Match Accepted"}</option>
            <option value="rejected">{lang === "fr" ? "Match rejeté" : "Match Rejected"}</option>
            <option value="multiple">{lang === "fr" ? "Plusieurs matchs" : "Multiple Matches"}</option>
          </select>
        </div>
        {/* Category */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Catégorie" : "Category"}
          </span>
          <select
            value={searchRequestsFilters.category}
            onChange={(e) => setSearchRequestsFilters({ ...searchRequestsFilters, category: e.target.value })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "160px",
            }}
          >
            <option value="all">{lang === "fr" ? "Toutes" : "All"}</option>
            <option value="friends">{lang === "fr" ? "Amis" : "Friends"}</option>
            <option value="romance">Romance</option>
            <option value="work">{lang === "fr" ? "Travail" : "Work"}</option>
            <option value="startup">Startup</option>
            <option value="business">Business</option>
            <option value="other">{lang === "fr" ? "Autre" : "Other"}</option>
          </select>
        </div>
        <FilterInput
          value={searchRequestsFilters.userSearch}
          onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, userSearch: v })}
          label={lang === "fr" ? "Nom utilisateur" : "User name"}
          placeholder={lang === "fr" ? "🔍 Jean Dupont..." : "🔍 John Doe..."}
          width={isMobile ? "100%" : "180px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={searchRequestsFilters.idSearch}
          onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, idSearch: v })}
          label={lang === "fr" ? "ID requête" : "Request ID"}
          placeholder="🔍 42..."
          width={isMobile ? "100%" : "120px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={searchRequestsFilters.emailSearch}
          onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, emailSearch: v })}
          label="Email"
          placeholder={lang === "fr" ? "🔍 user@mail.com..." : "🔍 user@mail.com..."}
          width={isMobile ? "100%" : "200px"}
          themeVars={themeVars}
        />
        {/* Matches count */}
        <FilterInput
          type="number"
          value={searchRequestsFilters.matchesMin}
          onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, matchesMin: parseInt(v) || 0 })}
          label={lang === "fr" ? "Matches min" : "Min matches"}
          placeholder="0"
          min={0}
          width={isMobile ? "100%" : "120px"}
          themeVars={themeVars}
        />
        {/* Date range */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Créé du" : "Created from"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <FilterInput
              type="date"
              value={searchRequestsFilters.dateFrom}
              onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, dateFrom: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
            <span style={{ color: themeVars.smallText, fontSize: "0.85rem", fontWeight: 500 }}>→</span>
            <FilterInput
              type="date"
              value={searchRequestsFilters.dateTo}
              onChange={(v) => setSearchRequestsFilters({ ...searchRequestsFilters, dateTo: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
          </div>
        </div>
        <button
          onClick={() => setSearchRequestsFilters({
            status: 'all', category: 'all', userSearch: '', idSearch: '', emailSearch: '',
            matchesMin: 0, dateFrom: '', dateTo: ''
          })}
          style={{
            padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
            background: `${themeVars.warning}0c`, color: themeVars.warning,
            border: `1px solid ${themeVars.warning}30`, fontSize: isMobile ? "0.85rem" : "0.9rem",
            fontWeight: 500, cursor: "pointer", marginLeft: isMobile ? "0" : "auto",
            transition: "all 0.2s ease", alignSelf: "flex-end",
          }}
          onMouseEnter={(e) => e.target.style.background = `${themeVars.warning}15`}
          onMouseLeave={(e) => e.target.style.background = `${themeVars.warning}0c`}
        >
          {lang === "fr" ? "✕ Réinitialiser" : "✕ Reset"}
        </button>
        {!isMobile && (
          <div style={{ marginLeft: "12px", fontSize: "0.9rem", color: themeVars.smallText, alignSelf: "flex-end", paddingBottom: "2px" }}>
            {filteredSearchRequests.length} {lang === "fr" ? "sur" : "of"} {searchRequests.length}
          </div>
        )}
      </div>
      )}

      {/* Mobile Card List */}
      {isMobile ? (
        <div style={{ padding: "12px" }}>
          {searchRequestsLoading ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              {lang === "fr" ? "Chargement..." : "Loading..."}
            </div>
          ) : filteredSearchRequests.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>🔍</div>
              <p style={{ fontWeight: 500 }}>{lang === "fr" ? "Aucune requête" : "No search requests"}</p>
            </div>
          ) : (
            filteredSearchRequests.map((sr) => {
              const statusInfo = getRequestStatus(sr, matches);
              return (
                <div
                  key={sr.id}
                  className={updatedRequestIds.has(sr.id) ? (theme === "dark" ? "row-updated-dark" : "row-updated-light") : ""}
                  style={{
                    background: themeVars.card, borderRadius: "16px",
                    border: `1px solid ${themeVars.border}`, marginBottom: "10px",
                    overflow: "hidden",
                    boxShadow: theme === "dark" ? "0 2px 12px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.06)",
                  }}
                >
                  <div style={{ padding: "14px 14px 10px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", marginBottom: "8px" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "1rem", color: themeVars.fg }}>{sr.user_name}</div>
                        <div style={{ fontSize: "0.78rem", color: themeVars.smallText, marginTop: "2px" }}>
                          #{sr.id} · {formatDate(sr.created_at)}
                        </div>
                      </div>
                      <StatusBadge status={statusInfo.type} type="request" details={statusInfo.details} />
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                      <CategoryBadge category={sr.category} />
                      {sr.total_matches > 0 && (
                        <span style={{
                          padding: "3px 10px", borderRadius: "16px", fontSize: "0.78rem", fontWeight: 600,
                          background: themeVars.successLight, color: themeVars.success, border: `1px solid ${themeVars.success}30`,
                        }}>
                          🤝 {sr.total_matches} {sr.pending_matches > 0 ? `(${sr.pending_matches} ⏳)` : ''}
                        </span>
                      )}
                      {sr.is_active !== false ? (
                        <span style={{ fontSize: "0.75rem", color: themeVars.success }}>✅ Active</span>
                      ) : (
                        <span style={{ fontSize: "0.75rem", color: themeVars.smallText }}>⚪ Inactive</span>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: "10px 14px", borderTop: `1px solid ${themeVars.border}` }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const participant = participants.find(p => p.user_id === sr.user_id);
                        if (participant) {
                          openDrawer(participant);
                          setTimeout(() => openRequestDetailsPanel(sr), 300);
                        }
                      }}
                      style={{
                        width: "100%", padding: "9px 0", borderRadius: "10px",
                        background: `${themeVars.accent}15`, color: themeVars.accent,
                        border: `1px solid ${themeVars.accent}30`, fontSize: "0.88rem",
                        fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      👁️ {lang === "fr" ? "Voir détails" : "View Details"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (

      /* Desktop Table */
      <>
      <div className="ead-table-wrap">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
          <thead>
            <tr>
              <SortableHeader table="searchRequests" columnKey="id">{lang === "fr" ? "ID" : "ID"}</SortableHeader>
              <SortableHeader table="searchRequests" columnKey="user_name">{lang === "fr" ? "Utilisateur" : "User"}</SortableHeader>
              <SortableHeader table="searchRequests" columnKey="category">{lang === "fr" ? "Catégorie" : "Category"}</SortableHeader>
              <SortableHeader table="searchRequests" columnKey="status">{lang === "fr" ? "Statut" : "Status"}</SortableHeader>
              <SortableHeader table="searchRequests" columnKey="created_at">{lang === "fr" ? "Créé le" : "Created At"}</SortableHeader>
              <SortableHeader table="searchRequests" columnKey="total_matches">{lang === "fr" ? "Matches" : "Matches"}</SortableHeader>
              <th style={tableCellStyle()}>{lang === "fr" ? "Détails des matchs" : "Match Details"}</th>
              <th style={tableCellStyle()}>{lang === "fr" ? "Actions" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {searchRequestsLoading ? (
              <tr><td colSpan="8" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                {lang === "fr" ? "Chargement des requêtes..." : "Loading search requests..."}
              </td></tr>
            ) : searchRequestsError ? (
              <tr><td colSpan="8" style={{ padding: "30px", textAlign: "center" }}>
                <div style={{
                  padding: "16px 24px", background: `${themeVars.warning}0a`,
                  border: `1px solid ${themeVars.warning}30`, borderRadius: "12px",
                  color: themeVars.warning, fontSize: "1.05rem", fontWeight: 500,
                }}>
                  {searchRequestsError}
                </div>
              </td></tr>
            ) : filteredSearchRequests.length === 0 ? (
              <tr><td colSpan="8" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                <div style={{ fontSize: "3rem", marginBottom: "12px" }}>🔍</div>
                <p style={{ fontSize: "1.2rem", fontWeight: 500, margin: "0 0 8px" }}>
                  {lang === "fr" ? "Aucune requête" : "No search requests"}
                </p>
                <p style={{ color: themeVars.smallText, opacity: 0.8 }}>
                  {lang === "fr"
                    ? "Les requêtes apparaîtront ici une fois créées"
                    : "Search requests will appear here once created"}
                </p>
              </td></tr>
            ) : (
              filteredSearchRequests.map((sr) => {
                const statusInfo = getRequestStatus(sr, matches);
                return (
                  <tr
                    key={sr.id}
                    className={updatedRequestIds.has(sr.id) ? (theme === "dark" ? "row-updated-dark" : "row-updated-light") : ""}
                    style={{ transition: "all 0.2s ease" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = themeVars.cardHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={tableCellStyle()}>{sr.id}</td>
                    <td style={tableCellStyle(true)}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: isMobile ? "1.05rem" : "1.15rem" }}>
                          {sr.user_name}
                        </div>
                        <div style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: themeVars.smallText }}>
                          {sr.user_email}
                        </div>
                      </div>
                    </td>
                    <td style={tableCellStyle()}>
                      <CategoryBadge category={sr.category} />
                    </td>
                    <td style={tableCellStyle()}>
                      <StatusBadge
                        status={statusInfo.type}
                        type="request"
                        details={statusInfo.details}
                      />
                    </td>
                    <td style={tableCellStyle()}>
                      <div style={{ fontSize: isMobile ? "0.9rem" : "0.95rem", color: themeVars.smallText }}>
                        {formatDate(sr.created_at)}
                      </div>
                    </td>
                    <td style={tableCellStyle()}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{
                          padding: "6px 14px", borderRadius: "20px",
                          background: themeVars.successLight, color: themeVars.success,
                          fontSize: "0.85rem", fontWeight: 600,
                          border: `1px solid ${themeVars.success}30`,
                        }}>
                          {sr.total_matches}
                        </span>
                        <span style={{ fontSize: "0.85rem", color: themeVars.smallText }}>
                          ({sr.pending_matches} pending)
                        </span>
                      </div>
                    </td>
                    <td style={tableCellStyle()}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "0.85rem" }}>
                        <span style={{ color: themeVars.smallText }}>
                          {lang === "fr" ? "Total:" : "Total:"} {sr.total_matches}
                        </span>
                        <span style={{ color: themeVars.smallText }}>
                          {lang === "fr" ? "En attente:" : "Pending:"} {sr.pending_matches}
                        </span>
                        {sr.is_active !== false ? (
                          <span style={{ color: themeVars.success, fontWeight: 500 }}>
                            ✅ {lang === "fr" ? "Actif" : "Active"}
                          </span>
                        ) : (
                          <span style={{ color: themeVars.smallText, fontWeight: 500 }}>
                            ⚪ {lang === "fr" ? "Inactif" : "Inactive"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={tableCellStyle()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const participant = participants.find(p => p.user_id === sr.user_id);
                          if (participant) {
                            openDrawer(participant);
                            setTimeout(() => {
                              openRequestDetailsPanel(sr);
                            }, 300);
                          }
                        }}
                        style={{
                          padding: "6px 12px", borderRadius: "8px",
                          background: `${themeVars.accent}15`, color: themeVars.accent,
                          border: `1px solid ${themeVars.accent}30`, fontSize: "0.85rem",
                          fontWeight: 500, cursor: "pointer", transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = `${themeVars.accent}25`;
                          e.target.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = `${themeVars.accent}15`;
                          e.target.style.transform = "scale(1)";
                        }}
                      >
                        👁️ Details
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div style={{
        padding: "24px", borderTop: `1px solid ${themeVars.border}`,
        fontSize: "1.05rem", color: themeVars.smallText, textAlign: "center",
      }}>
        {lang === "fr"
          ? "💡 Les requêtes de recherche sont créées par les participants pour trouver des correspondances"
          : "💡 Search requests are created by participants to find matches"}
      </div>
      </>
      )}
    </div>
  );

  // ===== MATCHES SECTION =====
  const renderMatchesSection = () => (
    <div style={{
      background: themeVars.subtle, borderRadius: "24px",
      border: `1px solid ${themeVars.border}`, overflow: "hidden",
      boxShadow: theme === "dark"
        ? "0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(176,96,144,0.12)"
        : "0 10px 30px rgba(0,0,0,0.08), 0 0 0 1px rgba(56,25,49,0.08)",
    }}>
      {/* Section Header */}
      <div className="ead-section-header" style={{
        padding: isMobile ? "14px 16px" : "28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: isMobile ? "10px" : "16px",
      }}>
        <div>
          <h2 style={{ fontSize: isMobile ? "1.15rem" : "1.8rem", fontWeight: 700, margin: "0 0 4px", color: themeVars.fg }}>
            {lang === "fr" ? "Matches" : "Matches"}
          </h2>
          {!isMobile && (
            <p style={{ fontSize: "1.05rem", color: themeVars.smallText, margin: 0 }}>
              {lang === "fr"
                ? "Tous les matches dans cet événement"
                : "All matches in this event"}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: isMobile ? "8px" : "14px", flexWrap: "wrap" }}>
          <IconButton
            onClick={exportMatchesToCSV}
            icon="📥"
            label={isMobile ? "" : (lang === "fr" ? "Exporter CSV" : "Export CSV")}
            color={themeVars.success}
          />
          <IconButton
            onClick={() => {
              const token = localStorage.getItem("token");
              if (!token || !eventId) return;
              fetch(`/api/events/${eventId}/matches`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => res.json())
                .then(data => setMatches(data.matches || []))
                .catch(err => console.error("Error reloading matches:", err));
            }}
            icon="🔄"
            label={isMobile ? "" : (lang === "fr" ? "Actualiser" : "Refresh")}
            color={themeVars.primary}
          />
        </div>
      </div>

      {/* Filters — mobile toggle header */}
      {isMobile && (
        <div style={{
          padding: "12px 16px", borderBottom: mobileMatchFiltersOpen ? "none" : `1px solid ${themeVars.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: themeVars.card,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              onClick={() => setMobileMatchFiltersOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "7px 14px", borderRadius: "10px", border: `1px solid ${themeVars.border}`,
                background: mobileMatchFiltersOpen ? `${themeVars.primary}15` : themeVars.subtle,
                color: mobileMatchFiltersOpen ? themeVars.primary : themeVars.fg,
                fontSize: "0.88rem", fontWeight: 600, cursor: "pointer",
              }}
            >
              🎛️ {lang === "fr" ? "Filtres" : "Filters"}
              <span style={{ fontSize: "0.7rem", transition: "transform 0.2s", transform: mobileMatchFiltersOpen ? "rotate(180deg)" : "none" }}>▼</span>
            </button>
          </div>
          <div style={{ fontSize: "0.85rem", color: themeVars.smallText, fontWeight: 600 }}>
            {filteredMatches.length}/{matches.length}
          </div>
        </div>
      )}

      {/* Filters */}
      {(!isMobile || mobileMatchFiltersOpen) && (
      <div className="ead-filter-bar" style={{
        padding: isMobile ? "12px 16px" : "20px 28px", borderBottom: `1px solid ${themeVars.border}`,
        display: "flex", flexWrap: "wrap", gap: isMobile ? "10px" : "14px", alignItems: "flex-end",
        background: isMobile ? themeVars.card : "transparent",
      }}>
        {/* Status */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Statut" : "Status"}
          </span>
          <select
            value={matchesFilters.status}
            onChange={(e) => setMatchesFilters({ ...matchesFilters, status: e.target.value })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "160px",
            }}
          >
            <option value="all">{lang === "fr" ? "Tous" : "All"}</option>
            <option value="pending">{lang === "fr" ? "En attente" : "Pending"}</option>
            <option value="active">{lang === "fr" ? "Accepté" : "Accepted"}</option>
            <option value="rejected">{lang === "fr" ? "Rejeté" : "Rejected"}</option>
          </select>
        </div>
        {/* Category */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Catégorie" : "Category"}
          </span>
          <select
            value={matchesFilters.category}
            onChange={(e) => setMatchesFilters({ ...matchesFilters, category: e.target.value })}
            style={{
              padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
              background: themeVars.card, border: `1px solid ${themeVars.border}`,
              color: themeVars.fg, fontSize: isMobile ? "0.9rem" : "0.95rem", fontWeight: 500,
              outline: "none", cursor: "pointer", minWidth: isMobile ? "140px" : "160px",
            }}
          >
            <option value="all">{lang === "fr" ? "Toutes" : "All"}</option>
            <option value="friends">{lang === "fr" ? "Amis" : "Friends"}</option>
            <option value="romance">Romance</option>
            <option value="work">{lang === "fr" ? "Travail" : "Work"}</option>
            <option value="startup">Startup</option>
            <option value="business">Business</option>
            <option value="other">{lang === "fr" ? "Autre" : "Other"}</option>
          </select>
        </div>
        <FilterInput
          value={matchesFilters.idSearch}
          onChange={(v) => setMatchesFilters({ ...matchesFilters, idSearch: v })}
          label={lang === "fr" ? "ID match" : "Match ID"}
          placeholder="🔍 42..."
          width={isMobile ? "100%" : "120px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={matchesFilters.user1Search}
          onChange={(v) => setMatchesFilters({ ...matchesFilters, user1Search: v })}
          label={lang === "fr" ? "Utilisateur 1 (nom)" : "User 1 (name)"}
          placeholder={lang === "fr" ? "🔍 Jean..." : "🔍 John..."}
          width={isMobile ? "100%" : "180px"}
          themeVars={themeVars}
        />
        <FilterInput
          value={matchesFilters.user2Search}
          onChange={(v) => setMatchesFilters({ ...matchesFilters, user2Search: v })}
          label={lang === "fr" ? "Utilisateur 2 (nom)" : "User 2 (name)"}
          placeholder={lang === "fr" ? "🔍 Marie..." : "🔍 Jane..."}
          width={isMobile ? "100%" : "180px"}
          themeVars={themeVars}
        />
        {/* Compatibility score range — grouped */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Score compatibilité %" : "Compatibility score %"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <FilterInput
              type="number"
              value={matchesFilters.scoreMin}
              onChange={(v) => setMatchesFilters({ ...matchesFilters, scoreMin: parseInt(v) || 0 })}
              placeholder="0"
              min={0}
              max={100}
              width={isMobile ? "80px" : "90px"}
              themeVars={themeVars}
            />
            <span style={{ color: themeVars.smallText, fontSize: "0.85rem", fontWeight: 500 }}>—</span>
            <FilterInput
              type="number"
              value={matchesFilters.scoreMax}
              onChange={(v) => setMatchesFilters({ ...matchesFilters, scoreMax: parseInt(v) || 100 })}
              placeholder="100"
              min={0}
              max={100}
              width={isMobile ? "80px" : "90px"}
              themeVars={themeVars}
            />
          </div>
        </div>
        {/* Date range */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: themeVars.smallText, paddingLeft: "2px" }}>
            {lang === "fr" ? "Créé du" : "Created from"}
          </span>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <FilterInput
              type="date"
              value={matchesFilters.dateFrom}
              onChange={(v) => setMatchesFilters({ ...matchesFilters, dateFrom: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
            <span style={{ color: themeVars.smallText, fontSize: "0.85rem", fontWeight: 500 }}>→</span>
            <FilterInput
              type="date"
              value={matchesFilters.dateTo}
              onChange={(v) => setMatchesFilters({ ...matchesFilters, dateTo: v })}
              width={isMobile ? "140px" : "155px"}
              themeVars={themeVars}
            />
          </div>
        </div>
        <button
          onClick={() => setMatchesFilters({
            status: 'all', category: 'all', idSearch: '', user1Search: '', user2Search: '',
            scoreMin: 0, scoreMax: 100, dateFrom: '', dateTo: ''
          })}
          style={{
            padding: isMobile ? "8px 14px" : "10px 16px", borderRadius: "12px",
            background: `${themeVars.warning}0c`, color: themeVars.warning,
            border: `1px solid ${themeVars.warning}30`, fontSize: isMobile ? "0.85rem" : "0.9rem",
            fontWeight: 500, cursor: "pointer", marginLeft: isMobile ? "0" : "auto",
            transition: "all 0.2s ease", alignSelf: "flex-end",
          }}
          onMouseEnter={(e) => e.target.style.background = `${themeVars.warning}15`}
          onMouseLeave={(e) => e.target.style.background = `${themeVars.warning}0c`}
        >
          {lang === "fr" ? "✕ Réinitialiser" : "✕ Reset"}
        </button>
        {!isMobile && (
          <div style={{ marginLeft: "12px", fontSize: "0.9rem", color: themeVars.smallText, alignSelf: "flex-end", paddingBottom: "2px" }}>
            {filteredMatches.length} {lang === "fr" ? "sur" : "of"} {matches.length}
          </div>
        )}
      </div>
      )}

      {/* Mobile Card List */}
      {isMobile ? (
        <div style={{ padding: "12px" }}>
          {matchesLoading ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              {lang === "fr" ? "Chargement..." : "Loading..."}
            </div>
          ) : filteredMatches.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>🤝</div>
              <p style={{ fontWeight: 500 }}>{lang === "fr" ? "Aucun match" : "No matches"}</p>
            </div>
          ) : (
            filteredMatches.map((match) => (
              <div
                key={match.id}
                className={updatedMatchIds.has(match.id) ? (theme === "dark" ? "row-updated-dark" : "row-updated-light") : ""}
                style={{
                  background: themeVars.card, borderRadius: "16px",
                  border: `1px solid ${themeVars.border}`, marginBottom: "10px",
                  overflow: "hidden",
                  boxShadow: theme === "dark" ? "0 2px 12px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                {/* Match card header: two user avatars */}
                <div style={{ padding: "14px 14px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                    {/* User 1 avatar */}
                    <div style={{
                      width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
                      background: `${themeVars.primary}20`, display: "flex", alignItems: "center",
                      justifyContent: "center", fontWeight: 700, fontSize: "1rem", color: themeVars.primary,
                      border: `2px solid ${themeVars.primary}30`,
                    }}>
                      {match.user1_name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.9rem", color: themeVars.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {match.user1_name}
                      </div>
                      {match.user1_status && (
                        <div style={{ fontSize: "0.72rem", color: match.user1_status === 'accepted' ? themeVars.success : match.user1_status === 'rejected' ? themeVars.danger : themeVars.warning }}>
                          {match.user1_status === 'accepted' ? '✅' : match.user1_status === 'rejected' ? '❌' : '⏳'} {match.user1_status}
                        </div>
                      )}
                    </div>
                    {/* compatibility score */}
                    <div style={{
                      width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0,
                      background: `linear-gradient(135deg, ${themeVars.primary}20, ${themeVars.accent}20)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: "0.78rem", color: themeVars.fg,
                      border: `2px solid ${themeVars.primary}20`,
                    }}>
                      {Math.round(match.compatibility_score)}%
                    </div>
                    <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                      <div style={{ fontWeight: 600, fontSize: "0.9rem", color: themeVars.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {match.user2_name}
                      </div>
                      {match.user2_status && (
                        <div style={{ fontSize: "0.72rem", color: match.user2_status === 'accepted' ? themeVars.success : match.user2_status === 'rejected' ? themeVars.danger : themeVars.warning, textAlign: "right" }}>
                          {match.user2_status === 'accepted' ? '✅' : match.user2_status === 'rejected' ? '❌' : '⏳'} {match.user2_status}
                        </div>
                      )}
                    </div>
                    <div style={{
                      width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
                      background: `${themeVars.accent}20`, display: "flex", alignItems: "center",
                      justifyContent: "center", fontWeight: 700, fontSize: "1rem", color: themeVars.accent,
                      border: `2px solid ${themeVars.accent}30`,
                    }}>
                      {match.user2_name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                    {getMatchStatusBadge(match.status, match.user1_status, match.user2_status, match.user1_name, match.user2_name)}
                    <CategoryBadge category={match.category} />
                    <span style={{ fontSize: "0.75rem", color: themeVars.smallText }}>#{match.id}</span>
                  </div>
                </div>
                <div style={{ padding: "10px 14px", borderTop: `1px solid ${themeVars.border}` }}>
                  <button
                    onClick={() => openMatchDetailsModal(match.id)}
                    style={{
                      width: "100%", padding: "9px 0", borderRadius: "10px",
                      background: `${themeVars.success}15`, color: themeVars.success,
                      border: `1px solid ${themeVars.success}30`, fontSize: "0.88rem",
                      fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    👁️ {lang === "fr" ? "Voir détails" : "View Details"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (

      /* Desktop Table */
      <>
      <div className="ead-table-wrap">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
          <thead>
            <tr>
              <SortableHeader table="matches" columnKey="id">{lang === "fr" ? "ID" : "ID"}</SortableHeader>
              <SortableHeader table="matches" columnKey="user1_name">{lang === "fr" ? "Utilisateur 1" : "User 1"}</SortableHeader>
              <SortableHeader table="matches" columnKey="user2_name">{lang === "fr" ? "Utilisateur 2" : "User 2"}</SortableHeader>
              <SortableHeader table="matches" columnKey="category">{lang === "fr" ? "Catégorie" : "Category"}</SortableHeader>
              <SortableHeader table="matches" columnKey="status">{lang === "fr" ? "Statut" : "Status"}</SortableHeader>
              <SortableHeader table="matches" columnKey="created_at">{lang === "fr" ? "Créé le" : "Created At"}</SortableHeader>
              <SortableHeader table="matches" columnKey="compatibility_score">{lang === "fr" ? "Compatibilité" : "Compatibility"}</SortableHeader>
              <th style={tableCellStyle()}>{lang === "fr" ? "Actions" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {matchesLoading ? (
              <tr><td colSpan="8" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                {lang === "fr" ? "Chargement des matches..." : "Loading matches..."}
              </td></tr>
            ) : matchesError ? (
              <tr><td colSpan="8" style={{ padding: "30px", textAlign: "center" }}>
                <div style={{
                  padding: "16px 24px", background: `${themeVars.warning}0a`,
                  border: `1px solid ${themeVars.warning}30`, borderRadius: "12px",
                  color: themeVars.warning, fontSize: "1.05rem", fontWeight: 500,
                }}>
                  {matchesError}
                </div>
              </td></tr>
            ) : filteredMatches.length === 0 ? (
              <tr><td colSpan="8" style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                <div style={{ fontSize: "3rem", marginBottom: "12px" }}>🤝</div>
                <p style={{ fontSize: "1.2rem", fontWeight: 500, margin: "0 0 8px" }}>
                  {lang === "fr" ? "Aucun match" : "No matches"}
                </p>
                <p style={{ color: themeVars.smallText, opacity: 0.8 }}>
                  {lang === "fr"
                    ? "Les matches apparaîtront ici une fois créés"
                    : "Matches will appear here once created"}
                </p>
              </td></tr>
            ) : (
              filteredMatches.map((match) => (
                <tr
                  key={match.id}
                  className={updatedMatchIds.has(match.id) ? (theme === "dark" ? "row-updated-dark" : "row-updated-light") : ""}
                  style={{ transition: "all 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = themeVars.cardHover}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={tableCellStyle()}>{match.id}</td>
                  <td style={tableCellStyle(true)}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: isMobile ? "1.05rem" : "1.15rem" }}>
                        {match.user1_name}
                      </div>
                      <div style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: themeVars.smallText }}>
                        ID: {match.user1_id}
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle(true)}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: isMobile ? "1.05rem" : "1.15rem" }}>
                        {match.user2_name}
                      </div>
                      <div style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", color: themeVars.smallText }}>
                        ID: {match.user2_id}
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle()}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                      <span style={{
                        fontSize: "0.72rem", fontWeight: 700, color: themeVars.info,
                        textTransform: "uppercase", letterSpacing: "0.4px",
                        display: "flex", alignItems: "center", gap: "3px",
                      }}>
                        🏷️ {lang === "fr" ? "Match par catégories" : "Match by categories"}
                      </span>
                      <CategoryBadge category={match.category} />
                    </div>
                  </td>
                  <td style={tableCellStyle()}>
                    {getMatchStatusBadge(match.status, match.user1_status, match.user2_status, match.user1_name, match.user2_name)}
                  </td>
                  <td style={tableCellStyle()}>
                    <div style={{ fontSize: isMobile ? "0.9rem" : "0.95rem", color: themeVars.smallText }}>
                      {formatDate(match.created_at)}
                    </div>
                  </td>
                  <td style={tableCellStyle()}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                      <div style={{
                        width: "40px", height: "40px", borderRadius: "50%",
                        background: `linear-gradient(135deg, ${themeVars.primary}25, ${themeVars.accent}25)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 600, fontSize: "0.95rem"
                      }}>
                        {Math.round(match.compatibility_score)}%
                      </div>
                    </div>
                  </td>
                  <td style={tableCellStyle()}>
                    <button
                      onClick={() => openMatchDetailsModal(match.id)}
                      style={{
                        padding: "6px 12px", borderRadius: "8px",
                        background: `${themeVars.success}15`, color: themeVars.success,
                        border: `1px solid ${themeVars.success}30`, fontSize: "0.85rem",
                        fontWeight: 500, cursor: "pointer", transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = `${themeVars.success}25`;
                        e.target.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = `${themeVars.success}15`;
                        e.target.style.transform = "scale(1)";
                      }}
                    >
                      👁️ Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div style={{
        padding: "24px", borderTop: `1px solid ${themeVars.border}`,
        fontSize: "1.05rem", color: themeVars.smallText, textAlign: "center",
      }}>
        {lang === "fr"
          ? "💡 Les matches sont créés automatiquement par le système de matchmaking"
          : "💡 Matches are created automatically by the matchmaking system"}
      </div>
      </>
      )}
    </div>
  );

  // ===== COMMENTS SECTION =====
  const renderCommentsSection = () => {
    const totalComments = adminComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

    const AdminCommentItem = ({ comment, depth = 0 }) => {
      const avatarLetter = (comment.author?.name || "?")[0].toUpperCase();
      const timeAgo = (dateStr) => {
        const diff = (Date.now() - new Date(dateStr)) / 1000;
        if (diff < 60) return lang === "fr" ? "à l'instant" : "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}${lang === "fr" ? " min" : "m"}`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}${lang === "fr" ? " h" : "h"}`;
        return new Date(dateStr).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "short", day: "numeric" });
      };

      return (
        <div style={{ marginLeft: depth > 0 ? 48 : 0, marginTop: depth > 0 ? 10 : 0 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            {/* Avatar */}
            <div style={{
              flexShrink: 0, width: depth > 0 ? 32 : 40, height: depth > 0 ? 32 : 40,
              borderRadius: "50%", background: themeVars.primaryLight,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, color: themeVars.primary, fontSize: depth > 0 ? "0.75rem" : "0.9rem",
              overflow: "hidden",
            }}>
              {comment.author?.avatar_url ? (
                <img
                  src={comment.author.avatar_url}
                  alt={comment.author.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                  onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = `<span style="font-size:0.9rem;font-weight:700">${avatarLetter}</span>`; }}
                />
              ) : (
                <span>{avatarLetter}</span>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Author + time */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.9rem", fontWeight: 700, color: themeVars.fg }}>{comment.author?.name}</span>
                <span style={{ fontSize: "0.75rem", color: themeVars.smallText }}>{timeAgo(comment.created_at)}</span>
                {comment.is_deleted && (
                  <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 10, background: `${themeVars.danger}15`, color: themeVars.danger, fontWeight: 600 }}>
                    {lang === "fr" ? "Supprimé" : "Deleted"}
                  </span>
                )}
              </div>

              {/* Body */}
              <p style={{
                fontSize: "0.92rem", lineHeight: 1.65, margin: 0,
                color: comment.is_deleted ? themeVars.smallText : themeVars.fg,
                fontStyle: comment.is_deleted ? "italic" : "normal",
                wordBreak: "break-word",
              }}>
                {comment.body}
              </p>

              {/* Author ID badge */}
              <div style={{ marginTop: 6, fontSize: "0.75rem", color: themeVars.smallText }}>
                ID: {comment.author?.id} · #{comment.id}
              </div>
            </div>
          </div>

          {/* Replies */}
          {comment.replies?.length > 0 && (
            <div style={{
              marginLeft: 48, marginTop: 4,
              borderLeft: `2px solid ${themeVars.border}`, paddingLeft: 16,
            }}>
              {comment.replies.map(reply => (
                <div key={reply.id} style={{ marginBottom: 16 }}>
                  <AdminCommentItem comment={reply} depth={depth + 1} />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div style={{
        background: themeVars.subtle, borderRadius: "24px",
        border: `1px solid ${themeVars.border}`, overflow: "hidden",
        boxShadow: theme === "dark"
          ? "0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(176,96,144,0.12)"
          : "0 10px 30px rgba(0,0,0,0.08), 0 0 0 1px rgba(56,25,49,0.08)",
      }}>
        {/* Section Header */}
        <div style={{
          padding: isMobile ? "20px" : "28px", borderBottom: `1px solid ${themeVars.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16,
        }}>
          <div>
            <h2 style={{ fontSize: isMobile ? "1.5rem" : "1.8rem", fontWeight: 700, margin: "0 0 6px", color: themeVars.fg }}>
              💬 {lang === "fr" ? "Commentaires & Avis" : "Comments & Reviews"}
            </h2>
            <p style={{ fontSize: "1rem", color: themeVars.smallText, margin: 0 }}>
              {totalComments} {lang === "fr" ? "commentaires au total" : "total comments"} · {adminComments.length} {lang === "fr" ? "discussions" : "threads"}
            </p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? "16px" : "28px" }}>
          {adminCommentsLoading ? (
            <div style={{ padding: "60px", textAlign: "center", color: themeVars.smallText }}>
              {lang === "fr" ? "Chargement des commentaires..." : "Loading comments..."}
            </div>
          ) : adminCommentsError ? (
            <div style={{ padding: "40px", textAlign: "center", color: themeVars.danger }}>{adminCommentsError}</div>
          ) : adminComments.length === 0 ? (
            <div style={{ padding: "60px", textAlign: "center", color: themeVars.smallText }}>
              <div style={{ fontSize: "3rem", marginBottom: 16, opacity: 0.4 }}>💬</div>
              <p style={{ fontSize: "1.1rem", fontWeight: 500, margin: "0 0 8px" }}>
                {lang === "fr" ? "Aucun commentaire" : "No comments yet"}
              </p>
              <p style={{ fontSize: "0.95rem", opacity: 0.7 }}>
                {lang === "fr" ? "Les avis des participants apparaîtront ici" : "Participant reviews will appear here"}
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {adminComments.map(comment => (
                <div
                  key={comment.id}
                  style={{
                    padding: "20px 24px", borderRadius: 16,
                    background: themeVars.card, border: `1px solid ${themeVars.border}`,
                    transition: "border-color 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = themeVars.primary}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = themeVars.border}
                >
                  <AdminCommentItem comment={comment} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===== INVITED USERS SECTION =====
  const renderInvitedUsersSection = () => {
    const filteredReferrals = referrals.filter(r => {
      if (!referralsSearch) return true;
      return r.user_name?.toLowerCase().includes(referralsSearch.toLowerCase()) ||
             r.ref_code?.toLowerCase().includes(referralsSearch.toLowerCase());
    });

    return (
      <div style={{
        background: themeVars.subtle, borderRadius: "24px",
        border: `1px solid ${themeVars.border}`, overflow: "hidden",
        boxShadow: theme === "dark"
          ? "0 10px 30px rgba(0,0,0,0.45), 0 0 0 1px rgba(176,96,144,0.12)"
          : "0 10px 30px rgba(0,0,0,0.08), 0 0 0 1px rgba(56,25,49,0.08)",
      }}>
        {/* Section Header */}
        <div style={{
          padding: isMobile ? "20px" : "28px", borderBottom: `1px solid ${themeVars.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: "16px",
        }}>
          <div>
            <h2 style={{ fontSize: isMobile ? "1.5rem" : "1.8rem", fontWeight: 700, margin: "0 0 6px", color: themeVars.fg }}>
              {lang === "fr" ? "Utilisateurs invités" : "Invited Users"}
            </h2>
            <p style={{ fontSize: isMobile ? "0.95rem" : "1.05rem", color: themeVars.smallText, margin: 0 }}>
              {lang === "fr"
                ? "Invitations par lien de parrainage — scans, inscriptions et actions"
                : "Referral invitations — scans, registrations and actions"}
            </p>
          </div>
          <IconButton
            onClick={() => window.location.reload()}
            icon="🔄"
            label={lang === "fr" ? "Actualiser" : "Refresh"}
            color={themeVars.primary}
          />
        </div>

        {/* Filter bar */}
        <div style={{
          padding: isMobile ? "16px 20px" : "20px 28px", borderBottom: `1px solid ${themeVars.border}`,
          display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap",
        }}>
          <FilterInput
            value={referralsSearch}
            onChange={setReferralsSearch}
            label={lang === "fr" ? "Rechercher" : "Search"}
            placeholder={lang === "fr" ? "Nom, email..." : "Name, email..."}
            width={isMobile ? "100%" : "280px"}
            themeVars={themeVars}
          />
          {referralsSearch && (
            <button
              onClick={() => setReferralsSearch('')}
              style={{
                padding: "10px 16px", borderRadius: "12px",
                background: `${themeVars.warning}0c`, color: themeVars.warning,
                border: `1px solid ${themeVars.warning}30`, fontSize: "0.9rem",
                fontWeight: 500, cursor: "pointer",
              }}
            >
              ✕ {lang === "fr" ? "Réinitialiser" : "Reset"}
            </button>
          )}
          <div style={{ marginLeft: "auto", fontSize: "0.9rem", color: themeVars.smallText }}>
            {filteredReferrals.length} {lang === "fr" ? "participants" : "participants"}
          </div>
        </div>

        {/* Content */}
        {referralsLoading ? (
          <div style={{ padding: "60px", textAlign: "center", color: themeVars.smallText, fontSize: "1.1rem" }}>
            ⏳ {lang === "fr" ? "Chargement..." : "Loading..."}
          </div>
        ) : referralsError ? (
          <div style={{ padding: "40px 28px" }}>
            <div style={{
              padding: "16px 24px", background: `${themeVars.warning}0a`,
              border: `1px solid ${themeVars.warning}30`, borderRadius: "12px",
              color: themeVars.warning, fontSize: "1.05rem", fontWeight: 500,
            }}>
              {referralsError}
            </div>
          </div>
        ) : filteredReferrals.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: themeVars.smallText }}>
            <div style={{ fontSize: "3rem", marginBottom: "12px" }}>📨</div>
            <p style={{ fontSize: "1.2rem", fontWeight: 500, margin: "0 0 8px", color: themeVars.fg }}>
              {lang === "fr" ? "Aucune invitation trouvée" : "No invitations found"}
            </p>
            <p style={{ opacity: 0.7 }}>
              {lang === "fr"
                ? "Les invitations apparaîtront ici dès qu'un participant partagera son lien"
                : "Invitations will appear here once a participant shares their link"}
            </p>
          </div>
        ) : (
          <div style={{ padding: isMobile ? "16px" : "24px 28px", display: "flex", flexDirection: "column", gap: "16px" }}>
            {filteredReferrals.map((referral) => {
              const isExpanded = expandedReferral === referral.id;
              const totalScans = referral.scan_count || 0;
              const uniqueScans = referral.unique_scan_count || 0;
              const registrations = referral.registration_count || 0;

              return (
                <div key={referral.id} style={{
                  borderRadius: "18px",
                  border: `1px solid ${isExpanded ? themeVars.primary + '50' : themeVars.border}`,
                  background: themeVars.card,
                  overflow: "hidden",
                  transition: "all 0.25s ease",
                  boxShadow: isExpanded ? `0 4px 20px ${themeVars.primary}15` : "none",
                }}>
                  {/* Referral Row Header */}
                  <div
                    onClick={() => setExpandedReferral(isExpanded ? null : referral.id)}
                    style={{
                      padding: isMobile ? "16px" : "20px 24px",
                      display: "flex", alignItems: "center", gap: "16px",
                      cursor: "pointer", flexWrap: "wrap",
                      background: isExpanded ? `${themeVars.primary}06` : "transparent",
                      transition: "background 0.2s ease",
                    }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = themeVars.cardHover; }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = "transparent"; }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: "46px", height: "46px", borderRadius: "50%", flexShrink: 0,
                      background: `linear-gradient(135deg, ${themeVars.primary}25, ${themeVars.accent}25)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: "1.2rem", color: themeVars.primary,
                      border: `2px solid ${themeVars.primary}30`,
                    }}>
                      {(referral.user_name || "?").charAt(0).toUpperCase()}
                    </div>

                    {/* User info */}
                    <div style={{ flex: 1, minWidth: "120px" }}>
                      <div style={{ fontWeight: 600, fontSize: "1.05rem", color: themeVars.fg, marginBottom: "2px" }}>
                        {referral.user_name || `User #${referral.user_id}`}
                      </div>
                      <div style={{ fontSize: "0.82rem", color: themeVars.smallText, fontFamily: "monospace", letterSpacing: "0.03em" }}>
                        🔗 {referral.ref_code}
                      </div>
                    </div>

                    {/* Stats pills */}
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{
                        padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
                        background: `${themeVars.info}15`, color: themeVars.info,
                        border: `1px solid ${themeVars.info}30`,
                        display: "flex", alignItems: "center", gap: "5px",
                      }}>
                        👁️ {totalScans} {lang === "fr" ? "scans" : "scans"}
                      </div>
                      <div style={{
                        padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
                        background: `${themeVars.warning}15`, color: themeVars.warning,
                        border: `1px solid ${themeVars.warning}30`,
                        display: "flex", alignItems: "center", gap: "5px",
                      }}>
                        🎯 {uniqueScans} {lang === "fr" ? "uniques" : "unique"}
                      </div>
                      <div style={{
                        padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
                        background: registrations > 0 ? `${themeVars.success}15` : `${themeVars.smallText}15`,
                        color: registrations > 0 ? themeVars.success : themeVars.smallText,
                        border: `1px solid ${registrations > 0 ? themeVars.success : themeVars.smallText}30`,
                        display: "flex", alignItems: "center", gap: "5px",
                      }}>
                        ✅ {registrations} {lang === "fr" ? "inscriptions" : "registrations"}
                      </div>
                    </div>

                    {/* Expand arrow */}
                    <div style={{
                      fontSize: "1rem", color: themeVars.smallText, flexShrink: 0,
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                    }}>
                      ▼
                    </div>
                  </div>

                  {/* Expanded: invited users table */}
                  {isExpanded && (
                    <div style={{ borderTop: `1px solid ${themeVars.border}` }}>
                      {!referral.scans || referral.scans.length === 0 ? (
                        <div style={{ padding: "32px", textAlign: "center", color: themeVars.smallText }}>
                          <div style={{ fontSize: "2rem", marginBottom: "8px" }}>📭</div>
                          <p style={{ margin: 0 }}>
                            {lang === "fr"
                              ? "Aucun scan enregistré pour ce lien"
                              : "No scans recorded for this link yet"}
                          </p>
                        </div>
                      ) : (
                        <div className="ead-table-wrap">
                          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                            <thead>
                              <tr>
                                {[
                                  { key: "user", label: lang === "fr" ? "Utilisateur invité" : "Invited User" },
                                  { key: "date", label: lang === "fr" ? "Date" : "Date" },
                                  { key: "result", label: lang === "fr" ? "Résultat" : "Result" },
                                  { key: "actions", label: lang === "fr" ? "Actions" : "Actions" },
                                ].map(col => (
                                  <th key={col.key} style={{
                                    textAlign: "left", padding: "14px 16px", fontSize: "0.78rem", fontWeight: 700,
                                    color: themeVars.fg, borderBottom: `2px solid ${themeVars.border}`,
                                    background: themeVars.subtle, textTransform: "uppercase", letterSpacing: "0.5px",
                                  }}>
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {referral.scans.map((scan) => {
                                // Priority: scanned_by if known, else registered_user if they registered
                                const knownById = !!scan.scanned_by_user_id;
                                const displayUsername = scan.scanned_by_username || scan.registered_user_username;
                                const displayName = displayUsername
                                  ? `@${displayUsername}`
                                  : (scan.scanned_by_user_name || scan.registered_user_name || null);
                                const displayId = scan.scanned_by_user_id || scan.registered_user_id;
                                const isRegistered = scan.resulted_in_registration && scan.registered_user_id;
                                const hasIdentity = knownById || isRegistered;
                                return (
                                <tr
                                  key={scan.id}
                                  style={{ transition: "background 0.2s ease" }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = themeVars.cardHover}
                                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                  <td style={tableCellStyle(true)}>
                                    {hasIdentity ? (
                                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <div style={{
                                          width: "36px", height: "36px", borderRadius: "50%", flexShrink: 0,
                                          background: `linear-gradient(135deg, ${themeVars.primary}20, ${themeVars.accent}20)`,
                                          display: "flex", alignItems: "center", justifyContent: "center",
                                          fontWeight: 700, fontSize: "1rem", color: themeVars.primary,
                                          border: `1px solid ${themeVars.primary}25`,
                                        }}>
                                          {(displayName || '?').replace('@','').charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                            {displayName || `User #${displayId}`}
                                          </div>
                                          <div style={{ fontSize: "0.78rem", color: themeVars.smallText }}>
                                            ID: {displayId}
                                            {!knownById && isRegistered && (
                                              <span style={{ marginLeft: "6px", color: themeVars.success, fontStyle: "normal" }}>
                                                · {lang === "fr" ? "inscrit via lien" : "registered via link"}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <span style={{ color: themeVars.smallText, fontStyle: "italic", fontSize: "0.9rem" }}>
                                        👁️ {lang === "fr" ? "External visit" : "External visit"}
                                      </span>
                                    )}
                                  </td>
                                  <td style={tableCellStyle()}>
                                    <span style={{ fontSize: "0.9rem" }}>
                                      {formatDate(scan.scanned_at)}
                                    </span>
                                  </td>
                                  <td style={tableCellStyle()}>
                                    {scan.kicked ? (
                                      <span style={{
                                        padding: "5px 12px", borderRadius: "16px", fontSize: "0.82rem", fontWeight: 600,
                                        background: themeVars.dangerLight, color: themeVars.danger,
                                        border: `1px solid ${themeVars.danger}30`,
                                      }}>
                                        🚫 {lang === "fr" ? "Expulsé" : "Kicked"}
                                      </span>
                                    ) : isRegistered ? (
                                      <span style={{
                                        padding: "5px 12px", borderRadius: "16px", fontSize: "0.82rem", fontWeight: 600,
                                        background: themeVars.successLight, color: themeVars.success,
                                        border: `1px solid ${themeVars.success}30`,
                                      }}>
                                        ✅ {lang === "fr" ? "Inscrit" : "Registered"}
                                      </span>
                                    ) : (
                                      <span style={{
                                        padding: "5px 12px", borderRadius: "16px", fontSize: "0.82rem", fontWeight: 600,
                                        background: `${themeVars.smallText}12`, color: themeVars.smallText,
                                        border: `1px solid ${themeVars.smallText}25`,
                                      }}>
                                        👁️ {lang === "fr" ? "Scan seulement" : "Scan only"}
                                      </span>
                                    )}
                                  </td>
                                  <td style={tableCellStyle()}>
                                    {isRegistered && !scan.kicked ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleKickUser(scan.registered_user_id, displayName);
                                        }}
                                        style={{
                                          padding: "7px 14px", borderRadius: "10px", fontSize: "0.82rem", fontWeight: 600,
                                          background: themeVars.dangerLight, color: themeVars.danger,
                                          border: `1px solid ${themeVars.danger}30`, cursor: "pointer",
                                          display: "flex", alignItems: "center", gap: "5px",
                                          transition: "all 0.2s ease",
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = `${themeVars.danger}28`}
                                        onMouseLeave={(e) => e.currentTarget.style.background = themeVars.dangerLight}
                                        title={lang === "fr" ? "Expulser du l'événement" : "Kick from event"}
                                      >
                                        🚫 {lang === "fr" ? "Expulser" : "Kick"}
                                      </button>
                                    ) : (
                                      <span style={{ color: themeVars.smallText, fontSize: "0.85rem" }}>—</span>
                                    )}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer hint */}
        <div style={{
          padding: isMobile ? "16px 20px" : "20px 28px", borderTop: `1px solid ${themeVars.border}`,
          fontSize: "0.92rem", color: themeVars.smallText, textAlign: "center",
        }}>
          💡 {lang === "fr"
            ? "Cliquez sur un participant pour voir le détail de ses invitations"
            : "Click on a participant row to expand their invitation details"}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body {
          margin: 0;
          background: ${theme === "dark" ? "#0a0a14" : "#FFF3E6"};
        }

        /* ── Scrollable tabs (hide scrollbar track) ── */
        .ead-tabs::-webkit-scrollbar { display: none; }

        /* ── Tables: horizontal scroll on small screens ── */
        .ead-table-wrap {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .ead-table-wrap table { min-width: 640px; }

        /* ── Mobile bottom tab bar ── */
        .ead-mobile-tabs {
          position: fixed;
          bottom: 0; left: 0; right: 0;
          z-index: 900;
          display: flex;
          background: var(--tab-bg);
          border-top: 1px solid var(--tab-border);
          padding: 0;
          padding-bottom: env(safe-area-inset-bottom, 0px);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        .ead-mobile-tab-btn {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 8px 4px 6px;
          border: none;
          background: transparent;
          cursor: pointer;
          position: relative;
          min-width: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .ead-mobile-tab-btn .tab-icon {
          font-size: 1.25rem;
          line-height: 1;
        }
        .ead-mobile-tab-btn .tab-label {
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 0.3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 56px;
        }
        .ead-mobile-tab-btn .tab-badge {
          position: absolute;
          top: 5px;
          right: calc(50% - 14px);
          min-width: 16px;
          height: 16px;
          border-radius: 8px;
          font-size: 0.6rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
          pointer-events: none;
        }

        /* ── Filter bar: stack on mobile ── */
        @media (max-width: 640px) {
          .ead-filter-bar {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .ead-filter-bar select,
          .ead-filter-bar input {
            width: 100% !important;
            min-width: unset !important;
          }
          .ead-section-header {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .ead-section-header-actions {
            width: 100%;
            justify-content: flex-start !important;
          }
          .ead-drawer-header {
            padding: 16px 16px 12px !important;
          }
          .ead-drawer-body {
            padding: 0 16px 20px !important;
          }
          .ead-drawer-content-grid {
            grid-template-columns: 1fr !important;
          }
          .ead-stats-card-value {
            font-size: 1.4rem !important;
          }
        }

        /* ── Tablet tweaks ── */
        @media (min-width: 641px) and (max-width: 1024px) {
          .ead-filter-bar {
            flex-wrap: wrap !important;
          }
          .ead-filter-bar select,
          .ead-filter-bar input {
            min-width: 140px !important;
          }
        }

        /* ── Wide screen: more columns ── */
        @media (min-width: 1400px) {
          .ead-table-wrap table { min-width: unset; }
        }

        /* ── Drawer content ── */
        .ead-drawer-header { padding: 32px 40px 24px; }
        .ead-drawer-body   { padding: 0 40px 32px; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateX(-50%) translateY(10px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes fabFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes slideUpIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes rowFlash {
          0%   { background: transparent; }
          25%  { background: rgba(139,92,246,0.18); }
          100% { background: transparent; }
        }
        @keyframes rowFlashLight {
          0%   { background: transparent; }
          25%  { background: rgba(56,25,49,0.12); }
          100% { background: transparent; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.85); }
        }
        .row-updated-dark  { animation: rowFlash 1.1s ease both; }
        .row-updated-light { animation: rowFlashLight 1.1s ease both; }
        .fab-float {
          animation: fabFloat 4s ease-in-out infinite;
        }
        .fab-float:hover,
        .fab-float:active {
          animation-play-state: paused;
        }
        .mobile-card-enter {
          animation: slideUpIn 0.22s ease both;
        }
      `}</style>

      {/* ===== BACKGROUND DECORATION ===== */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden",
      }}>
        {/* Orb 1 — top left */}
        <div style={{
          position: "absolute", top: "-10%", left: "-8%",
          width: "520px", height: "520px", borderRadius: "50%",
          background: theme === "dark"
            ? "radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(160,80,140,0.13) 0%, transparent 70%)",
          filter: "blur(40px)",
        }} />
        {/* Orb 2 — bottom right */}
        <div style={{
          position: "absolute", bottom: "-12%", right: "-6%",
          width: "600px", height: "600px", borderRadius: "50%",
          background: theme === "dark"
            ? "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(124,58,237,0.09) 0%, transparent 70%)",
          filter: "blur(50px)",
        }} />
        {/* Orb 3 — center subtle */}
        <div style={{
          position: "absolute", top: "40%", left: "30%",
          width: "700px", height: "400px", borderRadius: "50%",
          background: theme === "dark"
            ? "radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(200,140,180,0.07) 0%, transparent 70%)",
          filter: "blur(60px)",
        }} />
        {/* Subtle grid overlay */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: theme === "dark"
            ? "linear-gradient(rgba(139,92,246,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.04) 1px, transparent 1px)"
            : "linear-gradient(rgba(160,80,140,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(160,80,140,0.05) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      {/* ===== MAIN CONTAINER ===== */}
      <div style={{
        position: "relative", zIndex: 2, minHeight: "100vh",
        background: "transparent",
        color: themeVars.fg, fontFamily: "'Inter', system-ui, sans-serif",
        padding: isMobile ? "12px 12px 90px" : isTablet ? "20px" : "32px",
        paddingTop: isMobile ? "64px" : isTablet ? "80px" : "80px",
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        gap: isMobile ? "0" : "24px",
        // CSS vars for mobile tab bar
        "--tab-bg": theme === "dark" ? "rgba(10,10,20,0.96)" : "rgba(255,248,240,0.97)",
        "--tab-border": themeVars.border,
      }}>

        {/* ===== CO-ADMINS SIDEBAR (removed — now a tab) ===== */}

        {/* Main Content */}
        <div style={{
          width: isMobile ? "100%" : isTablet ? "100%" : "92%", maxWidth: "1440px",
          background: theme === "dark" ? "rgba(26,31,46,0.55)" : "rgba(255,243,230,0.55)",
          backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
          borderRadius: "0", border: "none", padding: isMobile ? "16px" : "24px",
          flexShrink: 0,
        }}>
          {/* Header */}
          {isMobile ? (
            <div style={{ marginBottom: "14px" }}>
              <button
                onClick={() => navigate(`/events/${slug}`)}
                style={{
                  background: "none", border: "none", color: themeVars.primary,
                  fontSize: "0.88rem", fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "5px",
                  padding: "4px 8px", borderRadius: "8px", marginBottom: "8px", marginLeft: "-8px",
                }}
              >
                ← {lang === "fr" ? "Retour" : "Back"}
              </button>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ minWidth: 0 }}>
                  <h1 style={{ fontSize: "1.3rem", fontWeight: 800, margin: "0 0 2px", color: themeVars.primary, letterSpacing: "-0.02em" }}>
                    {lang === "fr" ? "Admin Panel" : "Admin Panel"}
                  </h1>
                  <p style={{ fontSize: "0.85rem", color: themeVars.smallText, fontWeight: 500, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {eventTitle || (lang === "fr" ? "Événement" : "Event")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexDirection: "column",
            marginBottom: "32px",
            gap: "12px",
          }}>
            <button
              onClick={() => navigate(`/events/${slug}`)}
              style={{
                background: "none", border: "none", color: themeVars.primary,
                fontSize: "1.2rem", fontWeight: 600,
                cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
                padding: "6px 12px", borderRadius: "10px", transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = `${themeVars.primary}0a`}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              aria-label={lang === "fr" ? "Retour aux détails de l'événement" : "Back to event details"}
            >
              ← {lang === "fr" ? "Retour" : "Back"}
            </button>
            <div>
              <h1 style={{
                fontSize: isTablet ? "2rem" : "2.4rem", fontWeight: 800,
                margin: "8px 0 4px",
                color: themeVars.primary,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}>
                {lang === "fr" ? "Panneau d'Administration" : "Event Admin Panel"}
              </h1>
              <p style={{ fontSize: "1.3rem", color: themeVars.smallText, fontWeight: 500, margin: 0 }}>
                {eventTitle || (lang === "fr" ? "Événement" : "Event")}
              </p>
            </div>
          </div>
          )}

          {/* Stats Grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : isTablet ? "repeat(3, 1fr)" : "repeat(5, 1fr)",
            gap: isMobile ? "8px" : "16px", marginBottom: eventCapacity ? (isMobile ? "12px" : "16px") : (isMobile ? "16px" : "32px")
          }}>
            <StatsCard label={lang === "fr" ? "Total" : "Total"} value={stats.total} color={themeVars.primary} icon="👥" />
            <StatsCard label={lang === "fr" ? "100% profil" : "100% Profile"} value={stats.withProfile} color={themeVars.success} icon="✅" />
            <StatsCard label={lang === "fr" ? "50% profil" : "50% Profile"} value={stats.halfProfile} color={themeVars.warning} icon="⚠️" />
            <StatsCard label={lang === "fr" ? "Moyenne" : "Avg Profile"} value={`${stats.avgProfile}%`} color={themeVars.accent} icon="📊" />
            <StatsCard label={lang === "fr" ? "Confirmés" : "Confirmed"} value={stats.confirmed} color={themeVars.info} icon="✓" />
          </div>

          {/* Capacity & Cost Banner */}
          {eventCapacity && (
            <div style={{
              marginBottom: "32px",
              padding: isMobile ? "16px 20px" : "18px 28px",
              borderRadius: "16px",
              background: theme === "dark"
                ? "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(124,58,237,0.08))"
                : "linear-gradient(135deg, rgba(56,25,49,0.07), rgba(107,46,88,0.04))",
              border: `1px solid ${themeVars.primary}30`,
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "12px" : "32px",
              flexWrap: "wrap",
            }}>
              {/* Participants / Capacity */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "12px",
                  background: `${themeVars.primary}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.3rem", flexShrink: 0,
                }}>👥</div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: themeVars.smallText, marginBottom: "2px" }}>
                    {lang === "fr" ? "Participants / Capacité" : "Participants / Capacity"}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: 800, color: themeVars.fg }}>
                      {stats.total}
                    </span>
                    <span style={{ fontSize: "1rem", color: themeVars.smallText, fontWeight: 500 }}>
                      / {eventCapacity}
                    </span>
                    <span style={{
                      fontSize: "0.8rem", fontWeight: 700, padding: "2px 8px",
                      borderRadius: "8px",
                      background: stats.total >= eventCapacity
                        ? `${themeVars.danger}15`
                        : stats.total >= eventCapacity * 0.8
                          ? `${themeVars.warning}15`
                          : `${themeVars.success}15`,
                      color: stats.total >= eventCapacity
                        ? themeVars.danger
                        : stats.total >= eventCapacity * 0.8
                          ? themeVars.warning
                          : themeVars.success,
                    }}>
                      {Math.round((stats.total / eventCapacity) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Separator */}
              {!isMobile && (
                <div style={{ width: "1px", height: "40px", background: `${themeVars.border}`, flexShrink: 0 }} />
              )}

              {/* Fill Bar */}
              <div style={{ flex: 1, minWidth: "120px" }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: themeVars.smallText, marginBottom: "6px" }}>
                  {lang === "fr" ? "Remplissage" : "Fill Rate"}
                </div>
                <div style={{ height: "8px", borderRadius: "4px", background: `${themeVars.border}`, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${Math.min((stats.total / eventCapacity) * 100, 100)}%`,
                    borderRadius: "4px",
                    background: stats.total >= eventCapacity
                      ? themeVars.danger
                      : stats.total >= eventCapacity * 0.8
                        ? themeVars.warning
                        : themeVars.success,
                    transition: "width 0.5s ease",
                  }} />
                </div>
              </div>

              {/* Separator */}
              {!isMobile && (
                <div style={{ width: "1px", height: "40px", background: `${themeVars.border}`, flexShrink: 0 }} />
              )}

              {/* Estimated Cost */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{
                  width: "40px", height: "40px", borderRadius: "12px",
                  background: `${themeVars.accent}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "1.3rem", flexShrink: 0,
                }}>💶</div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: themeVars.smallText, marginBottom: "2px" }}>
                    {lang === "fr" ? "Coût estimé plateforme" : "Estimated Platform Cost"}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: 800, color: themeVars.accent }}>
                      €{estimatedCost !== null ? estimatedCost.toLocaleString() : (eventCapacity * 2).toLocaleString()}
                    </span>
                    <span style={{ fontSize: "0.82rem", color: themeVars.smallText }}>
                      ({eventCapacity} × €2)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabs — Desktop only (mobile uses bottom nav) */}
          {!isMobile && (
          <div className="ead-tabs" style={{
            display: "flex", gap: "6px", marginBottom: "24px",
            padding: "6px", background: themeVars.card, borderRadius: "16px",
            border: `1px solid ${themeVars.border}`,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            width: "100%",
            flexShrink: 0,
          }}>
            <TabButton id="participants" label={lang === "fr" ? "Participants" : "Participants"} icon="👥" count={participants.length} />
            {canViewSearchRequests && (
              <TabButton id="requests" label={lang === "fr" ? "Requêtes" : "Requests"} icon="🔍" count={searchRequests.length} />
            )}
            <TabButton id="matches" label={lang === "fr" ? "Matches" : "Matches"} icon="🤝" count={matches.length} />
            <TabButton id="tables" label={lang === "fr" ? "Tables" : "Tables"} icon="🪑" />
            <TabButton id="invitedUsers" label={lang === "fr" ? "Invités" : "Invited Users"} icon="📨" count={referrals.length} />
            <TabButton id="comments" label={lang === "fr" ? "Commentaires" : "Comments"} icon="💬" count={adminComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)} />
            {isCreator && (
              <TabButton id="admins" label={lang === "fr" ? "Co-Admins" : "Co-Admins"} icon="🛡️" count={coAdmins.length} />
            )}
          </div>
          )}

          {/* Content */}
          <div key={activeTab}>
            {renderContent()}
          </div>
        </div>


      </div>

      {/* ===== MOBILE BOTTOM TAB BAR ===== */}
      {isMobile && (() => {
        const mobileTabs = [
          { id: "participants", icon: "👥", label: lang === "fr" ? "Membres" : "Members", count: participants.length },
          ...(canViewSearchRequests ? [{ id: "requests", icon: "🔍", label: lang === "fr" ? "Requêtes" : "Requests", count: searchRequests.length }] : []),
          { id: "matches", icon: "🤝", label: lang === "fr" ? "Matches" : "Matches", count: matches.length },
          { id: "tables", icon: "🪑", label: lang === "fr" ? "Tables" : "Tables" },
          { id: "invitedUsers", icon: "📨", label: lang === "fr" ? "Invités" : "Invited", count: referrals.length },
          { id: "comments", icon: "💬", label: lang === "fr" ? "Comments" : "Comments", count: adminComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0) },
          ...(isCreator ? [{ id: "admins", icon: "🛡️", label: lang === "fr" ? "Admins" : "Admins", count: coAdmins.length }] : []),
        ];
        return (
          <nav className="ead-mobile-tabs">
            {mobileTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  className="ead-mobile-tab-btn"
                  onClick={() => setActiveTab(tab.id)}
                  style={{ color: isActive ? themeVars.primary : themeVars.smallText }}
                >
                  <span className="tab-icon" style={{
                    filter: isActive ? "none" : "grayscale(0.3) opacity(0.7)",
                    transform: isActive ? "scale(1.15)" : "scale(1)",
                    transition: "transform 0.18s ease",
                    display: "block",
                  }}>
                    {tab.icon}
                  </span>
                  <span className="tab-label" style={{
                    color: isActive ? themeVars.primary : themeVars.smallText,
                    fontWeight: isActive ? 700 : 500,
                    transition: "color 0.18s",
                  }}>
                    {tab.label}
                  </span>
                  {tab.count > 0 && (
                    <span className="tab-badge" style={{
                      background: isActive ? themeVars.primary : `${themeVars.smallText}30`,
                      color: isActive ? "#fff" : themeVars.smallText,
                    }}>
                      {tab.count > 99 ? "99+" : tab.count}
                    </span>
                  )}
                  {isActive && (
                    <span style={{
                      position: "absolute",
                      top: 0, left: "20%", right: "20%",
                      height: "2px",
                      borderRadius: "0 0 2px 2px",
                      background: themeVars.primary,
                    }} />
                  )}
                </button>
              );
            })}
          </nav>
        );
      })()}

      {/* ===== FLOATING ACTION BUTTONS ===== */}
      {/* Language Dropdown — only when settings open */}
      {/* <AnimatePresence>
        {isLangOpen && isSettingsOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.92 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              right: isMobile ? "16px" : "32px",
              bottom: isMobile ? "232px" : "184px",
              borderRadius: "14px",
              boxShadow: `0 8px 32px rgba(0,0,0,0.3)`,
              overflow: "hidden",
              zIndex: 1002,
              minWidth: "58px",
              padding: "6px",
            }}
          >
            {languageOptions.map((option, index) => (
              <button
                key={option.value}
                onClick={() => {
                  if (onToggleLang) onToggleLang(option.value);
                  setIsLangOpen(false);
                }}
                style={{
                  width: "100%",
                  padding: "9px 10px",
                  background: lang === option.value ? `${themeVars.primary}18` : "transparent",
                  border: "none",
                  borderBottom: index < languageOptions.length - 1 ? `1px solid ${themeVars.border}` : "none",
                  color: themeVars.fg,
                  fontSize: "20px",
                  cursor: "pointer",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${themeVars.primary}20`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = lang === option.value ? `${themeVars.primary}18` : "transparent"; }}
              >
                {option.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence> */}

      {/* FAB: Language — shown only when settings open */}
      {/* <AnimatePresence>
      {isSettingsOpen && (
      <motion.button
        key="fab-lang"
        className="fab-float"
        initial={{ opacity: 0, y: 20, scale: 0.72 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.72, transition: { duration: 0.14 } }}
        transition={{ type: "spring", stiffness: 420, damping: 24, delay: 0.08 }}
        onClick={() => { setIsLangOpen(!isLangOpen); }}
        title={lang === "fr" ? "Changer la langue" : "Change language"}
        style={{
          position: "fixed",
          right: isMobile ? "16px" : "32px",
          bottom: isMobile ? "232px" : "184px",
          width: 52,
          height: 52,
          border: "none",
          borderRadius: "50%",
          background: theme === "dark"
            ? "linear-gradient(135deg, #5b3fa8, #7c3aed)"
            : "linear-gradient(135deg, #6b2e58, #9a4080)",
          boxShadow: theme === "dark"
            ? `0 8px 28px rgba(139,92,246,0.55), 0 2px 8px rgba(0,0,0,0.4)`
            : `0 8px 28px rgba(107,46,88,0.45), 0 2px 8px rgba(0,0,0,0.15)`,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "20px",
          animationDelay: "0.3s",
        }}
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.93 }}
      >
        {languageOptions.find(opt => opt.value === lang)?.label || "🇺🇸"}
      </motion.button>
      )}
      </AnimatePresence> */}

      {/* FAB: Theme toggle — shown only when settings open */}
      {/* <AnimatePresence>
      {isSettingsOpen && (
      <motion.button
        key="fab-theme"
        className="fab-float"
        initial={{ opacity: 0, y: 20, scale: 0.72 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.72, transition: { duration: 0.14 } }}
        transition={{ type: "spring", stiffness: 420, damping: 24, delay: 0.04 }}
        onClick={() => { if (onToggleTheme) onToggleTheme(); setIsLangOpen(false); }}
        title={lang === "fr" ? "Changer le thème" : "Toggle theme"}
        style={{
          position: "fixed",
          right: isMobile ? "16px" : "32px",
          bottom: isMobile ? "160px" : "108px",
          width: 52,
          height: 52,
          border: "none",
          borderRadius: "50%",
          background: theme === "dark"
            ? "linear-gradient(135deg, #6b3fa8, #8b5cf6)"
            : "linear-gradient(135deg, #8a3e70, #b05090)",
          boxShadow: theme === "dark"
            ? `0 8px 28px rgba(139,92,246,0.60), 0 2px 8px rgba(0,0,0,0.4)`
            : `0 8px 28px rgba(138,62,112,0.45), 0 2px 8px rgba(0,0,0,0.15)`,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "22px",
          animationDelay: "0.15s",
        }}
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.93 }}
      >
        {theme === "dark" ? "🌙" : "☀️"}
      </motion.button>
      )}
      </AnimatePresence> */}

      {/* FAB: Settings (main — always visible) */}
      {/* <motion.button
        className="fab-settings fab-float"
        onClick={() => { setIsSettingsOpen(!isSettingsOpen); setIsLangOpen(false); }}
        title={lang === "fr" ? "Paramètres" : "Settings"}
        style={{
          position: "fixed",
          right: isMobile ? "16px" : "32px",
          bottom: isMobile ? "84px" : "32px",
          width: 60,
          height: 60,
          border: "none",
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${themeVars.primary}, ${themeVars.accent})`,
          boxShadow: `0 12px 40px ${themeVars.primary}70`,
          zIndex: 1001,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
        }}
        whileHover={{ scale: 1.08, transition: { duration: 0.25 } }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.img
          src={settingsIcon}
          alt="Settings"
          animate={{ rotate: isSettingsOpen ? 90 : 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={{ width: 34, height: 34, objectFit: "contain", filter: "brightness(0) invert(1)" }}
        />
      </motion.button> */}

      {/* ===== PARTICIPANT DETAIL DRAWER ===== */}
      <AnimatePresence>
        {isDrawerOpen && selectedParticipant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeDrawer}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
              backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              zIndex: 2000, display: "flex", alignItems: isMobile ? "flex-end" : "center",
              justifyContent: "center", padding: isMobile ? "0" : "0 40px",
            }}
          >
            <motion.div
              initial={{ x: isMobile ? 0 : "100%", y: isMobile ? "100%" : 0 }}
              animate={{ x: 0, y: 0 }}
              exit={{ x: isMobile ? 0 : "100%", y: isMobile ? "100%" : 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: themeVars.card,
                borderRadius: isMobile ? "24px 24px 0 0" : "28px",
                width: isMobile ? "100%" : isTablet ? "90%" : "760px",
                maxHeight: isMobile ? "92vh" : "88vh",
                overflowY: "auto", border: `1px solid ${themeVars.border}`,
                boxShadow: "0 28px 65px rgba(0,0,0,0.42)",
                display: "flex", flexDirection: "column",
              }}
            >
              {/* Drawer Header */}
              <div className="ead-drawer-header" style={{
                borderBottom: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "24px",
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <h2 style={{
                    margin: 0, fontSize: "2.3rem", fontWeight: 800,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.15,
                  }}>
                    {selectedParticipant.name}
                  </h2>
                  <div style={{
                    display: "flex", alignItems: "center", gap: "16px", marginTop: "14px",
                    flexWrap: "wrap", fontSize: "1.15rem",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", opacity: 0.92 }}>
                      <span>📧</span>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "220px" }}>
                        {selectedParticipant.email}
                      </span>
                    </div>
                    {selectedParticipant.username && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", opacity: 0.92 }}>
                        <span>👤</span>
                        <span>@{selectedParticipant.username}</span>
                      </div>
                    )}
                    {selectedParticipant.phone_number && (
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", opacity: 0.92 }}>
                        <span>📱</span>
                        <span>{selectedParticipant.phone_number}</span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "14px", marginTop: "16px", flexWrap: "wrap" }}>
                    <StatusBadge status={selectedParticipant.status} type="participant" />
                    {getRoleBadge(selectedParticipant.role)}
                    {selectedParticipant.is_owner && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: 600,
                        background: `${themeVars.accent}15`, color: themeVars.accent,
                        border: `1px solid ${themeVars.accent}30`, whiteSpace: "nowrap",
                      }}>
                        🏆 {lang === "fr" ? "Créateur" : "Creator"}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeDrawer}
                  style={{
                    background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                    width: "48px", height: "48px", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", fontSize: "1.6rem", flexShrink: 0,
                    transition: "all 0.25s ease", marginTop: "8px",
                  }}
                  onMouseEnter={(e) => e.target.style.background = `${themeVars.primary}0c`}
                  onMouseLeave={(e) => e.target.style.background = themeVars.subtle}
                  aria-label={lang === "fr" ? "Fermer" : "Close"}
                >
                  ×
                </button>
              </div>

              {/* Drawer Content */}
              <div className="ead-drawer-body" style={{ overflowY: "auto", flex: 1 }}>
                {/* Profile Section */}
                <div style={{ marginBottom: "40px" }}>
                  <h3 style={{
                    fontSize: "1.75rem", marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>👤</span>
                    {lang === "fr" ? "Profil Complet" : "Complete Profile"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "32px" }}>
                    {/* About Section */}
                    <div>
                      <div style={{
                        fontSize: "1.2rem", fontWeight: 600, marginBottom: "16px",
                        display: "flex", alignItems: "center", gap: "10px", color: themeVars.fg,
                      }}>
                        <span>📝</span>
                        {lang === "fr" ? "À propos" : "About"}
                      </div>
                      {selectedParticipant.about ? (
                        <div style={{
                          padding: "22px", borderRadius: "20px", background: themeVars.subtle,
                          border: `1px solid ${themeVars.border}`, lineHeight: 1.7,
                          fontSize: "1.12rem", minHeight: "100px", whiteSpace: "pre-wrap",
                        }}>
                          {selectedParticipant.about}
                        </div>
                      ) : (
                        <div style={{
                          padding: "22px", borderRadius: "20px", background: `${themeVars.warning}0a`,
                          border: `1px dashed ${themeVars.warning}45`, color: themeVars.warning,
                          fontSize: "1.12rem", minHeight: "100px", display: "flex", alignItems: "center",
                        }}>
                          ⚠️ {lang === "fr"
                            ? "L'utilisateur n'a pas rempli sa section 'À propos'"
                            : "User has not filled out their about section"}
                        </div>
                      )}
                    </div>
                    {/* Looking for Section */}
                    <div>
                      <div style={{
                        fontSize: "1.2rem", fontWeight: 600, marginBottom: "16px",
                        display: "flex", alignItems: "center", gap: "10px", color: themeVars.fg,
                      }}>
                        <span>🔍</span>
                        {lang === "fr" ? "Ce qu'il cherche" : "Looking for"}
                      </div>
                      {selectedParticipant.request ? (
                        <div style={{
                          padding: "22px", borderRadius: "20px", background: themeVars.subtle,
                          border: `1px solid ${themeVars.border}`, lineHeight: 1.7,
                          fontSize: "1.12rem", minHeight: "100px", whiteSpace: "pre-wrap",
                        }}>
                          {selectedParticipant.request}
                        </div>
                      ) : (
                        <div style={{
                          padding: "22px", borderRadius: "20px", background: `${themeVars.warning}0a`,
                          border: `1px dashed ${themeVars.warning}45`, color: themeVars.warning,
                          fontSize: "1.12rem", minHeight: "100px", display: "flex", alignItems: "center",
                        }}>
                          ⚠️ {lang === "fr"
                            ? "L'utilisateur n'a pas spécifié ce qu'il cherche"
                            : "User has not specified what they're looking for"}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Profile Completion */}
                  <div style={{ marginTop: "28px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <span style={{ fontWeight: 600, fontSize: "1.15rem", color: themeVars.fg }}>
                        {lang === "fr" ? "Complétion du profil" : "Profile Completion"}
                      </span>
                      <span style={{
                        fontWeight: 700, fontSize: "1.4rem",
                        color: getProfileFill(selectedParticipant) === 100 ? themeVars.success :
                          getProfileFill(selectedParticipant) === 50 ? themeVars.warning : themeVars.smallText,
                      }}>
                        {getProfileFill(selectedParticipant)}%
                      </span>
                    </div>
                    <div style={{
                      height: "12px", borderRadius: "6px", background: `${themeVars.primary}18`,
                      overflow: "hidden", boxShadow: `0 1px 3px ${themeVars.primary}25`,
                    }}>
                      <div style={{
                        height: "100%", width: `${getProfileFill(selectedParticipant)}%`,
                        background: getProfileFill(selectedParticipant) === 100 ? themeVars.success :
                          getProfileFill(selectedParticipant) === 50 ? themeVars.warning : themeVars.danger || themeVars.smallText,
                        borderRadius: "6px", transition: "width 0.4s ease",
                      }} />
                    </div>
                  </div>
                </div>

                {/* Search Requests List */}
                <div style={{ marginBottom: "40px" }}>
                  <h3 style={{
                    fontSize: "1.75rem", marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>📋</span>
                    {lang === "fr" ? "Requêtes de recherche" : "Search Requests"}
                    <span style={{
                      fontSize: "1rem", fontWeight: 500, color: themeVars.smallText,
                      background: `${themeVars.primary}15`, padding: "4px 12px", borderRadius: "12px",
                    }}>
                      {participantRequests.length}
                    </span>
                  </h3>
                  {participantRequestsLoading ? (
                    <div style={{ padding: "40px", textAlign: "center", color: themeVars.smallText }}>
                      {lang === "fr" ? "Chargement des requêtes..." : "Loading requests..."}
                    </div>
                  ) : participantRequests.length === 0 ? (
                    <div style={{
                      padding: "32px", textAlign: "center", background: `${themeVars.warning}0a`,
                      borderRadius: "16px", border: `1px dashed ${themeVars.warning}30`, color: themeVars.warning,
                    }}>
                      <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>📭</div>
                      <p style={{ fontSize: "1.1rem", fontWeight: 500, margin: "0 0 8px" }}>
                        {lang === "fr" ? "Aucune requête" : "No search requests"}
                      </p>
                      <p style={{ color: themeVars.smallText, fontSize: "0.95rem" }}>
                        {lang === "fr"
                          ? "Ce participant n'a pas créé de requêtes pour cet événement"
                          : "This participant hasn't created any requests for this event"}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {participantRequests.map((request) => (
                        <div
                          key={request.id}
                          onClick={() => openRequestDetailsPanel(request)}
                          style={{
                            padding: "20px", background: themeVars.subtle,
                            border: `1px solid ${themeVars.border}`, borderRadius: "16px",
                            cursor: "pointer", transition: "all 0.2s ease",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            gap: "16px", flexWrap: "wrap",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = themeVars.primary;
                            e.currentTarget.style.background = `${themeVars.primary}05`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = themeVars.border;
                            e.currentTarget.style.background = themeVars.subtle;
                          }}
                        >
                          <div style={{ flex: 1, minWidth: "200px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                              <span style={{ fontSize: "1.1rem", fontWeight: 600, color: themeVars.fg }}>
                                {request.title || 'Untitled Request'}
                              </span>
                              <span style={{
                                padding: "4px 10px", borderRadius: "12px",
                                background: `${themeVars.accent}15`, color: themeVars.accent,
                                fontSize: "0.8rem", fontWeight: 600,
                              }}>
                                {request.category}
                              </span>
                            </div>
                            <div style={{ fontSize: "0.9rem", color: themeVars.smallText, display: "flex", gap: "16px", flexWrap: "wrap" }}>
                              <span>📅 {formatDate(request.created_at)}</span>
                              <span>🤝 {request.total_matches} matches</span>
                              {request.pending_matches > 0 && (
                                <span style={{ color: themeVars.warning }}>⏳ {request.pending_matches} pending</span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {(() => {
                              const statusInfo = getRequestStatus(request, matches);
                              return (
                                <StatusBadge
                                  status={statusInfo.type}
                                  type="request"
                                  details={statusInfo.details}
                                />
                              );
                            })()}
                            <span style={{ fontSize: "1.2rem", color: themeVars.primary }}>→</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Technical Information */}
                <div>
                  <h3 style={{
                    fontSize: "1.6rem", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>ℹ️</span>
                    {lang === "fr" ? "Informations techniques" : "Technical Information"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "20px" }}>
                    {[
                      { label: lang === "fr" ? "ID Utilisateur" : "User ID", value: selectedParticipant.user_id, icon: "#" },
                      { label: lang === "fr" ? "Statut d'inscription" : "Registration Status", value: <StatusBadge status={selectedParticipant.status} type="participant" />, icon: "🎫", isBadge: true },
                      { label: lang === "fr" ? "Compte créé le" : "Account Created", value: formatDate(selectedParticipant.user_created_at), icon: "👤" },
                      { label: lang === "fr" ? "Inscrit à l'événement le" : "Registered At", value: formatDate(selectedParticipant.registered_at), icon: "📅" },
                      { label: lang === "fr" ? "Événement actif" : "Active Event", value: selectedParticipant.active_event_id ? `ID: ${selectedParticipant.active_event_id}` : (lang === "fr" ? "Aucun" : "None"), icon: "🎪" },
                    ].map((item, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "18px", background: themeVars.subtle, borderRadius: "16px",
                          border: `1px solid ${themeVars.border}`, transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = themeVars.primary}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = themeVars.border}
                      >
                        <div style={{
                          fontSize: "0.95rem", color: themeVars.smallText, marginBottom: "8px",
                          display: "flex", alignItems: "center", gap: "8px", fontWeight: 500,
                        }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: "24px", height: "24px", borderRadius: "8px",
                            background: `${themeVars.primary}15`, color: themeVars.primary,
                            fontSize: "0.9rem", fontWeight: 600,
                          }}>
                            {item.icon}
                          </span>
                          {item.label}
                        </div>
                        <div style={{ fontSize: "1.1rem", fontWeight: item.isBadge ? 400 : 600, color: themeVars.fg, lineHeight: 1.4 }}>
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Drawer Footer */}
              <div style={{
                padding: "24px 40px", borderTop: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "center", gap: "16px",
                background: themeVars.card, borderRadius: isMobile ? "0 0 28px 28px" : "0 0 28px 28px",
              }}>
                <button
                  onClick={closeDrawer}
                  style={{
                    padding: "14px 36px", borderRadius: "20px",
                    background: `${themeVars.primary}12`, color: themeVars.primary,
                    border: `1px solid ${themeVars.primary}35`, cursor: "pointer",
                    fontWeight: 600, fontSize: "1.15rem", transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `${themeVars.primary}18`;
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = `0 6px 20px ${themeVars.primary}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `${themeVars.primary}12`;
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = `0 4px 15px ${themeVars.primary}15`;
                  }}
                >
                  {lang === "fr" ? "Fermer le profil" : "Close Profile"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== REQUEST DETAILS PANEL ===== */}
      <AnimatePresence>
        {isRequestDetailsPanelOpen && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed", inset: 0, background: "transparent",
              backdropFilter: "none", WebkitBackdropFilter: "none",
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              zIndex: 2100, padding: "0 40px", pointerEvents: "none",
            }}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: themeVars.card, borderRadius: "28px 0 0 28px",
                width: "700px", maxWidth: "85vw", maxHeight: "90vh",
                overflowY: "auto", boxShadow: "-28px 0 65px rgba(0,0,0,0.42)",
                border: `1px solid ${themeVars.border}`, display: "flex",
                flexDirection: "column", marginRight: isMobile ? "0" : "40px",
                pointerEvents: "auto",
              }}
            >
              {/* Panel Header */}
              <div style={{
                padding: "32px 40px 24px", borderBottom: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "24px",
                background: `${themeVars.primary}05`,
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                    <span style={{ fontSize: "2rem" }}>📋</span>
                    <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 700, color: themeVars.fg }}>
                      {lang === "fr" ? "Détails de la requête" : "Request Details"}
                    </h2>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "1rem" }}>
                    <CategoryBadge category={selectedRequest.category} />
                    {(() => {
                      const statusInfo = getRequestStatus(selectedRequest, selectedRequestMatches);
                      return (
                        <StatusBadge
                          status={statusInfo.type}
                          type="request"
                          details={statusInfo.details}
                        />
                      );
                    })()}
                  </div>
                </div>
                <button
                  onClick={closeRequestDetailsPanel}
                  style={{
                    background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                    width: "48px", height: "48px", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", fontSize: "1.6rem", flexShrink: 0,
                    transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => e.target.style.background = `${themeVars.primary}0c`}
                  onMouseLeave={(e) => e.target.style.background = themeVars.subtle}
                  aria-label={lang === "fr" ? "Fermer" : "Close"}
                >
                  ×
                </button>
              </div>

              {/* Panel Content */}
              <div className="ead-drawer-body" style={{ overflowY: "auto", flex: 1 }}>
                {/* Request Information */}
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{
                    fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>📝</span>
                    {lang === "fr" ? "Informations sur la requête" : "Request Information"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>ID</div>
                      <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>#{selectedRequest.id}</div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Utilisateur" : "User"}
                      </div>
                      <div style={{ fontWeight: 500 }}>{selectedParticipant?.name || 'N/A'}</div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Créé le" : "Created At"}
                      </div>
                      <div style={{ fontWeight: 500 }}>{formatDate(selectedRequest.created_at)}</div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Dernier match" : "Last Matched"}
                      </div>
                      <div style={{ fontWeight: 500 }}>{selectedRequest.last_matched_at ? formatDate(selectedRequest.last_matched_at) : "N/A"}</div>
                    </div>
                  </div>
                </div>

                {/* Request Text */}
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{
                    fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>💬</span>
                    {lang === "fr" ? "Texte de la requête" : "Request Text"}
                  </h3>
                  <div style={{
                    padding: "20px", borderRadius: "16px", background: themeVars.subtle,
                    border: `1px solid ${themeVars.border}`, fontSize: "1.05rem",
                    lineHeight: 1.7, whiteSpace: "pre-wrap",
                  }}>
                    {selectedRequest.request_text}
                  </div>
                </div>

                {/* Matches for Request */}
                <div>
                  <h3 style={{
                    fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>🤝</span>
                    {lang === "fr" ? "Matches liés à cette requête" : "Matches for this Request"}
                    <span style={{
                      fontSize: "1rem", fontWeight: 500, color: themeVars.smallText,
                      background: `${themeVars.success}15`, padding: "4px 12px", borderRadius: "12px",
                    }}>
                      {selectedRequestMatches.length}
                    </span>
                  </h3>
                  {selectedRequestMatches.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "24px", color: themeVars.smallText }}>
                      {lang === "fr"
                        ? "Aucun match trouvé pour cette requête"
                        : "No matches found for this request"}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {selectedRequestMatches.map((match) => (
                        <div
                          key={match.id}
                          style={{
                            background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                            borderRadius: "16px", padding: "16px", cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => openMatchDetailsPanel(match)}
                          onMouseEnter={(e) => e.currentTarget.style.borderColor = themeVars.primary}
                          onMouseLeave={(e) => e.currentTarget.style.borderColor = themeVars.border}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                            <strong style={{ fontSize: "1.1rem" }}>Match #{match.id}</strong>
                            {getMatchStatusBadge(match.status, match.user1_status, match.user2_status, match.user1_name, match.user2_name)}
                          </div>
                          <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontSize: "0.95rem", color: themeVars.smallText, marginBottom: "4px" }}>
                              {lang === "fr" ? "Utilisateurs" : "Users"}
                            </div>
                            <div style={{ fontWeight: 500 }}>{match.user1_name} ↔ {match.user2_name}</div>
                          </div>
                          <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontSize: "0.95rem", color: themeVars.smallText, marginBottom: "4px" }}>
                              {lang === "fr" ? "Score de compatibilité" : "Compatibility Score"}
                            </div>
                            <div style={{ fontWeight: 600, color: themeVars.success, fontSize: "1.2rem" }}>
                              {Math.round(match.compatibility_score)}%
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: "0.95rem", color: themeVars.smallText, marginBottom: "4px" }}>
                              {lang === "fr" ? "Raison du match" : "Match Reason"}
                            </div>
                            <div style={{ fontSize: "0.9rem", opacity: 0.9 }}>{match.match_reason}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Panel Footer */}
              <div style={{
                padding: "24px 40px", borderTop: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "center", background: themeVars.card,
                borderRadius: "0 0 0 28px",
              }}>
                <button
                  onClick={closeRequestDetailsPanel}
                  style={{
                    padding: "14px 36px", borderRadius: "20px",
                    background: `${themeVars.primary}12`, color: themeVars.primary,
                    border: `1px solid ${themeVars.primary}35`, cursor: "pointer",
                    fontWeight: 600, fontSize: "1.15rem", transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `${themeVars.primary}18`;
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = `0 6px 20px ${themeVars.primary}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `${themeVars.primary}12`;
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = `0 4px 15px ${themeVars.primary}15`;
                  }}
                >
                  {lang === "fr" ? "Fermer" : "Close"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== MATCH DETAILS PANEL ===== */}
      <AnimatePresence>
        {isMatchDetailsPanelOpen && selectedMatch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed", inset: 0, background: "transparent",
              backdropFilter: "none", WebkitBackdropFilter: "none",
              display: "flex", alignItems: "center", justifyContent: "flex-start",
              zIndex: 2200, padding: "0 40px", pointerEvents: "none",
            }}
          >
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: themeVars.card, borderRadius: "0 28px 28px 0",
                width: "700px", maxWidth: "85vw", maxHeight: "90vh",
                overflowY: "auto", boxShadow: "28px 0 65px rgba(0,0,0,0.42)",
                border: `1px solid ${themeVars.border}`, display: "flex",
                flexDirection: "column", marginLeft: isMobile ? "0" : "40px",
                pointerEvents: "auto",
              }}
            >
              {/* Panel Header */}
              <div style={{
                padding: "32px 40px 24px", borderBottom: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "24px",
                background: `${themeVars.success}05`,
              }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                    <span style={{ fontSize: "2rem" }}>🤝</span>
                    <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 700, color: themeVars.fg }}>
                      {lang === "fr" ? "Détails du match" : "Match Details"}
                    </h2>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "1rem" }}>
                    <CategoryBadge category={selectedMatch.category} />
                    {getMatchStatusBadge(selectedMatch.status, selectedMatch.user1_status, selectedMatch.user2_status, selectedMatch.user1_name, selectedMatch.user2_name)}
                  </div>
                </div>
                <button
                  onClick={closeMatchDetailsPanel}
                  style={{
                    background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                    width: "48px", height: "48px", borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", fontSize: "1.6rem", flexShrink: 0,
                    transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => e.target.style.background = `${themeVars.primary}0c`}
                  onMouseLeave={(e) => e.target.style.background = themeVars.subtle}
                  aria-label={lang === "fr" ? "Fermer" : "Close"}
                >
                  ×
                </button>
              </div>

              {/* Panel Content */}
              <div className="ead-drawer-body" style={{ overflowY: "auto", flex: 1 }}>
                {/* Match Participants */}
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{
                    fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>👥</span>
                    {lang === "fr" ? "Participants du match" : "Match Participants"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                    <div style={{ padding: "20px", borderRadius: "16px", background: themeVars.subtle, border: `1px solid ${themeVars.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                        <div style={{
                          width: "50px", height: "50px", borderRadius: "50%",
                          background: `${themeVars.primary}15`, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: "1.3rem", color: themeVars.primary,
                        }}>
                          {selectedMatch.user1_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "4px" }}>
                            {selectedMatch.user1_name}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: themeVars.smallText }}>
                            ID: {selectedMatch.user1_id}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "6px" }}>
                          {lang === "fr" ? "Statut" : "Status"}
                        </div>
                        {selectedMatch.user1_status === 'accepted' ? (
                          <span style={{ color: themeVars.success }}>✅ {lang === "fr" ? "Accepté" : "Accepted"}</span>
                        ) : selectedMatch.user1_status === 'rejected' ? (
                          <span style={{ color: themeVars.danger }}>❌ {lang === "fr" ? "Rejeté" : "Rejected"}</span>
                        ) : (
                          <span style={{ color: themeVars.warning }}>⏳ {lang === "fr" ? "En attente" : "Pending"}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ padding: "20px", borderRadius: "16px", background: themeVars.subtle, border: `1px solid ${themeVars.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                        <div style={{
                          width: "50px", height: "50px", borderRadius: "50%",
                          background: `${themeVars.accent}15`, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: "1.3rem", color: themeVars.accent,
                        }}>
                          {selectedMatch.user2_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "4px" }}>
                            {selectedMatch.user2_name}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: themeVars.smallText }}>
                            ID: {selectedMatch.user2_id}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "6px" }}>
                          {lang === "fr" ? "Statut" : "Status"}
                        </div>
                        {selectedMatch.user2_status === 'accepted' ? (
                          <span style={{ color: themeVars.success }}>✅ {lang === "fr" ? "Accepté" : "Accepted"}</span>
                        ) : selectedMatch.user2_status === 'rejected' ? (
                          <span style={{ color: themeVars.danger }}>❌ {lang === "fr" ? "Rejeté" : "Rejected"}</span>
                        ) : (
                          <span style={{ color: themeVars.warning }}>⏳ {lang === "fr" ? "En attente" : "Pending"}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Match Information */}
                <div style={{ marginBottom: "32px" }}>
                  <h3 style={{
                    fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                    fontWeight: 700, color: themeVars.fg,
                  }}>
                    <span>📊</span>
                    {lang === "fr" ? "Informations du match" : "Match Information"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>ID</div>
                      <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>#{selectedMatch.id}</div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Créé le" : "Created At"}
                      </div>
                      <div style={{ fontWeight: 500 }}>{formatDate(selectedMatch.created_at)}</div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Score de compatibilité" : "Compatibility Score"}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                          width: "40px", height: "40px", borderRadius: "50%",
                          background: `linear-gradient(135deg, ${themeVars.success}25, ${themeVars.primary}25)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: "1rem", color: themeVars.success,
                        }}>
                          {Math.round(selectedMatch.compatibility_score)}%
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: "16px", background: themeVars.subtle, borderRadius: "12px", border: `1px solid ${themeVars.border}` }}>
                      <div style={{ fontSize: "0.85rem", color: themeVars.smallText, marginBottom: "6px" }}>
                        {lang === "fr" ? "Statut global" : "Overall Status"}
                      </div>
                      {getMatchStatusBadge(selectedMatch.status, selectedMatch.user1_status, selectedMatch.user2_status, selectedMatch.user1_name, selectedMatch.user2_name)}
                    </div>
                  </div>
                </div>

                {/* Match Reason */}
                {selectedMatch.match_reason && (
                  <div style={{ marginBottom: "32px" }}>
                    <h3 style={{
                      fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px",
                      fontWeight: 700, color: themeVars.fg,
                    }}>
                      <span>💬</span>
                      {lang === "fr" ? "Raison du match" : "Match Reason"}
                    </h3>
                    <div style={{
                      padding: "20px", borderRadius: "16px", background: themeVars.subtle,
                      border: `1px solid ${themeVars.border}`, fontSize: "1.05rem",
                      lineHeight: 1.7, whiteSpace: "pre-wrap",
                    }}>
                      {selectedMatch.match_reason}
                    </div>
                  </div>
                )}
              </div>

              {/* Panel Footer */}
              <div style={{
                padding: "24px 40px", borderTop: `1px solid ${themeVars.border}`,
                display: "flex", justifyContent: "center", background: themeVars.card,
                borderRadius: "0 0 28px 0",
              }}>
                <button
                  onClick={closeMatchDetailsPanel}
                  style={{
                    padding: "14px 36px", borderRadius: "20px",
                    background: `${themeVars.success}12`, color: themeVars.success,
                    border: `1px solid ${themeVars.success}35`, cursor: "pointer",
                    fontWeight: 600, fontSize: "1.15rem", transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `${themeVars.success}18`;
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = `0 6px 20px ${themeVars.success}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `${themeVars.success}12`;
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = `0 4px 15px ${themeVars.success}15`;
                  }}
                >
                  {lang === "fr" ? "Fermer" : "Close"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== MATCH DETAILS MODAL ===== */}
      {isMatchDetailsModalOpen && selectedMatchId && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsMatchDetailsModalOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(8px)", zIndex: 2300,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "20px",
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: themeVars.card, borderRadius: "20px",
              width: "90%", maxWidth: "850px", maxHeight: "85vh",
              overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              padding: "24px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
              <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 700 }}>
                {lang === "fr" ? "Détails du match" : "Match Details"}
              </h2>
              <button
                onClick={() => setIsMatchDetailsModalOpen(false)}
                style={{
                  background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                  width: "36px", height: "36px", borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", fontSize: "1.2rem",
                }}
              >
                ×
              </button>
            </div>
            {matchDetails ? (
              <div>
                <div style={{ marginBottom: "24px" }}>
                  <h3 style={{ fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>👥</span>
                    {lang === "fr" ? "Participants du match" : "Match Participants"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>
                    <div style={{ padding: "20px", borderRadius: "16px", background: themeVars.subtle, border: `1px solid ${themeVars.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                        <div style={{
                          width: "50px", height: "50px", borderRadius: "50%",
                          background: `${themeVars.primary}15`, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: "1.3rem", color: themeVars.primary,
                        }}>
                          {matchDetails.user1_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "4px" }}>
                            {matchDetails.user1_name}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: themeVars.smallText }}>
                            ID: {matchDetails.user1_id}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "6px" }}>
                          {lang === "fr" ? "Statut dans le match" : "Status in Match"}
                        </div>
                        <div style={{ fontWeight: 500 }}>
                          {matchDetails.user1_status === 'accepted' ? (
                            <span style={{ color: themeVars.success }}>✅ {lang === "fr" ? "Accepté" : "Accepted"}</span>
                          ) : matchDetails.user1_status === 'rejected' ? (
                            <span style={{ color: themeVars.danger }}>❌ {lang === "fr" ? "Rejeté" : "Rejected"}</span>
                          ) : (
                            <span style={{ color: themeVars.warning }}>⏳ {lang === "fr" ? "En attente" : "Pending"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: "20px", borderRadius: "16px", background: themeVars.subtle, border: `1px solid ${themeVars.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                        <div style={{
                          width: "50px", height: "50px", borderRadius: "50%",
                          background: `${themeVars.accent}15`, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontWeight: 600, fontSize: "1.3rem", color: themeVars.accent,
                        }}>
                          {matchDetails.user2_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "4px" }}>
                            {matchDetails.user2_name}
                          </div>
                          <div style={{ fontSize: "0.9rem", color: themeVars.smallText }}>
                            ID: {matchDetails.user2_id}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "6px" }}>
                          {lang === "fr" ? "Statut dans le match" : "Status in Match"}
                        </div>
                        <div style={{ fontWeight: 500 }}>
                          {matchDetails.user2_status === 'accepted' ? (
                            <span style={{ color: themeVars.success }}>✅ {lang === "fr" ? "Accepté" : "Accepted"}</span>
                          ) : matchDetails.user2_status === 'rejected' ? (
                            <span style={{ color: themeVars.danger }}>❌ {lang === "fr" ? "Rejeté" : "Rejected"}</span>
                          ) : (
                            <span style={{ color: themeVars.warning }}>⏳ {lang === "fr" ? "En attente" : "Pending"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: "24px" }}>
                  <h3 style={{ fontSize: "1.4rem", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>📊</span>
                    {lang === "fr" ? "Informations du match" : "Match Information"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
                    <div>
                      <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "4px" }}>ID</div>
                      <div style={{ fontWeight: 500 }}>#{matchDetails.id}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "4px" }}>
                        {lang === "fr" ? "Statut global" : "Overall Status"}
                      </div>
                      {getMatchStatusBadge(matchDetails.status, matchDetails.user1_status, matchDetails.user2_status, matchDetails.user1_name, matchDetails.user2_name)}
                    </div>
                    <div>
                      <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "4px" }}>
                        {lang === "fr" ? "Catégorie" : "Category"}
                      </div>
                      <CategoryBadge category={matchDetails.category} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "4px" }}>
                        {lang === "fr" ? "Créé le" : "Created At"}
                      </div>
                      <div>{formatDate(matchDetails.created_at)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: "0.9rem", color: themeVars.smallText, marginBottom: "4px" }}>
                        {lang === "fr" ? "Score de compatibilité" : "Compatibility Score"}
                      </div>
                      <div style={{
                        fontSize: "1.8rem", fontWeight: 700, color: themeVars.success,
                        display: "flex", alignItems: "center", gap: "8px",
                      }}>
                        <div style={{
                          width: "48px", height: "48px", borderRadius: "50%",
                          background: `linear-gradient(135deg, ${themeVars.primary}25, ${themeVars.accent}25)`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 600,
                        }}>
                          {Math.round(matchDetails.compatibility_score)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: "24px" }}>
                  <h3 style={{ fontSize: "1.4rem", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>💬</span>
                    {lang === "fr" ? "Raison du match" : "Match Reason"}
                  </h3>
                  <div style={{
                    padding: "16px", borderRadius: "12px", background: themeVars.subtle,
                    border: `1px solid ${themeVars.border}`, fontSize: "1.05rem",
                    lineHeight: 1.6, whiteSpace: "pre-wrap",
                  }}>
                    {matchDetails.match_reason}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px", color: themeVars.smallText }}>
                {lang === "fr" ? "Aucune information disponible" : "No information available"}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* ===== MOBILE CO-ADMINS BOTTOM SHEET ===== */}
      <AnimatePresence>
        {isMobile && mobileAdminsPanelOpen && isCreator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileAdminsPanelOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
              zIndex: 3000, display: "flex", alignItems: "flex-end",
            }}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                background: themeVars.card,
                borderRadius: "24px 24px 0 0",
                border: `1px solid ${themeVars.border}`,
                borderBottom: "none",
                boxShadow: "0 -12px 48px rgba(0,0,0,0.35)",
                maxHeight: "85vh",
                overflowY: "auto",
                paddingBottom: "env(safe-area-inset-bottom, 16px)",
              }}
            >
              {/* Handle bar */}
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                <div style={{ width: "40px", height: "4px", borderRadius: "2px", background: themeVars.border }} />
              </div>

              {/* Header */}
              <div style={{
                padding: "12px 20px 16px",
                borderBottom: `1px solid ${themeVars.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: themeVars.fg, display: "flex", alignItems: "center", gap: "8px" }}>
                    🛡️ {lang === "fr" ? "Co-Administrateurs" : "Co-Admins"}
                  </h3>
                  <p style={{ margin: "3px 0 0", fontSize: "0.8rem", color: themeVars.smallText }}>
                    {lang === "fr" ? "Gérer les admins de l'événement" : "Manage event admins"}
                  </p>
                </div>
                <button
                  onClick={() => setMobileAdminsPanelOpen(false)}
                  style={{
                    width: "32px", height: "32px", borderRadius: "50%",
                    background: themeVars.subtle, border: `1px solid ${themeVars.border}`,
                    color: themeVars.smallText, fontSize: "1rem", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >✕</button>
              </div>

              {/* Co-admins list */}
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                {coAdminsLoading ? (
                  <div style={{ color: themeVars.smallText, fontSize: "0.9rem", padding: "12px 0", textAlign: "center" }}>
                    ⏳ {lang === "fr" ? "Chargement..." : "Loading..."}
                  </div>
                ) : coAdmins.length === 0 ? (
                  <div style={{
                    padding: "20px", borderRadius: "14px", textAlign: "center",
                    background: `${themeVars.primary}08`, border: `1px dashed ${themeVars.border}`,
                    color: themeVars.smallText, fontSize: "0.9rem",
                  }}>
                    {lang === "fr" ? "Aucun co-admin pour l'instant" : "No co-admins yet"}
                  </div>
                ) : coAdmins.map(admin => (
                  <div key={admin.user_id} style={{
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "12px 14px", borderRadius: "14px",
                    background: `${themeVars.primary}07`,
                    border: `1px solid ${themeVars.border}`,
                  }}>
                    <div style={{
                      width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
                      background: `linear-gradient(135deg, ${themeVars.accent}30, ${themeVars.primary}20)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: "1rem", color: themeVars.accent,
                      border: `1px solid ${themeVars.accent}30`,
                    }}>
                      {(admin.name || admin.username || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.95rem", color: themeVars.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {admin.name || admin.username || `User #${admin.user_id}`}
                      </div>
                      {admin.username && (
                        <div style={{ fontSize: "0.78rem", color: themeVars.smallText }}>@{admin.username}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveAdmin(admin.user_id)}
                      title={lang === "fr" ? "Retirer admin" : "Remove admin"}
                      style={{
                        padding: "7px 12px", borderRadius: "10px", flexShrink: 0,
                        background: themeVars.dangerLight, color: themeVars.danger,
                        border: `1px solid ${themeVars.danger}30`,
                        cursor: "pointer", fontSize: "0.82rem", fontWeight: 700,
                      }}
                    >
                      {lang === "fr" ? "Retirer" : "Remove"}
                    </button>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div style={{ height: "1px", background: themeVars.border, margin: "0 20px" }} />

              {/* Add admin section */}
              <div style={{ padding: "16px 20px 8px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <label style={{ fontSize: "0.78rem", fontWeight: 700, color: themeVars.smallText, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  {lang === "fr" ? "Ajouter un admin" : "Add co-admin"}
                </label>
                {participants.length === 0 ? (
                  <div style={{ fontSize: "0.88rem", color: themeVars.smallText, fontStyle: "italic", padding: "8px 0" }}>
                    {lang === "fr" ? "Aucun participant disponible" : "No participants available"}
                  </div>
                ) : (
                  <>
                    <select
                      value={addAdminInput}
                      onChange={e => setAddAdminInput(e.target.value)}
                      style={{
                        padding: "12px 14px", borderRadius: "12px",
                        background: themeVars.bg, border: `1px solid ${themeVars.border}`,
                        color: addAdminInput ? themeVars.fg : themeVars.smallText,
                        fontSize: "0.95rem", outline: "none", cursor: "pointer", width: "100%",
                      }}
                    >
                      <option value="">{lang === "fr" ? "Choisir un participant..." : "Select a participant..."}</option>
                      {participants
                        .filter(p => !p.is_owner && !coAdmins.some(a => a.user_id === p.user_id))
                        .map(p => (
                          <option key={p.user_id} value={p.user_id}>
                            {p.name}{p.username ? ` (@${p.username})` : ''} #{p.user_id}
                          </option>
                        ))
                      }
                    </select>
                    <button
                      onClick={handleAddAdmin}
                      disabled={addAdminLoading || !addAdminInput}
                      style={{
                        padding: "13px", borderRadius: "12px", width: "100%",
                        background: addAdminLoading || !addAdminInput
                          ? `${themeVars.primary}40`
                          : `linear-gradient(135deg, ${themeVars.primary}, ${themeVars.accent})`,
                        color: "#fff", border: "none", fontSize: "1rem", fontWeight: 700,
                        cursor: addAdminLoading || !addAdminInput ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                      }}
                    >
                      {addAdminLoading ? "⏳" : "+"} {lang === "fr" ? "Ajouter comme admin" : "Add as co-admin"}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== TOAST NOTIFICATION ===== */}
      {toast && (
        <div style={{
          position: "fixed", bottom: isMobile ? "78px" : "100px", left: "50%", transform: "translateX(-50%)",
          background: theme === "dark" ? "rgba(26,31,46,0.97)" : "rgba(255,253,250,0.97)",
          border: `1px solid ${themeVars.primary}40`,
          color: themeVars.fg, padding: isMobile ? "10px 20px" : "12px 24px", borderRadius: "16px",
          fontSize: isMobile ? "0.88rem" : "0.95rem", fontWeight: 600, zIndex: 9999,
          boxShadow: `0 8px 32px rgba(0,0,0,0.28), 0 0 0 1px ${themeVars.primary}20`,
          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          display: "flex", alignItems: "center", gap: "8px",
          animation: "fadeInUp 0.2s ease",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}>
          {toast}
        </div>
      )}
      </>
  );
}